import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useNavBlocker from '../../hooks/useNavBlocker';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Helmet } from 'react-helmet-async';
import { getApiUrl } from '../../utils/apiConfig';
import LoginPrompt from '../../Auth/LoginPrompt';
import ProUpgradeModal from '../../components/chat/ProUpgradeModal';
import FilePreCheckModal from '../../components/common/FilePreCheckModal';
import Dialog from '../../components/common/Dialog';
import ModalActions from '../../components/common/ModalActions';
import { Progress } from '../../components/common/AsyncState';
import FieldHelpTooltip from '../../components/common/FieldHelpTooltip';
import StudentRecognitionReviewList from '../../components/common/StudentRecognitionReviewList';
import ScoringRosterMatch from './ScoringRosterMatch';
import RosterResultCard from '../../components/chat/batch/RosterResultCard';
import RoomRosterStatus from './RoomRosterStatus';
import { preCheckFiles } from '../../api/chatApi';
import { uploadScoringAttachment } from '../../firebase/storage';
// 📎 파일 첨부 한도 중앙 설정
import { getFileLimits, MAX_FILES_DEFAULT, MAX_PDF_PAGES, MAX_PPT_SLIDES } from '../../constants/fileLimits';
import { saveScoringRecord, updateScoringResult, refineScoringResult } from '../../api/scoringApi';
import { useToast } from '../../components/Toast';
import { useConfirm } from '../../components/common/ConfirmDialog';
import { ScoringScanDemo } from '../../components/demo';
import { QRCodeSVG } from 'qrcode.react';
import './ScoringPage.css';

import { fetchWithRetry, calcApiTimeout } from '../../utils/fetchWithRetry';
import { trackRiseFeatureAction, trackScoringUxEvent } from '../../utils/eventTracker';
import { normalizePreCheckStats, summarizeFileStats } from '../../utils/filePreCheckStats';
import { getPdfPageCount, maybeCompressScanPdf } from '../../utils/pdfCompressor';
import {
  getScoringStudentName,
  findInvalidMethodCStudent,
  hasCachedAnswerText,
  mergeCachedAnswerTextsWithResults,
  mergeScoringResultMeta,
  mergeScoringResultsMeta,
  normalizeStudentNameForMatch,
  normalizeScanAnswerTextCache,
  resolveAnswerTextForResult,
} from './scoringResultMeta';
import ScoringReviewWorkspace, { ScoringCriterionChecks, ScoringDistributionNotice, ScoringReviewLauncher } from './ScoringReviewWorkspace';
import { getGradeOnlySummary, isGradeOnlyResult, isGradeOnlyRubric } from './scoringGradeMode';
import { computeChunkFillRatio } from './batchProgressMath';
import { buildScoringResultExportRows } from './scoringExportData';
import { buildSaenggibuHandoffState } from './scoringSaenggibuHandoff';
import SaenggibuAttachMenu from '../../components/saenggibu/SaenggibuAttachMenu';
import { getReferenceMaterial } from '../../api/referenceMaterialApi';
import { buildLibraryMirrorTitle, buildRubricTextFromParsed, mirrorTextToLibrary } from '../../utils/libraryMirror';
import { downloadScoringResultsPdf } from './scoringPdfExport';
import { applyResultEditLocally } from './scoringResultEdits';
import {
  collectSplitPages,
  findDroppedSplitPages,
  formatPageList,
  mergeSplitStudentIntoPrevious,
  restoreSplitStudentsByPage,
} from './splitStudentReview';
import { BLANK_POLICY_VALUES, getBlankPolicyDefault, resolveBlankPolicyForRequest } from './scoringBlankPolicy';
import {
  NAME_SOURCE,
  extractStudentNameFromFilename,
  isPlaceholderStudentName,
  nameSourceForTeacherEdit,
  placeholderStudentLabel,
  studentNameForSubmit,
  studentNameSourceForSubmit,
} from '../../utils/studentNamePolicy';
import {
  buildRecognitionExportRows,
  getRecognitionExportDate,
  writeRecognitionWorkbook,
} from '../../utils/recognitionReview';

// 일괄 A/B/C 원본은 Storage 직접 업로드를 사용한다. PDF/문서는 Gemini inline
// 상한과 같은 50MB, 단일 이미지는 Vision 상한 20MB를 유지한다.
const PER_FILE_MAX_SIZE = 50 * 1024 * 1024;
const SINGLE_IMAGE_MAX_SIZE = 20 * 1024 * 1024;
const SCAN_PDF_MAX_PAGES = 80;
const BATCH_STUDENT_FILE_MAX = 50;
const BATCH_TOTAL_CONTENT_MAX = 500000;
const IMAGE_FILE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
const BATCH_IMAGE_VISION_CONCURRENCY = 2;
const BATCH_TEXT_CHUNK_PROGRESS_MS = 60_000;
const BATCH_IMAGE_CHUNK_PROGRESS_MS = 180_000;
const RESCORE_BATCH_CHUNK_SIZE = 5;

const createClientStudentId = (prefix = 'batch') => {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const batchStudentRosterKey = (student, index) => (
  student?.split_student_id || student?.client_student_id || `index_${index}`
);

// 예시 학생 답안 — 기준서 '예시 채우기'와 답안 '예시 답안 넣기'가 같은 본문을 쓴다
const EXAMPLE_STUDENT_ANSWER = `『데미안』을 읽고

이 책을 읽으면서 가장 인상 깊었던 것은 싱클레어가 자기 자신을 찾아가는 과정이었다. 처음에 싱클레어는 "밝은 세계"와 "어두운 세계"를 완전히 분리해서 생각했지만, 데미안을 만나면서 그 경계가 허물어지는 것을 경험한다. 나도 중학교 때 친구들 사이에서 "착한 아이"로만 보이려고 노력했던 기억이 있어서 싱클레어의 갈등이 진짜 공감됐다.

특히 "새는 알에서 나오려고 투쟁한다"는 문장이 기억에 남는다. 이 문장은 단순히 성장의 고통만을 의미하는 게 아니라, 기존의 안전한 세계를 스스로 깨뜨려야 한다는 뜻이라고 생각한다. 그래서 이 작품은 좋다. 나도 내 껍데기를 깨야 할 때가 있다고 느꼈다.

결론적으로 헤르만 헤세는 이 작품을 통해 인간의 본질에 대한 탐구를 보여준다. 진정한 자기 자신을 찾기 위해서는 사회가 정해놓은 틀에서 벗어나 내면의 목소리에 귀 기울여야 한다는 메시지가 현재를 살아가는 우리에게도 여전히 유효하다고 생각한다.`;

const DEFAULT_SCORING_OPTIONS = {
  school_level: 'auto',
  strictness: 'balanced',
  distribution_target: {
    mode: 'none',
    top: null,
    middle: null,
    bottom: null,
    levels: [],
    custom_text: '',
  },
  custom_instruction: '',
  blank_policy: 'auto',
};

const SCHOOL_LEVEL_OPTIONS = [
  { value: 'auto', label: '자동 추천' },
  { value: 'elementary', label: '초등' },
  { value: 'middle', label: '중등' },
  { value: 'high', label: '고등' },
];

const STRICTNESS_OPTIONS = [
  { value: 'lenient', label: '유하게', hint: '애매하면 조금 인정' },
  { value: 'balanced', label: '보통', hint: '평소 채점처럼' },
  { value: 'strict', label: '깐깐하게', hint: '근거 없으면 낮게' },
];

const DISTRIBUTION_OPTIONS = [
  {
    value: 'none',
    label: '사용 안 함',
    hint: '기준표만 적용',
    target: { mode: 'none', top: null, middle: null, bottom: null, custom_text: '' },
  },
  {
    value: '30-50-20',
    label: '상30 중50 하20',
    hint: '가장 일반적',
    target: { mode: 'preset', top: 30, middle: 50, bottom: 20, custom_text: '' },
  },
  {
    value: '20-60-20',
    label: '상20 중60 하20',
    hint: '조금 더 보수적',
    target: { mode: 'preset', top: 20, middle: 60, bottom: 20, custom_text: '' },
  },
  {
    value: 'custom',
    label: '직접 입력',
    hint: '문장으로 요청',
    target: { mode: 'custom', top: null, middle: null, bottom: null, custom_text: '' },
  },
];

const RESCORE_PRESETS = [
  {
    label: '조금 유하게 다시 보기',
    strictness: 'lenient',
    instruction: '전체적으로 조금 더 유하게 다시 봐줘. 답안에 실제 근거가 있고 하위 기준을 분명히 충족하면 경계선에서는 한 단계 위로 인정해줘. 다만 백지, 근거 부재, 문제 지문만 베낀 답안은 절대 올려주지 말고, 항목별 판정 근거는 짧게 남겨줘.',
  },
  {
    label: '보통 기준으로 다시 보기',
    strictness: 'balanced',
    instruction: '기준표에 가장 맞는 보통 기준으로 다시 봐줘. 답안의 직접 근거, 분량, 조건 충족 정도를 기준으로 가장 설명 가능한 수준을 선택하고, 최고점은 핵심 조건이 명확할 때만 줘. 항목별 충족/부족 근거를 함께 남겨줘.',
  },
  {
    label: '더 깐깐하게 다시 보기',
    strictness: 'strict',
    instruction: '전체적으로 더 깐깐하게 다시 봐줘. 기준표의 핵심 조건 중 하나라도 명확히 빠지면 최고점을 주지 말고, 애매하거나 근거가 부족한 답안은 낮은 수준으로 판단해줘. 부족한 조건은 판단 근거와 점수 조정 사유에 남겨줘.',
  },
  {
    label: '특정 항목 엄격히',
    strictness: 'strict',
    instruction: '특정 평가요소를 더 엄격하게 다시 봐줘. 내가 언급한 항목은 학생 답안의 직접 근거가 충분하고 기준의 핵심 조건이 확인될 때만 높은 점수를 줘. 누락된 조건이 있으면 한 단계 낮추고, 어떤 조건이 부족했는지 판단 근거에 남겨줘.',
  },
  {
    label: '상30 중50 하20',
    distribution_target: { mode: 'preset', top: 30, middle: 50, bottom: 20, custom_text: '' },
    instruction: '전체 점수 분포가 상 30%, 중 50%, 하 20%에 가깝게 나오도록 다시 봐줘. 기준표와 답안 근거를 우선하되, 비슷한 수준의 경계선 답안은 요청한 비율에 맞춰 상·중·하로 나눠줘. 분포 때문에 조정한 경우 그 이유를 남겨줘.',
  },
  {
    label: '상20 중60 하20',
    distribution_target: { mode: 'preset', top: 20, middle: 60, bottom: 20, custom_text: '' },
    instruction: '전체 점수 분포가 상 20%, 중 60%, 하 20%에 가깝게 나오도록 다시 봐줘. 기준표와 답안 근거를 우선하되, 비슷한 수준의 경계선 답안은 요청한 비율에 맞춰 중간권까지 분명히 나눠줘. 분포 때문에 조정한 경우 그 이유를 남겨줘.',
  },
];

const getSchoolLevelLabel = (value) => SCHOOL_LEVEL_OPTIONS.find(opt => opt.value === value)?.label || '자동 추천';
const getStrictnessLabel = (value) => STRICTNESS_OPTIONS.find(opt => opt.value === value)?.label || '보통';

const normalizeDistributionTarget = (rawTarget = {}) => {
  if (!rawTarget || typeof rawTarget !== 'object') {
    return DEFAULT_SCORING_OPTIONS.distribution_target;
  }
  const mode = ['none', 'preset', 'custom'].includes(rawTarget.mode) ? rawTarget.mode : 'none';
  if (mode === 'none') {
    return DEFAULT_SCORING_OPTIONS.distribution_target;
  }
  const coercePercent = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.min(100, Math.round(number))) : null;
  };
  return {
    mode,
    top: coercePercent(rawTarget.top),
    middle: coercePercent(rawTarget.middle),
    bottom: coercePercent(rawTarget.bottom),
    levels: Array.isArray(rawTarget.levels)
      ? rawTarget.levels.slice(0, 8).map(level => ({
          label: String(level?.label || '').slice(0, 40),
          percent: coercePercent(level?.percent),
        })).filter(level => level.label && level.percent !== null)
      : [],
    custom_text: typeof rawTarget.custom_text === 'string' ? rawTarget.custom_text.slice(0, 300) : '',
  };
};

const getDistributionOptionValue = (target = {}) => {
  const normalized = normalizeDistributionTarget(target);
  if (normalized.mode === 'none') return 'none';
  if (normalized.mode === 'custom') return 'custom';
  if (normalized.top === 30 && normalized.middle === 50 && normalized.bottom === 20) return '30-50-20';
  if (normalized.top === 20 && normalized.middle === 60 && normalized.bottom === 20) return '20-60-20';
  return 'custom';
};

const getDistributionLabel = (target = {}) => {
  const normalized = normalizeDistributionTarget(target);
  if (normalized.mode === 'none') return '비율 없음';
  const parts = [];
  if (normalized.top !== null) parts.push(`상${normalized.top}`);
  if (normalized.middle !== null) parts.push(`중${normalized.middle}`);
  if (normalized.bottom !== null) parts.push(`하${normalized.bottom}`);
  if (parts.length) return parts.join(' ');
  return normalized.custom_text ? '직접 분포' : '비율 없음';
};

const normalizeScoringOptionsFromPayload = (payload) => {
  const source = payload?.scoring_options || payload || {};
  return {
    school_level: ['auto', 'elementary', 'middle', 'high'].includes(source.school_level)
      ? source.school_level
      : DEFAULT_SCORING_OPTIONS.school_level,
    strictness: ['lenient', 'balanced', 'strict'].includes(source.strictness)
      ? source.strictness
      : DEFAULT_SCORING_OPTIONS.strictness,
    distribution_target: normalizeDistributionTarget(source.distribution_target),
    custom_instruction: typeof source.custom_instruction === 'string'
      ? source.custom_instruction.slice(0, 2000)
      : DEFAULT_SCORING_OPTIONS.custom_instruction,
    blank_policy: BLANK_POLICY_VALUES.includes(source.blank_policy)
      ? source.blank_policy
      : DEFAULT_SCORING_OPTIONS.blank_policy,
  };
};

const ScoringHelpLabel = ({ children, help }) => (
  <span className="scoring-help-label">
    {children}
    <FieldHelpTooltip title={help.title} items={help.items} ariaLabel={`${children} 안내`} variant="compact" />
  </span>
);

const SCORING_HELP = {
  rubric: {
    title: '좋은 채점 기준서에는 무엇이 있나요?',
    items: [
      '총점, 평가요소, 배점, 수행수준 또는 감점/가산 조건이 보이면 가장 정확해요.',
      '백지, 미작성, 5줄 이하, 최저점 같은 특수 조건은 그대로 넣어주세요.',
      '한 파일에 여러 과제나 차시가 있으면 AI가 감지한 뒤 채점할 항목을 고르게 해요.',
    ],
  },
  rubricFile: {
    title: '기준서 파일 예시',
    items: [
      '수행평가 계획서, 채점 기준표, 루브릭, 평가요소표, 감점 기준표를 올려주세요.',
      'PDF, HWP/HWPX, PPT, Excel, 이미지 파일을 지원해요.',
      '총점을 100점으로 바꾸지 말고 원문 기준 그대로 올리는 게 좋아요.',
    ],
  },
  singleAnswer: {
    title: '학생 과제에는 무엇을 넣나요?',
    items: [
      '학생이 실제로 작성한 답안 본문을 넣어주세요. 문제 지문이나 예시 답안은 섞이지 않는 게 좋아요.',
      '이름, 학번, 날짜, 양식만 있는 답안은 백지로 판단될 수 있어요.',
      '손글씨는 사진이나 PDF로 첨부할 수 있어요.',
    ],
  },
  answerFile: {
    title: '답안 파일은 어떻게 올리나요?',
    items: [
      '1명 채점은 한 학생 답안만 채점해요. 파일을 여러 개 올려도 결과는 1개만 나와요.',
      '한 학생 답안이 여러 장이면 한 PDF로 합치거나 여러 파일로 첨부해도 돼요.',
      '사진, 스캔 PDF, HWP/HWPX, PPT, Excel을 지원해요. 단일 파일은 최대 30MB예요.',
      '여러 학생을 채점하려면 여러 명 채점에서 학생별 파일이나 한 PDF 방식을 사용해주세요.',
    ],
  },
  batchMode: {
    title: '어떤 방식을 고르면 좋나요?',
    items: [
      `반 전체처럼 여러 학생을 채점할 때 사용해요. 최대 ${BATCH_STUDENT_FILE_MAX}명까지 한 번에 올릴 수 있어요.`,
      '학생별 파일이 따로 있으면 방법 A, 한 스캔 PDF에 이어져 있으면 방법 B가 좋아요.',
      '한 문서 안에 표처럼 여러 답안이 섞여 있으면 방법 C에서 AI가 먼저 나눠요.',
    ],
  },
  batchStudentFiles: {
    title: '학생별 파일 업로드 기준',
    items: [
      `학생 한 명당 파일 1개가 가장 좋아요. 최대 ${BATCH_STUDENT_FILE_MAX}명까지 한 번에 올릴 수 있어요.`,
      '학생별 PDF/PPT는 최대 80페이지·80슬라이드까지 읽어요.',
      '파일명에 학생 이름이 있으면 자동 인식이 더 잘돼요.',
      '스캔이 흐리거나 답안이 작게 찍힌 사진은 결과 확인이 필요해요.',
    ],
  },
  scanPdf: {
    title: '스캔 PDF 방식 기준',
    items: [
      '한 PDF 안에 여러 학생 답안지가 이어진 경우에 사용해요.',
      '2~80페이지까지 지원하고, AI가 학생별 페이지 범위를 먼저 추천해요.',
      '추천 범위가 틀리면 채점 전에 직접 수정할 수 있어요.',
    ],
  },
  singleFileStudents: {
    title: '한 페이지에 여러 답안이 있을 때',
    items: [
      '한 PDF, 한글, Excel 파일 안에 여러 학생 답안이 같이 있을 때 사용해요.',
      'AI가 학생별 답안을 먼저 나누고, 선생님이 이름과 답안을 확인한 뒤 채점해요.',
      'PDF/PPT는 최대 80페이지·80슬라이드까지 읽고, 텍스트가 잘 보일수록 안정적이에요.',
    ],
  },
};

const hasImageFilename = (filename = '') => {
  const lower = filename.toLowerCase();
  return IMAGE_FILE_EXTENSIONS.some(ext => lower.endsWith(ext));
};

/**
 * 일괄 채점 예상 시간(분). 실측(Cloud Run 30일, 2026-08-16~09-15):
 *  - 파일 일괄 /evaluate-answers-batch     N=72  p50 59초  p90 100초
 *  - 스캔 PDF  /evaluate-answers-batch-pdf N=81  p50 165초 p90 232초, 60초 미만 0%
 * 스캔 PDF는 학생 수가 적어도 1~2분에 끝나는 경우가 없어 최소 3~4분을 보장한다.
 */
export const estimateBatchScoringMinutes = (count, hasImages, isScanPdf = false) => {
  const safeCount = Math.max(1, Number(count) || 1);
  if (isScanPdf) {
    return {
      min: Math.max(3, Math.ceil(safeCount * 0.1)),
      max: Math.max(4, Math.ceil(safeCount * 0.15)),
    };
  }
  if (hasImages) {
    const waves = Math.max(1, Math.ceil(safeCount / BATCH_IMAGE_VISION_CONCURRENCY));
    const min = Math.max(3, Math.ceil(waves * 1.5));
    const max = Math.max(min + 2, Math.ceil(waves * 2.5));
    return { min, max };
  }
  return {
    min: Math.max(1, Math.ceil(safeCount * 0.1)),
    max: Math.max(2, Math.ceil(safeCount * 0.15)),
  };
};

// 교사 공감 한마디 (로딩 중 랜덤 표시)
const TEACHER_TIPS = [
  '커피 한 모금 하고 오세요',
  '선생님, 오늘도 수고 많으셨어요',
  '이 시간에 다른 과제 준비하셔도 돼요',
  '방과후에 뭐 드실지 고민해보세요',
  '밤새 채점하던 시절은 이제 안녕이에요',
  '채점하는 동안 스트레칭 한번 어때요?',
  '오늘 하루도 학생들에게 힘이 되셨을 거예요',
  '잠깐 눈 쉬는 시간, 창밖을 한번 보세요',
];

const getRandomTeacherTip = () => TEACHER_TIPS[Math.floor(Math.random() * TEACHER_TIPS.length)];

const startTeacherTipTimer = (setTip, intervalMs = 12000) => {
  setTip(getRandomTeacherTip());
  return setInterval(() => {
    setTip(prev => {
      let next = getRandomTeacherTip();
      if (TEACHER_TIPS.length > 1) {
        let guard = 0;
        while (next === prev && guard < 5) {
          next = getRandomTeacherTip();
          guard += 1;
        }
      }
      return next;
    });
  }, intervalMs);
};

const buildPdfPreviewSrc = (url, page) => {
  const pageNumber = Number.parseInt(page, 10);
  const pageParam = Number.isFinite(pageNumber) && pageNumber > 0 ? `page=${pageNumber}&` : '';
  return `${url}#${pageParam}toolbar=0&navpanes=0&scrollbars=1&statusbar=0&messages=0&pagemode=none&view=FitH`;
};

const compactAnswerPreview = (text = '', limit = 120) => {
  const compact = text.replace(/\s+/g, ' ').trim();
  if (compact.length <= limit) return compact;
  return `${compact.slice(0, limit).trim()}...`;
};

const hasMethodCSplitStudents = (students = []) => (
  Array.isArray(students)
  && students.some(student => Boolean(
    student?.answer_text
    && (
      student?.split_student_id
      || student?.split_confidence
      || student?.source_file_name
      || student?.split_warnings?.length
    )
  ))
);

const limitMethodCText = (value, limit = 300) => (
  value ? String(value).trim().slice(0, limit) : ''
);

const limitMethodCList = (value, limit = 50, itemLimit = 300) => (
  Array.isArray(value)
    ? value.slice(0, limit).map(item => (
      typeof item === 'number' ? item : limitMethodCText(item, itemLimit)
    )).filter(item => item !== '')
    : []
);

const buildMethodCStudentPayload = (student) => ({
  student_name: studentNameForSubmit(student.name),
  name_source: studentNameSourceForSubmit(student.name, student.name_source),
  filename: limitMethodCText(student.filename || '분리된 답안'),
  answer_text: student.answer_text,
  source_pages: limitMethodCList(student.source_pages, 20, 30),
  source_file_name: limitMethodCText(student.source_file_name),
  split_student_id: student.split_student_id || null,
  client_student_id: student.client_student_id || student.split_student_id || null,
  split_confidence: limitMethodCText(student.split_confidence, 30),
  split_warnings: limitMethodCList(student.split_warnings, 10),
  split_strategy: limitMethodCText(student.split_strategy || 'ai', 60),
});

const attachMethodCResultMeta = (result, student) => {
  if (!student?.answer_text) return result;
  const hasSplitMeta = (
    student.split_student_id
    || student.split_confidence
    || student.source_file_name
    || student.split_warnings?.length
    || student.source_pages?.length
  );
  if (!hasSplitMeta) return result;
  return {
    ...result,
    source_pages: limitMethodCList(student.source_pages, 20, 30),
    source_file_name: limitMethodCText(student.source_file_name),
    split_student_id: student.split_student_id || null,
    client_student_id: student.client_student_id || student.split_student_id || null,
    split_confidence: limitMethodCText(student.split_confidence, 30),
    split_warnings: limitMethodCList(student.split_warnings, 10),
    split_strategy: limitMethodCText(student.split_strategy || 'ai', 60),
  };
};

const normalizeScanPageRangesForClient = (ranges = [], pageCount = 0) => (
  (Array.isArray(ranges) ? ranges : []).map((range) => {
    const rawStart = Number(range.start_page ?? range.startPage ?? range.start ?? 1);
    const start = Math.max(1, Number.isFinite(rawStart) ? rawStart : 1);
    const rawEnd = Number(range.end_page ?? range.endPage ?? range.end ?? start);
    const end = Math.max(start, Math.min(Number.isFinite(rawEnd) ? rawEnd : start, pageCount || rawEnd || start));
    const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    const previewName = studentNameForSubmit(range.student_name || range.studentName);
    return {
      student_name: previewName,
      name_source: range.name_source || (previewName ? NAME_SOURCE.AI_PREVIEW : NAME_SOURCE.PLACEHOLDER),
      start_page: start,
      end_page: end,
      pages,
      page_label: start === end ? `${start}` : `${start}-${end}`,
      confidence: range.confidence || 'medium',
      reason: range.reason || '',
      warnings: Array.isArray(range.warnings) ? range.warnings : [],
      ...(range.name_confidence ? { name_confidence: range.name_confidence } : {}),
      ...(range.name_source ? { name_source: range.name_source } : {}),
      ...(range.student_number ? { student_number: range.student_number } : {}),
    };
  }).filter(range => range.start_page >= 1 && range.end_page <= pageCount)
);

const buildEvenScanPageRanges = (pageCount, pagesPerStudent = 1) => {
  const step = Math.max(1, Number(pagesPerStudent) || 1);
  const ranges = [];
  for (let start = 1; start <= pageCount; start += step) {
    const end = Math.min(start + step - 1, pageCount);
    ranges.push({
      student_name: '',
      name_source: NAME_SOURCE.PLACEHOLDER,
      start_page: start,
      end_page: end,
      pages: Array.from({ length: end - start + 1 }, (_, i) => start + i),
      page_label: start === end ? `${start}` : `${start}-${end}`,
      confidence: 'manual',
      reason: `${step}페이지씩 자동 분할`,
      warnings: [],
    });
  }
  return ranges;
};

const buildNormalizedScanPageRangesForSubmit = (ranges = [], pageCount = 0) => (
  (Array.isArray(ranges) ? ranges : []).map((range) => {
    const start = Number(range.start_page);
    const end = Number(range.end_page);
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return null;
    }
    const normalizedStart = Math.max(1, Math.min(start, pageCount || start));
    const normalizedEnd = Math.max(normalizedStart, Math.min(end, pageCount || end));
    const pages = Array.from(
      { length: normalizedEnd - normalizedStart + 1 },
      (_, pageIdx) => normalizedStart + pageIdx
    );
    return {
      ...range,
      student_name: studentNameForSubmit(range.student_name),
      name_source: studentNameSourceForSubmit(range.student_name, range.name_source),
      start_page: normalizedStart,
      end_page: normalizedEnd,
      pages,
      page_label: normalizedStart === normalizedEnd ? `${normalizedStart}` : `${normalizedStart}-${normalizedEnd}`,
    };
  }).filter(Boolean)
);

const validateScanPageRanges = (ranges = [], pageCount = 0) => {
  if (!Array.isArray(ranges) || ranges.length === 0) {
    return '학생별 페이지 범위를 먼저 확인해 주세요.';
  }
  const used = new Set();
  for (let i = 0; i < ranges.length; i += 1) {
    const range = ranges[i];
    const start = Number(range.start_page);
    const end = Number(range.end_page);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start < 1 || end < start || end > pageCount) {
      return `${i + 1}번 학생의 페이지 범위를 확인해 주세요.`;
    }
    for (let page = start; page <= end; page += 1) {
      if (used.has(page)) return `${page}페이지가 여러 학생에게 겹쳐 있어요.`;
      used.add(page);
    }
  }
  return '';
};

const BatchModeExample = ({ type }) => {
  if (type === 'studentFiles') {
    return (
      <div className="scoring-mode-example" aria-hidden="true">
        <div className="scoring-mode-example-files">
          <span>홍길동.pdf</span>
          <span>김철수.jpg</span>
          <span>이영희.pptx</span>
        </div>
        <div className="scoring-mode-example-caption">학생마다 파일이 따로 있어요</div>
      </div>
    );
  }

  if (type === 'scanPdf') {
    return (
      <div className="scoring-mode-example" aria-hidden="true">
        <div className="scoring-mode-example-pdf">
          <div><strong>1-4쪽</strong><span>홍길동</span></div>
          <div><strong>5-8쪽</strong><span>김철수</span></div>
          <div><strong>9-12쪽</strong><span>이영희</span></div>
        </div>
        <div className="scoring-mode-example-caption">AI가 페이지 범위를 먼저 나눠요</div>
      </div>
    );
  }

  return (
    <div className="scoring-mode-example" aria-hidden="true">
      <div className="scoring-mode-example-sheet">
        <div><span>이름</span><span>답안</span></div>
        <div><strong>홍길동</strong><span>청년 주거...</span></div>
        <div><strong>김철수</strong><span>노인 복지...</span></div>
      </div>
      <div className="scoring-mode-example-caption">한 문서 안에 여러 답안이 있어요</div>
    </div>
  );
};

// 미리보기 불가 파일에 대한 사용자 친화 안내 메시지.
// HWP/Office 문서 등 브라우저에서 직접 렌더 불가능한 형식일 때 다운로드 안내에 사용한다.
function getPreviewFallbackHint(filename, fileType) {
  const lower = (filename || '').toLowerCase();
  const ft = (fileType || '').toLowerCase();
  if (lower.endsWith('.hwp') || lower.endsWith('.hwpx') || ft.includes('hwp')) {
    return '한글(HWP) 문서는 브라우저에서 직접 보기 어려워요. 다운로드해서 한글 프로그램으로 열어주세요.';
  }
  if (lower.endsWith('.docx') || lower.endsWith('.doc') || ft.includes('msword') || ft.includes('wordprocessingml')) {
    return 'Word 문서는 브라우저에서 직접 보기 어려워요. 다운로드해서 Word/Pages로 열어주세요.';
  }
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls') || ft.includes('spreadsheetml') || ft.includes('excel')) {
    return 'Excel 문서는 브라우저에서 직접 보기 어려워요. 다운로드해서 Excel/Numbers로 열어주세요.';
  }
  if (lower.endsWith('.pptx') || lower.endsWith('.ppt') || ft.includes('presentationml') || ft.includes('powerpoint')) {
    return 'PowerPoint 문서는 브라우저에서 직접 보기 어려워요. 다운로드해서 PowerPoint/Keynote로 열어주세요.';
  }
  return '이 파일은 브라우저에서 직접 미리보기를 지원하지 않아요. 다운로드해서 확인해주세요.';
}

const getResultTotalScore = (result) => {
  if (Array.isArray(result?.question_results) && result.question_results.length > 0) {
    return result.question_results.reduce((sum, item) => sum + (Number(item?.score) || 0), 0);
  }
  if (Array.isArray(result?.item_scores) && result.item_scores.length > 0) {
    return result.item_scores.reduce((sum, item) => sum + (Number(item?.score) || 0), 0);
  }
  return result?.total_score ?? result?.score ?? 0;
};

const getRubricItemName = (item) => (
  item?.item_name || item?.name || item?.title || '평가요소'
);

const getRubricItemMaxScore = (item) => (
  Number(item?.max_score ?? item?.max_points ?? item?.points ?? 0) || 0
);

const ScoringPageInner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isAnonymous } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const isPro = subscriptionStatus?.isPro || false;
  const isAnonymousUser = isAnonymous || !currentUser;
  const { showToast, removeToast } = useToast();
  const confirmDialog = useConfirm();

  const [anonScoringCount, setAnonScoringCount] = useState(() => {
    return parseInt(sessionStorage.getItem('scoring-anon-count') || '0');
  });

  const [showPreview] = useState(() => {
    try {
      const count = parseInt(localStorage.getItem('scoring-preview-views') || '0', 10);
      localStorage.setItem('scoring-preview-views', String(Math.min(count + 1, 4)));
      return count < 3;
    } catch { return false; }
  });
  
  // Section 1 입력 모드 탭: 'text' | 'file'
  const [rubricInputMode, setRubricInputMode] = useState('text');
  
  // 평가 기준 상태
  const [rubricText, setRubricText] = useState('');
  const [rubricFile, setRubricFile] = useState(null);
  const [rubricFileUploading, setRubricFileUploading] = useState(false);
  const [rubricLoadingMessage, setRubricLoadingMessage] = useState('');
  const [rubricLoadingProgress, setRubricLoadingProgress] = useState(0);
  
  // 파일 검사 스피너 상태
  const [isFileChecking, setIsFileChecking] = useState(false);
  const [checkingFileName, setCheckingFileName] = useState('');
  
  // 여러 수행평가 선택 관련 상태
  const [multiRubric, setMultiRubric] = useState(false);
  const [parsedRubrics, setParsedRubrics] = useState([]);
  const [selectedRubric, setSelectedRubric] = useState(null);
  const [confirmedRubric, setConfirmedRubric] = useState(null);
  const [rubricNeedsConfirmation, setRubricNeedsConfirmation] = useState(false);
  const [isRubricReviewOpen, setIsRubricReviewOpen] = useState(false);
  const [rubricReviewText, setRubricReviewText] = useState('');
  const [rubricReviewRubrics, setRubricReviewRubrics] = useState([]);
  const [rubricReviewRubric, setRubricReviewRubric] = useState(null);
  const [isRubricReviewEditing, setIsRubricReviewEditing] = useState(false);
  const [rubricReviewTextEdited, setRubricReviewTextEdited] = useState(false);
  const [questionSet, setQuestionSet] = useState(null);
  const [rubricReviewQuestionSet, setRubricReviewQuestionSet] = useState(null);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [rubricReviewBlankChoice, setRubricReviewBlankChoice] = useState(null);
  
  // 2단계: 과목 감지 → 선택 → 상세 분석
  const [detectedSubjects, setDetectedSubjects] = useState([]);
  const [rawText, setRawText] = useState('');
  const [rubricDetailLoading, setRubricDetailLoading] = useState(false);
  
  // 채점 모드: 'single' (개별) | 'batch' (일괄)
  const [scoringMode, setScoringMode] = useState('single');
  const [scoringOptions, setScoringOptions] = useState(DEFAULT_SCORING_OPTIONS);
  const [resolvedScoringContext, setResolvedScoringContext] = useState(null);
  const [isScoringCalibrationOpen, setIsScoringCalibrationOpen] = useState(false);
  const autoOpenedScoringCalibrationRef = useRef(false);
  const singleAutoSavePendingRef = useRef(false);
  // 단계별 퍼널 계측용 ref (C-1)
  const pageOpenedAtRef = useRef(Date.now());
  const rubricReadyTrackedRef = useRef(false);
  const rubricSourceRef = useRef(null);
  const answerReadyTrackedRef = useRef(false);
  const prevScoringOptionsRef = useRef(null);
  const generateRunRef = useRef(null);
  const prevReScoreCountRef = useRef(null);
  const prevIsSavedRef = useRef(null);
  
  // 일괄 채점 상태
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchStudents, setBatchStudents] = useState([]);
  const batchStudentsRef = useRef(batchStudents);
  batchStudentsRef.current = batchStudents;
  const [batchFilesStats, setBatchFilesStats] = useState([]);
  const [batchCarryoverFiles, setBatchCarryoverFiles] = useState([]);
  const [batchCarryoverFilesStats, setBatchCarryoverFilesStats] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [batchResults, setBatchResults] = useState([]);
  const [batchRetryCount, setBatchRetryCount] = useState(0);
  // UI에 표시할 최대 재시도 횟수. 각 API 호출 직전에 maxRetries 값과 동기화한다.
  const [batchMaxRetries, setBatchMaxRetries] = useState(2);
  // 현재 청크 시작 시각(ms). 청크 내부 추정 진행률 계산용.
  const [chunkStartTime, setChunkStartTime] = useState(null);
  // 청크 단위 실패한 학생 목록(부분 결과 모드). 재시도 버튼으로 다시 채점할 수 있게 한다.
  // [{ chunkIndex, total, message, students: [{file, name, filename, ...}, ...] }, ...]
  const [batchFailedChunks, setBatchFailedChunks] = useState([]);
  // 일괄 채점 실패 시 결과 화면을 비우지 않고 에러 카드를 보여주기 위한 state.
  // { type, title, message, actionLabel, canRetry, retryFn? }
  const [batchError, setBatchError] = useState(null);
  const batchRequestRef = useRef(false);
  // 진행률 갱신용 ref — 청크 내부에서도 점진적으로 progress를 끌어올리기 위함.
  const batchProgressBaseRef = useRef(0); // 현재 청크 시작 진행률(%)
  const batchProgressTargetRef = useRef(0); // 다음 청크 시작 진행률(%)
  const batchChunkExpectedMsRef = useRef(BATCH_TEXT_CHUNK_PROGRESS_MS);
  const submitBatchInChunksRef = useRef(null);
  // 재시도 후 history 저장에서 최신 batchResults를 참조하기 위한 ref.
  // setBatchResults 직후에는 state 값이 아직 갱신 전이라 ref로 동기 추적.
  const batchResultsRef = useRef([]);
  // 실제 채점 완료 인원 (청크/학생 단위 실측) — 추정이 아닌 사실 기반 "N명 중 M명" 표시용
  const [batchLiveCounts, setBatchLiveCounts] = useState(null); // {completed, total}
  // 채점 완료 후 저장 단계 안내 (100%에서 멈춘 것처럼 보이는 문제 방지)
  const [batchPhaseMessage, setBatchPhaseMessage] = useState('');
  // 결과 스크롤은 로딩이 실제로 끝난 뒤에 실행 (로딩 중에는 결과 ref가 없어 무시되던 버그 수정)
  const batchScrollPendingRef = useRef(false);
  
  // 일괄 스캔 PDF 상태
  const [scanPdfDetected, setScanPdfDetected] = useState(false);
  const [scanPdfFile, setScanPdfFile] = useState(null);
  const [scanPdfDescriptor, setScanPdfDescriptor] = useState(null);
  const [, setScanStorageUploadProgress] = useState(0);
  const [scanPdfPageCount, setScanPdfPageCount] = useState(0);
  const [scanPdfLoading, setScanPdfLoading] = useState(false);
  const [scanRangePreview, setScanRangePreview] = useState(null);
  const [scanPageRanges, setScanPageRanges] = useState([]);
  const [scanRangeLoading, setScanRangeLoading] = useState(false);
  const [scanRangeError, setScanRangeError] = useState('');
  const [splitPreviewFile, setSplitPreviewFile] = useState(null);
  const [splitPreviewDescriptor, setSplitPreviewDescriptor] = useState(null);
  const [splitPreview, setSplitPreview] = useState(null);
  const [splitPreviewLoading, setSplitPreviewLoading] = useState(false);
  const [splitPreviewProgress, setSplitPreviewProgress] = useState(0);
  const [splitPreviewMessage, setSplitPreviewMessage] = useState('');
  const [restoredSplitMetadata, setRestoredSplitMetadata] = useState(null);
  const [restoredScanMetadata, setRestoredScanMetadata] = useState(null);

  // 제출방 상태 (일괄 채점 > 학생이 직접 올리기)
  const [batchUploadMode, setBatchUploadMode] = useState(null); // null | 'teacher' | 'student'
  const [batchAnswerStructure, setBatchAnswerStructure] = useState(null); // null | 'studentFiles' | 'scanPdf' | 'singleFileStudents'
  const [submissionRoom, setSubmissionRoom] = useState(null);
  const [roomSubmissions, setRoomSubmissions] = useState([]);
  const [roomRosterStatus, setRoomRosterStatus] = useState(null);
  const [roomCreating, setRoomCreating] = useState(false);
  const [activeRooms, setActiveRooms] = useState([]);
  // 제출방 이름 인라인 수정 (window.prompt 대체)
  const [renamingRoomId, setRenamingRoomId] = useState(null);
  const [renameRoomDraft, setRenameRoomDraft] = useState('');
  const [showQrFullscreen, setShowQrFullscreen] = useState(false);
  const [roomScoringLoading, setRoomScoringLoading] = useState(false);
  const [roomTitle, setRoomTitle] = useState('');
  const [previewSubmission, setPreviewSubmission] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewZoomed, setPreviewZoomed] = useState(false);
  const roomPollRef = useRef(null);
  const scoringDescriptorCacheRef = useRef(new WeakMap());
  
  // 학생 답안 파일들
  const [answerFiles, setAnswerFiles] = useState([]);
  const [answerFilesStats, setAnswerFilesStats] = useState([]);
  const [answerText, setAnswerText] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isRubricDragging, setIsRubricDragging] = useState(false);
  const [studentSectionHighlight, setStudentSectionHighlight] = useState(false);
  
  // 학생 과제 입력 섹션 ref
  const studentSectionRef = useRef(null);
  const resultsTopRef = useRef(null);
  const scoringAssistDetailsRef = useRef(null);

  // 🆕 [버그수정] auto-resize textarea ref들
  //   - rubricText/answerText가 빈 문자열로 리셋될 때 inline style.height가
  //     남아 textarea가 부풀어 보이던 문제 해결용. useLayoutEffect로 자동 동기화.
  const rubricTextareaRef = useRef(null);
  const answerTextareaRef = useRef(null);

  useLayoutEffect(() => {
    const ta = rubricTextareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.max(ta.scrollHeight, 120) + 'px';
  }, [rubricText]);

  useLayoutEffect(() => {
    const ta = answerTextareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.max(ta.scrollHeight, 120) + 'px';
  }, [answerText]);

  const closeScoringAssistGuide = useCallback(() => {
    if (scoringAssistDetailsRef.current) {
      scoringAssistDetailsRef.current.open = false;
    }
  }, []);
  
  // 평가 결과
  const [evaluationResults, setEvaluationResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  
  // 저장 상태
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // 저장 요청 중: 중복 클릭 방지 + 버튼 스피너
  const [savedScoringRecordId, setSavedScoringRecordId] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  
  // 결과 UI 상태
  const [rubricSummaryOpen, setRubricSummaryOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingItemField, setEditingItemField] = useState(null);
  const [editingItemDraft, setEditingItemDraft] = useState({ score: '', reason: '', matched_level: '' });
  const [aiRefineTarget, setAiRefineTarget] = useState(null);
  const [aiRefineInstruction, setAiRefineInstruction] = useState('');
  const [aiRefineLoading, setAiRefineLoading] = useState(false);
  const [aiRefinePreview, setAiRefinePreview] = useState(null);
  const [aiRefineApplying, setAiRefineApplying] = useState(false);
  const [reviewModal, setReviewModal] = useState(null);
  
  // 재채점 상태
  const [cachedAnswerTexts, setCachedAnswerTexts] = useState([]);
  const [reScoreInstructions, setReScoreInstructions] = useState('');
  const [reScoreLoading, setReScoreLoading] = useState(false);
  const [reScoreCount, setReScoreCount] = useState(0);
  const [rescoreHistory, setRescoreHistory] = useState([]);
  const [activeScoringPolicy, setActiveScoringPolicy] = useState(null);
  const [previousResults, setPreviousResults] = useState(null);
  const isResultInteractionLocked = reScoreLoading;
  const activeResultsForEvidence = batchResults.length > 0 ? batchResults : evaluationResults;
  const resolvedAnswerTextsForCurrentResults = useMemo(() => (
    mergeCachedAnswerTextsWithResults(cachedAnswerTexts, [], activeResultsForEvidence)
  ), [activeResultsForEvidence, cachedAnswerTexts]);
  const canReScoreCurrentResults = resolvedAnswerTextsForCurrentResults.some(hasCachedAnswerText);
  const shouldShowReScoreSection = canReScoreCurrentResults
    || Boolean(location.state?.restoreScoringRecord && (evaluationResults.length > 0 || batchResults.length > 0));
  
  // 로그인/PRO 모달 상태
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showProRequiredModal, setShowProRequiredModal] = useState(false);

  
  // 파일 사전 검사 관련 상태
  const [showPreCheckModal, setShowPreCheckModal] = useState(false);
  const [preCheckResult, setPreCheckResult] = useState(null);
  const [pendingAnswerFiles, setPendingAnswerFiles] = useState(null);
  const [pendingRubricFile, setPendingRubricFile] = useState(null);
  const [preCheckLoading, setPreCheckLoading] = useState(false);
  // 일괄 채점 사전 검사 청크 진행률 ({ current, total } | null).
  // chatApi.preCheckFiles 가 25MB/10개 단위로 청크 분할 호출할 때 표시.
  const [precheckChunkProgress, setPrecheckChunkProgress] = useState(null);
  // 큰 스캔 PDF (30MB+) 자동 다운샘플링 압축 진행률 ({ current, total, filename, originalMB } | null).
  // pdfjs-dist + pdf-lib 으로 200DPI/JPEG85 압축 진행 시 표시.
  const [pdfCompressionProgress, setPdfCompressionProgress] = useState(null);
  
  // 함수 참조를 위한 ref (useCallback 순서 독립성 확보)
  const actualRubricFileUploadRef = useRef();
  const actualAnswerFilesUploadRef = useRef();
  const actualBatchFilesUploadRef = useRef();

  // 미저장 이탈 방지
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if ((evaluationResults.length > 0 || batchResults.length > 0) && !isSaved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [evaluationResults, batchResults, isSaved]);

  // batchResults 변경을 ref에 동기화 (재시도 후 history 저장 시 최신 값 참조용).
  useEffect(() => {
    batchResultsRef.current = batchResults;
  }, [batchResults]);

  // 일괄 채점 진행 중 1초마다 청크 내부 추정 진행률 갱신.
  // 청크 단위 호출은 이미지 포함 여부에 따라 수십 초~수 분이 걸리는데 progress가 시작/완료 시점에만 점프하면
  // 0%에 멈춰 있는 것처럼 보여 사용자가 멈춤으로 오인하기 쉽다.
  // 이를 방지하기 위해 청크 내부에서도 estimated progress를 천천히 끌어올린다.
  useEffect(() => {
    if (!batchLoading) return;
    const tick = () => {
      const now = Date.now();
      // 청크 내부 추정 진행률: chunkStart 이후 경과 시간 비율로 base→target 사이를 채움.
      // 이미지 청크는 Vision 동시성을 낮춘 만큼 더 천천히 올라가게 한다(완료 전 100% 방지).
      if (chunkStartTime && batchProgressTargetRef.current > batchProgressBaseRef.current) {
        const chunkElapsed = now - chunkStartTime;
        const range = batchProgressTargetRef.current - batchProgressBaseRef.current;
        // 예상 시간 안에는 구간의 85%까지 선형으로 채우고,
        // 예상보다 오래 걸리면 98%를 향해 점근적으로 계속 움직인다.
        // (기존에는 예상 시간이 지나면 90%에서 완전히 얼어붙어 "멈췄다"는 오인을 유발)
        const fillRatio = computeChunkFillRatio(chunkElapsed, batchChunkExpectedMsRef.current);
        const estimated = batchProgressBaseRef.current + range * fillRatio;
        setBatchProgress(prev => Math.max(prev, Math.round(estimated)));
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [batchLoading, chunkStartTime]);

  // 결과 영역으로 스크롤은 로딩 종료 후 결과 DOM 이 생긴 뒤에 실행한다.
  // (기존에는 로딩 중 setTimeout 으로 실행돼 resultsTopRef 가 null → 스크롤이 조용히 무시됨)
  useEffect(() => {
    if (batchLoading || !batchScrollPendingRef.current) return;
    batchScrollPendingRef.current = false;
    const timer = setTimeout(() => {
      resultsTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(timer);
  }, [batchLoading]);

  const navBlocker = useNavBlocker((evaluationResults.length > 0 || batchResults.length > 0) && !isSaved);

  useEffect(() => {
    if (location.state?.startBatch && !location.state?.restoreScoringRecord) setScoringMode('batch');
  }, [location.state]);

  // 히스토리에서 기준서 복원
  useEffect(() => {
    if (location.state?.restoreScoringRecord) {
      const restoredResults = Array.isArray(location.state.restoredResults)
        ? location.state.restoredResults
        : [];
      const restoredCachedTexts = mergeCachedAnswerTextsWithResults(
        [],
        location.state.restoredCachedAnswerTexts || [],
        restoredResults,
      );
      const restoredType = String(location.state.recordType || '');
      const shouldUseBatchMode = restoredResults.length > 1
        || restoredType === 'room'
        || restoredType === 'batch'
        || restoredType.startsWith('batch')
        || restoredType.startsWith('multi-question-batch')
        || restoredType.startsWith('multi-question-room');
      const restoredAnswerStructure = location.state.answerStructure || '';
      const isRestoredMethodC = restoredAnswerStructure === 'singleFileStudents' || restoredType.includes('batch-split');
      const isRestoredScanPdf = restoredAnswerStructure === 'scanPdfPageRanges' || restoredType.includes('batch-scan');
      const restoredScanMeta = isRestoredScanPdf && location.state.scanMetadata ? location.state.scanMetadata : null;

      setScoringMode(shouldUseBatchMode ? 'batch' : 'single');
      setEvaluationResults(shouldUseBatchMode ? [] : restoredResults);
      setBatchResults(shouldUseBatchMode ? restoredResults : []);
      setCachedAnswerTexts(restoredCachedTexts);
      setBatchUploadMode(shouldUseBatchMode ? 'teacher' : null);
      setBatchAnswerStructure(isRestoredMethodC ? 'singleFileStudents' : (isRestoredScanPdf ? 'scanPdf' : null));
      setRestoredSplitMetadata(isRestoredMethodC && location.state.splitMetadata ? location.state.splitMetadata : null);
      setRestoredScanMetadata(restoredScanMeta);
      if (restoredScanMeta?.page_count) {
        setScanPdfPageCount(Number(restoredScanMeta.page_count) || 0);
      }
      if (Array.isArray(restoredScanMeta?.page_ranges)) {
        setScanPageRanges(normalizeScanPageRangesForClient(restoredScanMeta.page_ranges, Number(restoredScanMeta.page_count) || 0));
        setScanRangePreview({
          success: true,
          confidence: restoredScanMeta.confidence || '',
          warnings: restoredScanMeta.warnings || [],
          detected_count: restoredScanMeta.page_ranges.length,
          page_count: restoredScanMeta.page_count,
        });
      }
      setPreviousResults(null);
      setReScoreInstructions('');
      setReScoreCount(Number(location.state.reScoreCount || 0));
      setRescoreHistory(Array.isArray(location.state.rescoreHistory) ? location.state.rescoreHistory : []);
      setActiveScoringPolicy(location.state.activeScoringPolicy || null);
      setIsSaved(true);
      setSavedScoringRecordId(location.state.recordId || null);

      if (location.state.questionSet) {
        setQuestionSet(location.state.questionSet);
        setSelectedQuestionIds(location.state.questionSet.questions?.map((q) => String(q.question_id)) || []);
      } else if (location.state.parsedRubric) {
        setConfirmedRubric(location.state.parsedRubric);
        setSelectedRubric(location.state.parsedRubric);
        setParsedRubrics([location.state.parsedRubric]);
      }

      setTimeout(() => {
        resultsTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    if (location.state?.rubricText) {
      setRubricText(location.state.rubricText);
    }
    if (location.state?.scoringOptions) {
      setScoringOptions(normalizeScoringOptionsFromPayload(location.state.scoringOptions));
      setResolvedScoringContext({
        resolved_school_level: location.state.resolvedSchoolLevel,
        resolved_strictness: location.state.resolvedStrictness,
      });
    }
  }, [location.state]);

  useEffect(() => {
    if (!rubricText.trim() || confirmedRubric || questionSet || rubricNeedsConfirmation) return;
    try {
      const savedQuestionSet = JSON.parse(sessionStorage.getItem('scoring-confirmed-question-set') || 'null');
      if (savedQuestionSet?.rubric_text === rubricText && savedQuestionSet?.question_set) {
        setQuestionSet(savedQuestionSet.question_set);
        setSelectedQuestionIds(savedQuestionSet.question_set.questions?.map((q) => String(q.question_id)) || []);
        return;
      }
      const saved = JSON.parse(sessionStorage.getItem('scoring-confirmed-rubric') || 'null');
      if (saved?.rubric_text === rubricText && saved?.rubric) {
        setConfirmedRubric(saved.rubric);
        setSelectedRubric(saved.rubric);
        setParsedRubrics([saved.rubric]);
      }
    } catch {
      sessionStorage.removeItem('scoring-confirmed-rubric');
      sessionStorage.removeItem('scoring-confirmed-question-set');
    }
  }, [rubricText, confirmedRubric, questionSet, rubricNeedsConfirmation]);

  // 히스토리/제출방 관리에서 roomId로 진입 시 자동으로 해당 제출방 로드
  useEffect(() => {
    if (!location.state?.roomId || !currentUser) return;
    const roomId = location.state.roomId;
    const roomTitle = location.state.roomTitle || '';
    const roomCode = location.state.roomCode || '';

    (async () => {
      try {
        const token = await currentUser.getIdToken();
        const res = await fetch(getApiUrl(`/api/scoring/room/${roomId}/submissions`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const roomContext = data.room || data;
          if (roomContext?.scoring_options) {
            setScoringOptions(normalizeScoringOptionsFromPayload(roomContext.scoring_options));
          }
          if (roomContext?.resolved_school_level || roomContext?.resolved_strictness) {
            setResolvedScoringContext({
              resolved_school_level: roomContext.resolved_school_level || null,
              resolved_strictness: roomContext.resolved_strictness || null,
            });
          }
          setSubmissionRoom({
            room_id: roomId,
            code: roomCode || data.room?.code || '',
            title: roomTitle || data.room?.title || '',
            expires_at: null,
          });
          setRoomSubmissions(data.submissions || []);
          setRoomRosterStatus(data.roster_status || null);
          setBatchUploadMode('student');
          setScoringMode('batch');
          if (data.room?.rubric_text && !rubricText) {
            setRubricText(data.room.rubric_text);
            if (
              data.room?.rubric_criteria &&
              typeof data.room.rubric_criteria === 'object' &&
              !Array.isArray(data.room.rubric_criteria) &&
              Object.keys(data.room.rubric_criteria).length > 0
            ) {
              setConfirmedRubric(data.room.rubric_criteria);
              setSelectedRubric(data.room.rubric_criteria);
              setParsedRubrics([data.room.rubric_criteria]);
              sessionStorage.setItem('scoring-confirmed-rubric', JSON.stringify({
                rubric_text: data.room.rubric_text,
                rubric: data.room.rubric_criteria,
              }));
            }
          }
        }
      } catch {
        // 로드 실패 시 무시 — 사용자가 수동으로 선택 가능
      }
    })();

    // state를 소비한 후 제거하여 새로고침 시 중복 실행 방지
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.roomId, currentUser]);

  // 로그인 후 비로그인 시 입력했던 데이터 + 결과 복원
  useEffect(() => {
    if (currentUser && !isAnonymous) {
      const savedRubric = localStorage.getItem('scoring-saved-rubric');
      const savedAnswer = localStorage.getItem('scoring-saved-answer');
      const savedResult = localStorage.getItem('scoring-saved-result');
      if (savedRubric) {
        setRubricText(savedRubric);
        localStorage.removeItem('scoring-saved-rubric');
      }
      if (savedAnswer) {
        setAnswerText(savedAnswer);
        localStorage.removeItem('scoring-saved-answer');
      }
      if (savedResult) {
        try { setEvaluationResults(JSON.parse(savedResult)); } catch {}
        localStorage.removeItem('scoring-saved-result');
      }
    }
  }, [currentUser, isAnonymous]);

  // 비로그인 사용자 입력 실시간 자동 저장 (디바운스 1초)
  useEffect(() => {
    if (isAnonymousUser && (rubricText.trim() || answerText.trim())) {
      const timer = setTimeout(() => {
        localStorage.setItem('scoring-saved-rubric', rubricText);
        localStorage.setItem('scoring-saved-answer', answerText);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [rubricText, answerText, isAnonymousUser]);
  
  // 로그인 확인 함수 (기본 기능용)
  const checkLogin = useCallback(() => {
    if (!currentUser) {
      setShowLoginModal(true);
      return false;
    }
    return true;
  }, [currentUser]);

  // PRO 권한 확인 함수 (파일 첨부용)
  const checkProAccess = useCallback(() => {
    if (!currentUser) {
      setShowLoginModal(true);
      return false;
    }
    if (!isPro) {
      setShowProRequiredModal(true);
      return false;
    }
    return true;
  }, [currentUser, isPro]);

  const getActiveRubricCriteria = useCallback(() => {
    if (rubricNeedsConfirmation) return null;
    return confirmedRubric || selectedRubric || null;
  }, [confirmedRubric, selectedRubric, rubricNeedsConfirmation]);

  const getActiveQuestionSet = useCallback(() => {
    if (rubricNeedsConfirmation || !questionSet?.questions?.length) return null;
    const selected = selectedQuestionIds.length > 0
      ? questionSet.questions.filter((q) => selectedQuestionIds.includes(String(q.question_id)))
      : questionSet.questions;
    if (selected.length <= 1) return null;
    const totalPoints = selected.reduce((sum, q) => sum + (Number(q.max_points) || 0), 0);
    return {
      ...questionSet,
      questions: selected,
      question_count: selected.length,
      total_points: totalPoints,
    };
  }, [questionSet, selectedQuestionIds, rubricNeedsConfirmation]);

  const isMultiQuestionScoring = Boolean(getActiveQuestionSet());

  const appendActiveRubricCriteria = useCallback((formData) => {
    const rubricCriteria = getActiveRubricCriteria();
    if (rubricCriteria) {
      formData.append('rubric_criteria', JSON.stringify(rubricCriteria));
    }
  }, [getActiveRubricCriteria]);

  const getScoringOptionsForMode = useCallback((optionsOverride = null, modeOverride = null) => {
    const nextOptions = normalizeScoringOptionsFromPayload(optionsOverride || scoringOptions);
    const mode = modeOverride || scoringMode;
    if (mode !== 'batch') {
      return {
        ...nextOptions,
        distribution_target: DEFAULT_SCORING_OPTIONS.distribution_target,
      };
    }
    return nextOptions;
  }, [scoringMode, scoringOptions]);

  const appendScoringOptions = useCallback((formData, optionsOverride = null) => {
    formData.append('scoring_options', JSON.stringify(getScoringOptionsForMode(optionsOverride)));
  }, [getScoringOptionsForMode]);

  const buildActiveScoringPolicyPayload = useCallback((optionsOverride = null) => {
    const nextOptions = getScoringOptionsForMode(optionsOverride);
    const history = Array.isArray(rescoreHistory) ? rescoreHistory : [];
    if (activeScoringPolicy) {
      return {
        ...activeScoringPolicy,
        scoring_options: nextOptions,
        recent_instructions: history.slice(-6),
      };
    }
    const latestInstruction = history.length > 0 ? history[history.length - 1]?.instruction || '' : '';
    return {
      initial_instruction: nextOptions.custom_instruction || '',
      latest_instruction: latestInstruction,
      instruction_count: history.length + (nextOptions.custom_instruction ? 1 : 0),
      scoring_options: nextOptions,
      recent_instructions: history.slice(-6),
    };
  }, [activeScoringPolicy, getScoringOptionsForMode, rescoreHistory]);

  const getScoringRecordMeta = useCallback((optionsOverride = null, contextOverride = null) => {
    const context = contextOverride || resolvedScoringContext || {};
    const effectiveOptions = getScoringOptionsForMode(optionsOverride);
    return {
      scoring_options: effectiveOptions,
      resolved_school_level: context.resolved_school_level || context.resolvedSchoolLevel || null,
      resolved_strictness: context.resolved_strictness || context.resolvedStrictness || null,
      active_scoring_policy: buildActiveScoringPolicyPayload(effectiveOptions),
      rescore_history: rescoreHistory,
    };
  }, [buildActiveScoringPolicyPayload, getScoringOptionsForMode, resolvedScoringContext, rescoreHistory]);

  const getMethodCRecordMeta = useCallback((students = batchStudents, preview = splitPreview) => {
    if (batchAnswerStructure === 'singleFileStudents' && !hasMethodCSplitStudents(students) && restoredSplitMetadata) {
      return {
        input_method: 'single_file_students',
        answer_structure: 'singleFileStudents',
        split_metadata: restoredSplitMetadata,
      };
    }

    if (batchAnswerStructure !== 'singleFileStudents' || !hasMethodCSplitStudents(students)) {
      return {};
    }

    const confidenceCounts = students.reduce((acc, student) => {
      const confidence = student.split_confidence || 'unknown';
      acc[confidence] = (acc[confidence] || 0) + 1;
      return acc;
    }, {});
    const warnings = [
      ...(Array.isArray(preview?.warnings) ? preview.warnings : []),
      ...students.flatMap(student => student.split_warnings || []),
    ].filter(Boolean);

    return {
      input_method: 'single_file_students',
      answer_structure: 'singleFileStudents',
      split_metadata: {
        strategy: preview?.strategy || 'ai_text',
        splitter_mode: preview?.splitter_mode || '',
        splitter_model: preview?.splitter_model || '',
        confidence: preview?.confidence || '',
        confidence_counts: confidenceCounts,
        detected_count: students.length,
        initial_detected_count: preview?.initial_detected_count || null,
        merged_count: students.reduce((sum, student) => sum + Math.max(0, (student.merged_split_ids?.length || 1) - 1), 0),
        removed_count: (preview?.removed_students || []).length,
        dropped_pages: limitMethodCList(findDroppedSplitPages(preview?.initial_pages, students), 200),
        unassigned_pages: limitMethodCList(preview?.unassigned_pages, 200),
        page_structure: preview?.page_structure || null,
        page_count: preview?.page_count || null,
        source_file_name: limitMethodCText(splitPreviewFile?.name || preview?.fileName || ''),
        warnings: limitMethodCList(Array.from(new Set(warnings)), 50),
        students: students.map((student, index) => ({
          student_index: index + 1,
          student_name: limitMethodCText(studentNameForSubmit(student.name), 80),
          source_pages: limitMethodCList(student.source_pages, 20, 30),
          answer_chars: (student.answer_text || '').length,
          split_student_id: student.split_student_id || null,
          split_confidence: limitMethodCText(student.split_confidence, 30),
          split_warnings: limitMethodCList(student.split_warnings, 10),
          merged_split_ids: limitMethodCList(student.merged_split_ids, 20, 30),
        })),
      },
    };
  }, [batchAnswerStructure, batchStudents, restoredSplitMetadata, splitPreview, splitPreviewFile]);

  const getScanRecordMeta = useCallback((ranges = scanPageRanges, preview = scanRangePreview) => {
    if (batchAnswerStructure !== 'scanPdf' && !restoredScanMetadata) {
      return {};
    }
    const pageRanges = Array.isArray(ranges) && ranges.length > 0
      ? ranges
      : (Array.isArray(restoredScanMetadata?.page_ranges) ? restoredScanMetadata.page_ranges : []);
    if (!pageRanges.length) return {};
    return {
      input_method: 'scan_pdf_page_ranges',
      answer_structure: 'scanPdfPageRanges',
      scan_metadata: {
        mode: restoredScanMetadata?.mode || 'teacher_confirmed_ranges',
        page_count: scanPdfPageCount || restoredScanMetadata?.page_count || null,
        student_count: pageRanges.length,
        confidence: preview?.confidence || restoredScanMetadata?.confidence || '',
        warnings: limitMethodCList([
          ...(Array.isArray(preview?.warnings) ? preview.warnings : []),
          ...(Array.isArray(restoredScanMetadata?.warnings) ? restoredScanMetadata.warnings : []),
        ], 50),
        page_ranges: pageRanges.map((range, index) => ({
          student_index: index + 1,
          student_name: limitMethodCText(studentNameForSubmit(range.student_name || range.studentName), 80),
          name_source: studentNameSourceForSubmit(range.student_name || range.studentName, range.name_source),
          start_page: Number(range.start_page || range.startPage || range.start || 0),
          end_page: Number(range.end_page || range.endPage || range.end || 0),
          pages: limitMethodCList(range.pages, 20, 30),
          page_label: limitMethodCText(range.page_label || `${range.start_page || ''}-${range.end_page || ''}`, 60),
          confidence: limitMethodCText(range.confidence, 30),
          warnings: limitMethodCList(range.warnings, 10),
        })),
      },
    };
  }, [batchAnswerStructure, restoredScanMetadata, scanPageRanges, scanPdfPageCount, scanRangePreview]);

  const applyScoringContextFromResponse = useCallback((data) => {
    if (!data) return;
    if (data.scoring_options) {
      setScoringOptions(normalizeScoringOptionsFromPayload(data.scoring_options));
    }
    if (Array.isArray(data.rescore_history)) {
      setRescoreHistory(data.rescore_history);
    }
    if (data.active_scoring_policy) {
      setActiveScoringPolicy(data.active_scoring_policy);
    }
    if (
      data.rubric_criteria &&
      typeof data.rubric_criteria === 'object' &&
      !Array.isArray(data.rubric_criteria) &&
      Object.keys(data.rubric_criteria).length > 0
    ) {
      setConfirmedRubric(data.rubric_criteria);
      setSelectedRubric(data.rubric_criteria);
      setParsedRubrics([data.rubric_criteria]);
    }
    if (data.resolved_school_level || data.resolved_strictness) {
      setResolvedScoringContext({
        resolved_school_level: data.resolved_school_level || null,
        resolved_strictness: data.resolved_strictness || null,
      });
    }
  }, []);

  const openRubricReview = useCallback(({ extractedText, rubrics, questionSet: nextQuestionSet = null, startEditing = false, clearCurrentRubricText = true }) => {
    const safeRubrics = Array.isArray(rubrics) ? rubrics.filter(Boolean) : [];
    const firstRubric = safeRubrics[0] || null;
    if (clearCurrentRubricText) {
      setRubricText('');
      setParsedRubrics([]);
      setSelectedRubric(null);
      setQuestionSet(null);
      setSelectedQuestionIds([]);
    }
    setRubricReviewText(extractedText || '');
    setRubricReviewRubrics(safeRubrics);
    setRubricReviewRubric(firstRubric);
    setRubricReviewQuestionSet(nextQuestionSet);
    if (nextQuestionSet?.questions?.length) {
      setSelectedQuestionIds(nextQuestionSet.questions.map((q) => String(q.question_id)));
    }
    setIsRubricReviewEditing(startEditing);
    setRubricReviewTextEdited(startEditing);
    setRubricReviewBlankChoice(null);
    setRubricNeedsConfirmation(true);
    setConfirmedRubric(null);
    setIsRubricReviewOpen(true);
  }, []);

  const resetRubricReviewState = useCallback(() => {
    setIsRubricReviewOpen(false);
    setRubricNeedsConfirmation(false);
    setRubricReviewText('');
    setRubricReviewRubrics([]);
    setRubricReviewRubric(null);
    setRubricReviewQuestionSet(null);
    setIsRubricReviewEditing(false);
    setRubricReviewTextEdited(false);
    setRubricReviewBlankChoice(null);
  }, []);

  const rubricReviewBlankDefault = useMemo(() => {
    const questions = rubricReviewQuestionSet?.questions || [];
    const rubrics = questions.length > 1
      ? questions.filter((q) => selectedQuestionIds.includes(String(q.question_id))).map((q) => q.rubric_criteria)
      : [rubricReviewRubric];
    return getBlankPolicyDefault({
      rubrics: rubricReviewTextEdited ? [] : rubrics,
      rubricText: rubricReviewText,
      customInstruction: scoringOptions.custom_instruction,
    });
  }, [rubricReviewQuestionSet, rubricReviewRubric, rubricReviewText, rubricReviewTextEdited, scoringOptions.custom_instruction, selectedQuestionIds]);
  const rubricReviewBlankPolicy = rubricReviewBlankChoice || rubricReviewBlankDefault.policy;
  const rubricReviewBlankGradeOnly = Boolean(rubricReviewRubric && isGradeOnlyRubric(rubricReviewRubric));
  // 여러 문항 채점 경로에는 백지 가드가 없어 선택해도 점수에 반영되지 않는다.
  const rubricReviewMultiQuestion = (rubricReviewQuestionSet?.questions || [])
    .filter((q) => selectedQuestionIds.includes(String(q.question_id))).length > 1;
  const showRubricReviewBlankToggle = !rubricReviewBlankGradeOnly
    && !rubricReviewMultiQuestion
    && (!rubricReviewBlankDefault.hasItems || rubricReviewBlankDefault.floorTotal > 0);

  const handleRubricReviewConfirm = useCallback(() => {
    const trimmedText = rubricReviewText.trim();
    if (!trimmedText) {
      showToast('확인할 채점 기준이 없어요', 'warning');
      return;
    }

    const selectedQuestionList = rubricReviewQuestionSet?.questions?.filter((q) =>
      selectedQuestionIds.includes(String(q.question_id))
    ) || [];
    const selectedQuestionSetForScoring = selectedQuestionList.length > 1
      ? {
          ...rubricReviewQuestionSet,
          questions: selectedQuestionList,
          question_count: selectedQuestionList.length,
          total_points: selectedQuestionList.reduce((sum, q) => sum + (Number(q.max_points) || 0), 0),
        }
      : null;
    const singleSelectedRubric = selectedQuestionList.length === 1
      ? selectedQuestionList[0]?.rubric_criteria
      : rubricReviewRubric;
    const rubricForScoring = rubricReviewTextEdited ? null : singleSelectedRubric;
    const rubricsForDisplay = rubricForScoring
      ? (rubricReviewRubrics.length > 0 ? rubricReviewRubrics : [rubricForScoring])
      : [];

    setRubricText(trimmedText);
    setParsedRubrics(rubricsForDisplay);
    setSelectedRubric(rubricForScoring);
    setConfirmedRubric(rubricForScoring);
    setQuestionSet(selectedQuestionSetForScoring);
    setSelectedQuestionIds(selectedQuestionSetForScoring?.questions?.map((q) => String(q.question_id)) || []);
    setRubricInputMode('text');
    if (selectedQuestionSetForScoring) {
      sessionStorage.setItem('scoring-confirmed-question-set', JSON.stringify({
        rubric_text: trimmedText,
        question_set: selectedQuestionSetForScoring,
      }));
      sessionStorage.removeItem('scoring-confirmed-rubric');
    } else if (rubricForScoring) {
      sessionStorage.setItem('scoring-confirmed-rubric', JSON.stringify({
        rubric_text: trimmedText,
        rubric: rubricForScoring,
      }));
      sessionStorage.removeItem('scoring-confirmed-question-set');
    } else {
      sessionStorage.removeItem('scoring-confirmed-rubric');
      sessionStorage.removeItem('scoring-confirmed-question-set');
    }
    setMultiRubric(false);
    setDetectedSubjects([]);
    setScoringOptions((prev) => ({
      ...prev,
      blank_policy: selectedQuestionSetForScoring
        ? 'auto'
        : resolveBlankPolicyForRequest(rubricReviewBlankChoice, rubricReviewBlankDefault),
    }));
    setRubricNeedsConfirmation(false);
    setIsRubricReviewOpen(false);
    setIsRubricReviewEditing(false);
    setRubricReviewTextEdited(false);

    showToast(
      selectedQuestionSetForScoring
        ? `${selectedQuestionSetForScoring.question_count}개 문항을 한 번에 채점할게요`
        : rubricForScoring
          ? '이 기준으로 채점할게요'
          : '수정한 기준으로 채점할게요',
      'success'
    );
  }, [rubricReviewTextEdited, rubricReviewRubric, rubricReviewRubrics, rubricReviewText, rubricReviewQuestionSet, rubricReviewBlankChoice, rubricReviewBlankDefault, selectedQuestionIds, showToast]);

  const handleRubricReviewReset = useCallback(() => {
    setRubricFile(null);
    setMultiRubric(false);
    setParsedRubrics([]);
    setSelectedRubric(null);
    setConfirmedRubric(null);
    setQuestionSet(null);
    setSelectedQuestionIds([]);
    setRubricText('');
    setDetectedSubjects([]);
    setRawText('');
    sessionStorage.removeItem('scoring-confirmed-rubric');
    sessionStorage.removeItem('scoring-confirmed-question-set');
    setScoringOptions((prev) => (prev.blank_policy === 'auto' ? prev : { ...prev, blank_policy: 'auto' }));
    resetRubricReviewState();
  }, [resetRubricReviewState]);

  const resetDirectBatchUpload = useCallback(() => {
    setBatchAnswerStructure(null);
    setBatchFiles([]);
    setBatchStudents([]);
    setBatchFilesStats([]);
    setBatchCarryoverFiles([]);
    setBatchCarryoverFilesStats([]);
    setScanPdfDetected(false);
    setScanPdfFile(null);
    setScanPdfPageCount(0);
    setScanRangePreview(null);
    setScanPageRanges([]);
    setScanRangeError('');
    setSplitPreviewFile(null);
    setSplitPreview(null);
    setSplitPreviewLoading(false);
    setSplitPreviewProgress(0);
    setSplitPreviewMessage('');
  }, []);

  const handleRubricTextChange = useCallback((nextText) => {
    if ((confirmedRubric || selectedRubric || questionSet) && !rubricNeedsConfirmation) {
      openRubricReview({
        extractedText: nextText,
        rubrics: [],
        startEditing: true,
        clearCurrentRubricText: true,
      });
      sessionStorage.removeItem('scoring-confirmed-rubric');
      sessionStorage.removeItem('scoring-confirmed-question-set');
      return;
    }

    setRubricText(nextText);
    setConfirmedRubric(null);
    setSelectedRubric(null);
    setParsedRubrics([]);
    setQuestionSet(null);
    setSelectedQuestionIds([]);
    setRubricNeedsConfirmation(false);
    setScoringOptions((prev) => (prev.blank_policy === 'auto' ? prev : { ...prev, blank_policy: 'auto' }));
    sessionStorage.removeItem('scoring-confirmed-rubric');
    sessionStorage.removeItem('scoring-confirmed-question-set');
  }, [confirmedRubric, selectedRubric, questionSet, rubricNeedsConfirmation, openRubricReview]);

  const ensureRubricReady = useCallback(() => {
    if (rubricNeedsConfirmation) {
      setIsRubricReviewOpen(true);
      showToast('채점 기준을 먼저 확인해 주세요', 'warning');
      return false;
    }
    if (!rubricText.trim()) {
      showToast('채점 기준서를 먼저 입력해 주세요', 'warning');
      return false;
    }
    return true;
  }, [rubricNeedsConfirmation, rubricText, showToast]);

  const performRubricFilePreCheck = useCallback(async (file) => {
    setIsFileChecking(true);
    setCheckingFileName(file.name);
    setPreCheckLoading(true);
    setShowPreCheckModal(true);
    
    try {
      const result = await preCheckFiles([file], 'rubric', isPro);
      
      const hasNoIssues = result.can_proceed && 
                          !result.needs_confirmation && 
                          result.overall_status === 'ok';
      
      if (hasNoIssues) {
        setShowPreCheckModal(false);
        setPreCheckLoading(false);
        setPreCheckResult(null);
        actualRubricFileUploadRef.current(file);
      } else {
        setPreCheckResult(result);
        setPendingRubricFile(file);
        setPreCheckLoading(false);
      }
      
    } catch (error) {
      console.error('파일 사전 검사 오류:', error);
      setShowPreCheckModal(false);
      setPreCheckLoading(false);
      setPreCheckResult(null);
      actualRubricFileUploadRef.current(file);
    } finally {
      setIsFileChecking(false);
      setCheckingFileName('');
    }
  }, [isPro]);

  // 평가 기준 파일 업로드 핸들러 (사전 검사 호출)
  const handleRubricFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // PRO 권한 확인 (파일 첨부)
    if (!checkProAccess()) return;
    
    performRubricFilePreCheck(file);
  }, [performRubricFilePreCheck, checkProAccess]);

  // 실제 평가 기준 파일 업로드
  const actualRubricFileUpload = useCallback(async (file) => {
    // 채점 기준 분석 로딩 메시지 목록 (useCallback 내부에 정의)
    const messages = [
      '파일을 읽고 있어요',
      '문서 구조를 분석하고 있어요',
      '평가 요소를 추출하고 있어요',
      '배점 기준을 정리하고 있어요',
      '수행 수준을 파악하고 있어요',
      '채점 기준표를 생성하고 있어요',
    ];
    
    setRubricFile(file);
    setRubricFileUploading(true);
    setRubricLoadingProgress(0);
    setRubricLoadingMessage(messages[0]);
    
    // 로딩 메시지 변경 인터벌 (20초마다)
    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % messages.length;
      setRubricLoadingMessage(messages[messageIndex]);
    }, 20000);
    
    // 프로그레스 바 업데이트 (2분 기준: 1.3초마다 1%씩, 최대 90%)
    const progressInterval = setInterval(() => {
      setRubricLoadingProgress(prev => Math.min(prev + 1, 90));
    }, 1300);

    try {
      // FormData 생성
      const formData = new FormData();
      formData.append('file', file);

      // 토큰 가져오기
      const token = currentUser ? await currentUser.getIdToken() : null;
      if (!token) {
        showToast('로그인이 필요해요', 'warning');
        clearInterval(messageInterval);
        clearInterval(progressInterval);
        setRubricFileUploading(false);
        return;
      }

      // API 호출하여 파일 내용 추출
      const response = await fetchWithRetry(
        getApiUrl('/api/lab/extract-rubric'),
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData },
        { maxRetries: 1, timeoutMs: calcApiTimeout('rubric-extract') }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || '파일 분석에 실패했어요');
      }

      const data = await response.json();
      
      setRubricLoadingProgress(100);
      
      if (data.success) {
        if (data.multi_rubric && data.detected_subjects && data.detected_subjects.length > 1) {
          // 2단계 흐름: 과목 목록만 반환됨 → 사용자가 선택
          setMultiRubric(true);
          setDetectedSubjects(data.detected_subjects);
          setRawText(data.raw_text || '');
          setParsedRubrics([]);
          setSelectedRubric(null);
          setRubricText('');
          showToast(`${data.detected_subjects.length}개 서술형 평가를 감지했어요. 아래에서 선택해 주세요`, 'info', 5000);
        } else if (data.extracted_text) {
          setMultiRubric(false);
          setDetectedSubjects([]);
          openRubricReview({
            extractedText: data.extracted_text,
            rubrics: data.parsed_rubrics || [],
            questionSet: data.question_set || null,
          });
          showToast('채점 기준을 추출했어요. 한 번만 확인해 주세요', 'success');
        } else {
          throw new Error(data.message || '파일에서 내용을 추출할 수 없어요');
        }
      } else {
        throw new Error(data.message || '파일에서 내용을 추출할 수 없어요');
      }
    } catch (error) {
      console.error('평가 기준 파일 분석 실패:', error);
      showToast(error.message || '파일 분석 중 오류가 발생했어요', 'error');
      setRubricFile(null);
      setMultiRubric(false);
      setParsedRubrics([]);
      setSelectedRubric(null);
      setConfirmedRubric(null);
      setQuestionSet(null);
      setSelectedQuestionIds([]);
      resetRubricReviewState();
      setDetectedSubjects([]);
      setRawText('');
    } finally {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
      setRubricFileUploading(false);
      setRubricLoadingProgress(0);
    }
  }, [currentUser, showToast, openRubricReview, resetRubricReviewState]);
  
  // 🔧 ref 업데이트 (최신 함수 참조 유지)
  actualRubricFileUploadRef.current = actualRubricFileUpload;

  // 평가 기준 파일 제거
  const handleRubricFileRemove = useCallback(() => {
    setRubricFile(null);
    setMultiRubric(false);
    setParsedRubrics([]);
    setSelectedRubric(null);
    setConfirmedRubric(null);
    setQuestionSet(null);
    setSelectedQuestionIds([]);
    setRubricText('');
    setDetectedSubjects([]);
    setRawText('');
    sessionStorage.removeItem('scoring-confirmed-rubric');
    sessionStorage.removeItem('scoring-confirmed-question-set');
    setScoringOptions((prev) => (prev.blank_policy === 'auto' ? prev : { ...prev, blank_policy: 'auto' }));
    resetRubricReviewState();
  }, [resetRubricReviewState]);

  // 과목/문항 선택 → 상세 분석 호출 (2단계). __all__이면 감지된 전체 문항을 기본 선택으로 분석.
  const handleSubjectSelect = useCallback(async (subject) => {
    if (!rawText || !subject?.name) return;
    
    setRubricDetailLoading(true);
    try {
      const token = currentUser ? await currentUser.getIdToken() : null;
      if (!token) {
        showToast('로그인이 필요해요', 'warning');
        return;
      }

      const response = await fetchWithRetry(
        getApiUrl('/api/lab/extract-rubric-detail'),
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ raw_text: rawText, selected_subject: subject.name }),
        },
        { maxRetries: 1, timeoutMs: calcApiTimeout('rubric-extract') }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || '상세 분석에 실패했어요');
      }

      const data = await response.json();
      if (data.success && data.extracted_text) {
        setMultiRubric(false);
        setDetectedSubjects([]);
        openRubricReview({
          extractedText: data.extracted_text,
          rubrics: data.parsed_rubrics || [],
          questionSet: data.question_set || null,
        });
        showToast(
          subject.name === '__all__'
            ? '전체 문항 기준을 추출했어요. 한 번만 확인해 주세요'
            : `'${subject.name}' 채점 기준을 추출했어요. 한 번만 확인해 주세요`,
          'success'
        );
      } else {
        throw new Error('상세 분석 결과를 받지 못했어요');
      }
    } catch (error) {
      console.error('상세 분석 실패:', error);
      showToast(error.message || '상세 분석 중 오류가 발생했어요', 'error');
    } finally {
      setRubricDetailLoading(false);
    }
  }, [rawText, currentUser, showToast, openRubricReview]);

  // 🔍 파일 사전 검사 수행 (답안 파일)
  const performAnswerFilesPreCheck = useCallback(async (files) => {
    // 로딩 시작
    setPreCheckLoading(true);
    setShowPreCheckModal(true);
    
    // 📎 제한 상수 (Single Source of Truth: constants/fileLimits.js)
    // Free/Pro 공통: 200,000자
    const MAX_FILES = MAX_FILES_DEFAULT;
    const { contentLength: MAX_CONTENT_LENGTH } = getFileLimits(isPro);
    
    // 이미 첨부된 파일 정보 합계
    const currentStats = {
      fileCount: answerFiles.length,
      ...summarizeFileStats(answerFilesStats),
    };
    
    // 파일 수 초과 체크 (프론트에서 먼저 체크)
    const totalFileCount = currentStats.fileCount + files.length;
    if (totalFileCount > MAX_FILES) {
      const availableSlots = Math.max(0, MAX_FILES - currentStats.fileCount);
      setPreCheckResult({
        can_proceed: false,
        needs_confirmation: false,
        overall_status: 'error',
        total_files: files.length,
        total_size: files.reduce((sum, f) => sum + f.size, 0),
        total_size_display: `${(files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(1)}MB`,
        files_info: files.map(f => ({
          filename: f.name,
          status: 'ok',
          file_size: f.size,
          file_size_display: `${(f.size / 1024).toFixed(1)}KB`,
          issues: []
        })),
        recommendations: availableSlots > 0 
          ? [`개별 채점은 한 학생 자료 기준으로 현재 ${availableSlots}개 파일만 더 올릴 수 있어요.`]
          : ['학생 여러 명을 채점하려면 여러 명 채점으로 바꿔주세요'],
        file_count_error: `1명 채점은 파일 ${MAX_FILES}개까지예요. 학생 여러 명을 채점하려면 여러 명 채점에서 최대 ${BATCH_STUDENT_FILE_MAX}명까지 올릴 수 있어요.`,
        alternate_action: 'switch_to_batch_student_files'
      });
      setPendingAnswerFiles(files);
      setPreCheckLoading(false);
      return;
    }

    const combinedFiles = [...answerFiles, ...files];
    const totalRequestFileSize = combinedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
    const MAX_REQUEST_FILE_SIZE = 25 * 1024 * 1024;
    if (totalRequestFileSize > MAX_REQUEST_FILE_SIZE) {
      setPreCheckResult({
        can_proceed: false,
        needs_confirmation: false,
        overall_status: 'error',
        title: '파일 용량을 줄여주세요',
        message: `1명 채점 파일 합계가 ${(totalRequestFileSize / 1024 / 1024).toFixed(1)}MB예요. 한 번에 25MB까지만 첨부할 수 있어요.`,
        total_files: files.length,
        total_size: totalRequestFileSize,
        total_size_display: `${(totalRequestFileSize / 1024 / 1024).toFixed(1)}MB`,
        files_info: files.map(f => ({
          filename: f.name,
          status: 'warning',
          file_size: f.size,
          file_size_display: `${(f.size / 1024 / 1024).toFixed(1)}MB`,
          issues: [],
        })),
        recommendations: ['파일을 압축하거나 나눠서 첨부해 주세요.', '학생 여러 명 자료라면 여러 명 채점으로 나눠 올려주세요.'],
      });
      setPendingAnswerFiles(null);
      setPreCheckLoading(false);
      return;
    }
    
    try {
      const result = await preCheckFiles(files, 'lab', isPro);
      
      // 새 파일들의 페이지/슬라이드/문자수 합계 (백엔드에서 실제 추출한 값 사용)
      const newFileStats = normalizePreCheckStats(result.files_info || []);
      const newStats = summarizeFileStats(newFileStats);
      
      // 합산 제한 체크
      const totalPages = currentStats.pageCount + newStats.pageCount;
      const totalSlides = currentStats.slideCount + newStats.slideCount;
      const totalContentLength = currentStats.contentLength + newStats.contentLength;
      
      const exceededLimits = [];
      
      if (totalPages > MAX_PDF_PAGES) {
        exceededLimits.push(`PDF 페이지: 현재 ${currentStats.pageCount}페이지 + 새 파일 ${newStats.pageCount}페이지 = 총 ${totalPages}페이지 (최대 ${MAX_PDF_PAGES}페이지)`);
      }
      
      if (totalSlides > MAX_PPT_SLIDES) {
        exceededLimits.push(`PPT 슬라이드: 현재 ${currentStats.slideCount}개 + 새 파일 ${newStats.slideCount}개 = 총 ${totalSlides}개 (최대 ${MAX_PPT_SLIDES}개)`);
      }
      
      if (totalContentLength > MAX_CONTENT_LENGTH) {
        exceededLimits.push(`총 분석량: 현재 ${currentStats.contentLength.toLocaleString()}자 + 새 파일 ${newStats.contentLength.toLocaleString()}자 = 총 ${totalContentLength.toLocaleString()}자 (최대 ${MAX_CONTENT_LENGTH.toLocaleString()}자)`);
      }
      
      // 합산 초과 시 경고 추가
      if (exceededLimits.length > 0) {
        result.can_proceed = false;
        result.needs_confirmation = false;
        result.overall_status = 'error';
        result.exceeded_limits = exceededLimits;
        result.recommendations = [
          '기존 파일을 삭제하거나, 더 작은 파일을 업로드해 주세요',
          ...exceededLimits
        ];
      }
      
      // 결과에 새 파일 통계 저장 (업로드 시 사용)
      const filesStats = newFileStats;
      result._newFilesStats = filesStats;
      
      // 🆕 문제가 없으면 모달 없이 바로 진행
      const hasNoIssues = result.can_proceed && 
                          !result.needs_confirmation && 
                          result.overall_status === 'ok';
      
      if (hasNoIssues) {
        // 모달 닫고 바로 업로드 진행
        setShowPreCheckModal(false);
        setPreCheckLoading(false);
        setPreCheckResult(null);
        actualAnswerFilesUploadRef.current(files, filesStats);
      } else {
        // 문제가 있으면 모달로 표시 (경고/에러)
        setPreCheckResult(result);
        setPendingAnswerFiles(files);
        setPreCheckLoading(false);
      }
      
    } catch (error) {
      console.error('파일 사전 검사 오류:', error);
      // 오류 시에도 바로 진행 (검사 실패해도 업로드는 허용)
      setShowPreCheckModal(false);
      setPreCheckLoading(false);
      setPreCheckResult(null);
      actualAnswerFilesUploadRef.current(files, []);
    }
  }, [answerFiles, answerFilesStats, isPro]);

  // 🔍 사전 검사 확인 후 업로드 진행
  const handlePreCheckConfirm = useCallback(() => {
    setShowPreCheckModal(false);
    if (pendingAnswerFiles) {
      const filesStats = preCheckResult?._newFilesStats || [];
      actualAnswerFilesUploadRef.current(pendingAnswerFiles, filesStats);
      setPendingAnswerFiles(null);
    }
    if (pendingRubricFile) {
      actualRubricFileUploadRef.current(pendingRubricFile);
      setPendingRubricFile(null);
    }
    if (pendingBatchFilesRef.current) {
      const filesStats = preCheckResult?._newFilesStats || [];
      actualBatchFilesUploadRef.current(pendingBatchFilesRef.current, filesStats);
      pendingBatchFilesRef.current = null;
    }
    setPreCheckResult(null);
  }, [pendingAnswerFiles, pendingRubricFile, preCheckResult]);

  // 🔍 사전 검사 취소
  const handlePreCheckCancel = useCallback(() => {
    setShowPreCheckModal(false);
    setPendingAnswerFiles(null);
    setPendingRubricFile(null);
    pendingBatchFilesRef.current = null;
    setPreCheckResult(null);
  }, []);

  // 📎 실제 답안 파일 업로드
  const actualAnswerFilesUpload = useCallback((files, filesStats = []) => {
    setAnswerFiles(prev => [...prev, ...files]);
    setAnswerFilesStats(prev => [...prev, ...filesStats]);
  }, []);
  
  // 🔧 ref 업데이트 (최신 함수 참조 유지)
  actualAnswerFilesUploadRef.current = actualAnswerFilesUpload;

  // 답안 파일 업로드 (사전 검사 포함)
  const handleAnswerFilesUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    const MAX_FILES = MAX_FILES_DEFAULT;
    // 📎 파일 크기 (Pro 30MB / Free 30MB)
    const { fileSize: MAX_SIZE } = getFileLimits(isPro);
    const maxSizeMB = Math.round(MAX_SIZE / (1024 * 1024));
    
    if (files.length === 0) return;
    
    // PRO 권한 확인 (파일 첨부)
    if (!checkProAccess()) return;
    
    // 파일 수 제한 체크
    const totalFiles = answerFiles.length + files.length;
    if (totalFiles > MAX_FILES) {
      performAnswerFilesPreCheck(files);
      return;
    }
    
    // 파일 크기 체크
    const oversizedFiles = files.filter(f => f.size > MAX_SIZE);
    if (oversizedFiles.length > 0) {
      showToast(`${maxSizeMB}MB 이하 파일만 올릴 수 있어요. 파일 크기를 줄여주세요`, 'warning');
      return;
    }
    
    // 파일 사전 검사 수행
    performAnswerFilesPreCheck(files);
  }, [answerFiles.length, performAnswerFilesPreCheck, checkProAccess, showToast, isPro]);

  // 답안 파일 제거
  const handleAnswerFileRemove = useCallback((index) => {
    setAnswerFiles(prev => prev.filter((_, i) => i !== index));
    setAnswerFilesStats(prev => prev.filter((_, i) => i !== index));
  }, []);

  // 드래그 앤 드롭 핸들러 (학생 과제)
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    // PRO 권한 확인 (파일 첨부)
    if (!checkProAccess()) return;
    
    const files = Array.from(e.dataTransfer.files);
    const MAX_FILES = MAX_FILES_DEFAULT;
    // 📎 파일 크기 (Pro 30MB / Free 30MB)
    const { fileSize: MAX_SIZE } = getFileLimits(isPro);
    const maxSizeMB = Math.round(MAX_SIZE / (1024 * 1024));
    
    // 파일 수 제한 체크
    const totalFiles = answerFiles.length + files.length;
    if (totalFiles > MAX_FILES) {
      showToast(`파일은 최대 ${MAX_FILES}개까지 올릴 수 있어요. 개수를 줄여주세요`, 'warning');
      return;
    }
    
    // 파일 크기 체크
    const oversizedFiles = files.filter(f => f.size > MAX_SIZE);
    if (oversizedFiles.length > 0) {
      showToast(`${maxSizeMB}MB 이하 파일만 올릴 수 있어요. 파일 크기를 줄여주세요`, 'warning');
      return;
    }
    
    // 파일 사전 검사 수행
    performAnswerFilesPreCheck(files);
  }, [checkProAccess, answerFiles.length, performAnswerFilesPreCheck, showToast, isPro]);

  // 드래그 앤 드롭 핸들러 (채점 기준서)
  const handleRubricDragOver = useCallback((e) => {
    e.preventDefault();
    setIsRubricDragging(true);
  }, []);

  const handleRubricDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsRubricDragging(false);
  }, []);

  const handleRubricDrop = useCallback((e) => {
    e.preventDefault();
    setIsRubricDragging(false);
    
    // PRO 권한 확인 (파일 첨부)
    if (!checkProAccess()) return;
    
    const files = Array.from(e.dataTransfer.files);
    // 📎 파일 크기 (Pro 30MB / Free 30MB)
    const { fileSize: MAX_SIZE } = getFileLimits(isPro);
    const maxSizeMB = Math.round(MAX_SIZE / (1024 * 1024));
    
    // 파일 1개만 허용
    if (files.length > 1) {
      showToast('채점 기준서는 파일 1개만 올릴 수 있어요. 하나만 골라주세요', 'warning');
      return;
    }
    
    const file = files[0];
    if (!file) return;
    
    // 파일 크기 체크
    if (file.size > MAX_SIZE) {
      showToast(`${maxSizeMB}MB 이하 파일만 올릴 수 있어요. 파일 크기를 줄여주세요`, 'warning');
      return;
    }
    
    // 파일 사전 검사 수행
    performRubricFilePreCheck(file);
  }, [checkProAccess, performRubricFilePreCheck, showToast, isPro]);

  // 엑셀 다운로드
  const handleDownloadExcel = useCallback(async () => {
    if (evaluationResults.length === 0) return;

    try {
      trackScoringUxEvent('export', { format: 'xlsx', mode: 'single', isPro, isAnonymous: isAnonymousUser, sinceOpenMs: Date.now() - pageOpenedAtRef.current });
      const XLSX = await import('xlsx');
      const excelData = buildScoringResultExportRows({
        results: evaluationResults,
        cachedAnswerTexts,
        rubric: getActiveRubricCriteria(),
      });
      writeRecognitionWorkbook(XLSX, excelData, {
        filename: `서술형채점결과_${getRecognitionExportDate()}.xlsx`,
        sheetName: '채점결과',
      });
      showToast('AI가 읽은 답안 원문과 채점 결과를 다운로드 폴더에 저장했어요.', 'success', 10000);
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      showToast('엑셀 다운로드에 실패했어요', 'error');
    }
  }, [cachedAnswerTexts, evaluationResults, getActiveRubricCriteria, showToast, isPro, isAnonymousUser]);

  // 로딩 단계 (체크리스트 형태)
  const loadingSteps = [
    '채점 기준 분석',
    '과제 내용 읽기',
    '점수 산정',
    '피드백 작성',
    '생기부 문구 생성',
    '최종 검토',
  ];

  // 현재 완료된 단계 추적
  const [completedSteps, setCompletedSteps] = useState(0);

  const [currentTip, setCurrentTip] = useState('');

  // 답안 평가 제출
  const handleEvaluationSubmit = async () => {
    // 채점 기준서 필수 체크
    if (!ensureRubricReady()) return;
    
    // 파일 또는 텍스트 입력 확인
    if (answerFiles.length === 0 && !answerText.trim()) {
      showToast('학생 과제를 입력해 주세요', 'warning');
      return;
    }

    // 비로그인 사용자: 1회 체험 허용, 2회차부터 가입 필수
    if (isAnonymousUser) {
      localStorage.setItem('scoring-saved-rubric', rubricText);
      localStorage.setItem('scoring-saved-answer', answerText);
      if (anonScoringCount >= 1) {
        setShowLoginModal(true);
        return;
      }
    }

    setIsLoading(true);
    setEvaluationResults([]);
    setSavedScoringRecordId(null);
    singleAutoSavePendingRef.current = false;
    setLoadingProgress(0);
    setCompletedSteps(0);
    const tipInterval = startTeacherTipTimer(setCurrentTip);
    
    // 단계별 체크리스트 진행 — 1명 채점 실측 p50 26초·p90 41초(N=89)에 맞춰 5초 간격
    let stepIndex = 0;
    const messageInterval = setInterval(() => {
      stepIndex = Math.min(stepIndex + 1, loadingSteps.length - 1);
      setCompletedSteps(stepIndex);
    }, 5000);
    
    // 프로그레스 바 업데이트 (p90 41초 기준: 0.45초마다 1%씩, 최대 90%)
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => Math.min(prev + 1, 90));
    }, 450);

    const longWaitTimers = [
      setTimeout(() => {
        showToast('평소보다 조금 오래 걸리고 있어요. 기준서와 답안을 깊게 비교하는 중이니 잠시만 기다려주세요.', 'info', 9000);
      }, 45000),
      setTimeout(() => {
        showToast('아직 채점 중이에요. 멈춘 게 아니라 AI가 근거와 점수를 검토하는 중이에요.', 'info', 9000);
      }, 120000),
    ];

    try {
      const formData = new FormData();
      formData.append('rubric_text', rubricText);
      appendScoringOptions(formData);
      const activeQuestionSet = getActiveQuestionSet();
      if (activeQuestionSet) {
        formData.append('question_set', JSON.stringify(activeQuestionSet));
      } else {
        appendActiveRubricCriteria(formData);
      }
      
      answerFiles.forEach((file) => {
        formData.append('answer_files', file);
      });
      
      if (answerText.trim()) {
        formData.append('answer_text', answerText.trim());
      }

      // 토큰: 로그인 유저만 전송, 비로그인은 null (auth-optional)
      const token = currentUser && !isAnonymous ? await currentUser.getIdToken() : null;
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetchWithRetry(
        getApiUrl(activeQuestionSet ? '/api/lab/evaluate-answers-multi-question' : '/api/lab/evaluate-answers'),
        { method: 'POST', headers, body: formData },
        { maxRetries: 1, timeoutMs: calcApiTimeout('single-scoring') }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || '평가 중 오류가 발생했어요');
      }

      const data = await response.json();
      
      setLoadingProgress(100);
      
      if (data.success) {
        applyScoringContextFromResponse(data);
        setEvaluationResults(data.results || []);
        setIsSaved(false);
        singleAutoSavePendingRef.current = !isAnonymousUser
          && (data.results || []).some((result) => result && !result.error && result.storage_path);
        setReScoreInstructions('');
        setReScoreCount(0);
        setRescoreHistory([]);
        setActiveScoringPolicy(buildActiveScoringPolicyPayload());
        if (data.extracted_answer_texts) {
          setCachedAnswerTexts(data.extracted_answer_texts);
        }
        // 채점 시점에 제외된 파일(추출 실패·크기 초과 등)을 교사에게 즉시 알린다.
        // 사전 검사를 통과해도 채점 중 실패로 조용히 빠지는 케이스의 최후 방어.
        if (Array.isArray(data.file_warnings) && data.file_warnings.length > 0) {
          showToast(`채점에서 제외된 파일이 있어요. ${data.file_warnings.join(' / ')}`, 'warning', 8000);
        }
        trackRiseFeatureAction('scoring', 'generate', {
          mode: activeQuestionSet ? 'multi_question_single' : 'single',
          student_count: (data.results || []).length,
          question_count: activeQuestionSet?.question_count || 1,
        });
        setTimeout(() => {
          resultsTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);

        // 비로그인 사용자 체험 카운터 증가 + 결과 보존
        if (isAnonymousUser) {
          const newCount = anonScoringCount + 1;
          setAnonScoringCount(newCount);
          sessionStorage.setItem('scoring-anon-count', String(newCount));
          try { localStorage.setItem('scoring-saved-result', JSON.stringify(data.results || [])); } catch {}
        }
      } else {
        throw new Error(data.message || '평가 결과를 받을 수 없어요');
      }
    } catch (error) {
      console.error('답안 평가 실패:', error);
      showToast(error.message || '채점 중 오류가 발생했어요', 'error');
    } finally {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
      clearInterval(tipInterval);
      longWaitTimers.forEach(clearTimeout);
      setIsLoading(false);
      setLoadingProgress(0);
      setCompletedSteps(0);
      setCurrentTip('');
    }
  };

  // 재채점 (개별/일괄 공통)
  const handleReScore = async (optionsOverride = null) => {
    const isBatch = scoringMode === 'batch' || batchResults.length > 0;
    const nextScoringOptions = optionsOverride
      ? getScoringOptionsForMode({ ...scoringOptions, ...optionsOverride }, isBatch ? 'batch' : 'single')
      : getScoringOptionsForMode(null, isBatch ? 'batch' : 'single');
    const instructionText = reScoreInstructions.trim();

    if (!instructionText && !optionsOverride) {
      showToast('추가 요청을 입력해주세요', 'warning');
      return;
    }
    if (!canReScoreCurrentResults) {
      showToast('이전 채점 데이터가 없어요. 새로 채점해주세요.', 'warning');
      return;
    }

    setReScoreLoading(true);
    setTimeout(() => {
      resultsTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    const tipInterval = startTeacherTipTimer(setCurrentTip);

    try {
      const token = currentUser && !isAnonymous ? await currentUser.getIdToken() : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // 직전 결과를 모델 컨텍스트로 보내기 위해 전체 dict로 전달
      // (백엔드는 신형 dict·구형 number 모두 호환)
      const resultsSource = isBatch ? batchResults : evaluationResults;
      const answerTextsForRescore = mergeCachedAnswerTextsWithResults(cachedAnswerTexts, [], resultsSource);

      const previousScores = resultsSource.map((result, index) => {
        const cached = answerTextsForRescore[index] || {};
        return {
          total_score: result.total_score ?? result.score ?? 0,
          item_scores: Array.isArray(result.item_scores) ? result.item_scores : [],
          question_results: Array.isArray(result.question_results) ? result.question_results : [],
          student_name: getScoringStudentName(result, cached.student_name || `학생 ${index + 1}`),
          fileName: result.fileName || result.filename || cached.fileName || cached.filename,
          client_student_id: result.client_student_id || cached.client_student_id || null,
          submission_id: result.submission_id || cached.submission_id || null,
          split_student_id: result.split_student_id || cached.split_student_id || null,
          pages: result.pages || cached.pages || '',
          source_pages: result.source_pages || cached.source_pages || [],
        };
      });

      const activeQuestionSet = getActiveQuestionSet();
      if (activeQuestionSet) {
        const nextResults = [];
        const nextExtractedTexts = [];
        let latestScoringMemory = null;
        for (let idx = 0; idx < answerTextsForRescore.length; idx += 1) {
          const cached = answerTextsForRescore[idx] || {};
          const previous = previousScores[idx] ? [previousScores[idx]] : [];
          const response = await fetchWithRetry(
            getApiUrl('/api/lab/re-evaluate-multi-question'),
            {
              method: 'POST',
              headers,
              body: JSON.stringify({
                rubric_text: rubricText,
                question_set: activeQuestionSet,
                answer_text: cached.answer_text || '',
                student_name: cached.student_name || getScoringStudentName(resultsSource[idx], `학생 ${idx + 1}`),
                client_student_id: cached.client_student_id
                  || resultsSource[idx]?.client_student_id
                  || cached.split_student_id
                  || resultsSource[idx]?.split_student_id
                  || null,
                additional_instructions: instructionText,
                previous_scores: previous,
                scoring_options: nextScoringOptions,
                active_scoring_policy: buildActiveScoringPolicyPayload(nextScoringOptions),
                rescore_history: rescoreHistory,
              }),
            },
            { maxRetries: 1, timeoutMs: calcApiTimeout('single-scoring') }
          );
          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.detail || '다문항 재채점 중 오류가 발생했어요');
          }
          const data = await response.json();
          if (data.rescore_history || data.active_scoring_policy) {
            latestScoringMemory = data;
          }
          const result = (data.results || [])[0];
          if (result) {
            const previousResult = resultsSource[idx] || {};
            nextResults.push(mergeScoringResultMeta(previousResult, result, idx));
          }
          if (Array.isArray(data.extracted_answer_texts)) {
            nextExtractedTexts.push(...data.extracted_answer_texts);
          }
        }
        if (isBatch) {
          setPreviousResults({ type: 'batch', data: batchResults });
          setBatchResults(nextResults);
        } else {
          setPreviousResults({ type: 'single', data: evaluationResults });
          setEvaluationResults(nextResults);
        }
        setCachedAnswerTexts(mergeCachedAnswerTextsWithResults(answerTextsForRescore, nextExtractedTexts, nextResults));
        applyScoringContextFromResponse({ ...(latestScoringMemory || nextResults[0] || {}), scoring_options: nextScoringOptions });
        if (optionsOverride) setScoringOptions(nextScoringOptions);
        setReScoreCount(prev => prev + 1);
        setReScoreInstructions('');
        setIsSaved(false);
        setSavedScoringRecordId(null);
        showToast('재채점이 완료되었어요', 'success');
        setTimeout(() => {
          resultsTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        return;
      }

      const endpoint = isBatch
        ? getApiUrl('/api/lab/re-evaluate-batch')
        : getApiUrl('/api/lab/re-evaluate');

      const requestBody = isBatch
        ? {
            rubric_text: rubricText,
            answer_texts: answerTextsForRescore,
            additional_instructions: instructionText,
            previous_scores: previousScores,
            scoring_options: nextScoringOptions,
            active_scoring_policy: buildActiveScoringPolicyPayload(nextScoringOptions),
            rescore_history: rescoreHistory,
          }
        : {
            rubric_text: rubricText,
            answer_text: answerTextsForRescore[0]?.answer_text || '',
            student_name: answerTextsForRescore[0]?.student_name || getScoringStudentName(resultsSource[0], '학생'),
            additional_instructions: instructionText,
            previous_scores: previousScores,
            scoring_options: nextScoringOptions,
            active_scoring_policy: buildActiveScoringPolicyPayload(nextScoringOptions),
            rescore_history: rescoreHistory,
          };
      const rubricCriteria = getActiveRubricCriteria();
      if (rubricCriteria) {
        requestBody.rubric_criteria = rubricCriteria;
      }

      if (isBatch) {
        const chunkedResults = [];
        const chunkedExtractedTexts = [];
        let latestScoringMemory = null;
        for (let start = 0; start < answerTextsForRescore.length; start += RESCORE_BATCH_CHUNK_SIZE) {
          const answerChunk = answerTextsForRescore.slice(start, start + RESCORE_BATCH_CHUNK_SIZE);
          const previousChunk = previousScores.slice(start, start + RESCORE_BATCH_CHUNK_SIZE);
          const chunkBody = {
            ...requestBody,
            answer_texts: answerChunk,
            previous_scores: previousChunk,
          };
          const response = await fetchWithRetry(
            endpoint,
            { method: 'POST', headers, body: JSON.stringify(chunkBody) },
            {
              maxRetries: 1,
              timeoutMs: calcApiTimeout('batch-scoring', {
                fileCount: answerChunk.length,
                hasImages: false,
              }),
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.detail || '재채점 중 오류가 발생했어요');
          }

          const data = await response.json();
          if (!data.success) {
            throw new Error(data.message || '재채점 결과를 받을 수 없어요');
          }

          applyScoringContextFromResponse(data);
          latestScoringMemory = data;
          chunkedResults.push(...(data.results || []).map((result, localIndex) => ({
            ...result,
            student_id: start + localIndex + 1,
          })));
          if (Array.isArray(data.extracted_answer_texts)) {
            chunkedExtractedTexts.push(...data.extracted_answer_texts);
          }
        }

        if (optionsOverride) setScoringOptions(nextScoringOptions);
        const mergedResults = mergeScoringResultsMeta(resultsSource, chunkedResults);
        setPreviousResults({ type: 'batch', data: batchResults });
        setBatchResults(mergedResults);
        setCachedAnswerTexts(mergeCachedAnswerTextsWithResults(
          answerTextsForRescore,
          chunkedExtractedTexts,
          mergedResults,
        ));
        applyScoringContextFromResponse({ ...(latestScoringMemory || {}), scoring_options: nextScoringOptions });
        setReScoreCount(prev => prev + 1);
        setReScoreInstructions('');
        setIsSaved(false);
        setSavedScoringRecordId(null);

        showToast('재채점이 완료되었어요', 'success');
        setTimeout(() => {
          resultsTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        return;
      }

      const reScoreTimeoutMs = isBatch
        ? calcApiTimeout('batch-scoring', { fileCount: cachedAnswerTexts.length, hasImages: false })
        : calcApiTimeout('single-scoring');
      const response = await fetchWithRetry(
        endpoint,
        { method: 'POST', headers, body: JSON.stringify(requestBody) },
        { maxRetries: 1, timeoutMs: reScoreTimeoutMs }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || '재채점 중 오류가 발생했어요');
      }

      const data = await response.json();

      if (data.success) {
        applyScoringContextFromResponse(data);
        if (optionsOverride) setScoringOptions(nextScoringOptions);
        const mergedResults = mergeScoringResultsMeta(resultsSource, data.results || []);
        if (isBatch) {
          setPreviousResults({ type: 'batch', data: batchResults });
          setBatchResults(mergedResults);
        } else {
          setPreviousResults({ type: 'single', data: evaluationResults });
          setEvaluationResults(mergedResults);
        }
        setCachedAnswerTexts(mergeCachedAnswerTextsWithResults(
          answerTextsForRescore,
          data.extracted_answer_texts || [],
          mergedResults,
        ));
        setReScoreCount(prev => prev + 1);
        setReScoreInstructions('');
        setIsSaved(false);
        setSavedScoringRecordId(null);

        showToast('재채점이 완료되었어요', 'success');
        setTimeout(() => {
          resultsTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        throw new Error(data.message || '재채점 결과를 받을 수 없어요');
      }
    } catch (error) {
      console.error('재채점 실패:', error);
      showToast(error.message || '재채점 중 오류가 발생했어요', 'error');
    } finally {
      setReScoreLoading(false);
      clearInterval(tipInterval);
      setCurrentTip('');
    }
  };

  const handleRestorePrevious = async () => {
    if (!previousResults) return;
    const ok = await confirmDialog({
      title: '이전 채점 결과로 되돌릴까요?',
      description: '지금 결과는 사라지고 이전 결과가 다시 보여요',
      confirmLabel: '되돌리기',
    });
    if (!ok) return;

    if (previousResults.type === 'batch') {
      setBatchResults(previousResults.data);
    } else {
      setEvaluationResults(previousResults.data);
    }
    setPreviousResults(null);
    setIsSaved(false);
    setSavedScoringRecordId(null);
    setReScoreCount(prev => Math.max(prev - 1, 0));
    showToast('이전 결과로 되돌렸어요', 'success');
  };

  // 수동 저장
  // A-5: 저장한 기준서를 자료함에도 한 벌 보관 (PRO만, 중복이면 건너뜀, 실패해도 저장 흐름에 영향 없음)
  // 파일로 올린 기준서는 rubricText 가 비어 있으므로 서버가 파싱한 기준표(parsed rubric)를 텍스트로 만들어 보관한다.
  const mirrorRubricToLibrary = useCallback((subjectName, parsedRubric = null) => {
    if (!isPro || !currentUser) return;
    const content = rubricText.trim() || buildRubricTextFromParsed(parsedRubric);
    if (!content) return;
    const firstLine = content.split('\n').find((line) => line.trim()) || '';
    const title = buildLibraryMirrorTitle('채점 기준서', subjectName || firstLine);
    mirrorTextToLibrary(currentUser, { title, content }).then((outcome) => {
      if (outcome.status === 'created') {
        showToast('채점 기준서를 자료함에도 보관했어요', 'info', 4000);
      }
    });
  }, [isPro, currentUser, rubricText, showToast]);

  // silent: 채점 직후 자동 저장. 로그인 모달·성공 토스트 없이 저장하고, 실패하면 수동 저장을 안내한다.
  const persistSingleScoringRecord = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      if (!currentUser || isAnonymous) return;
    } else if (!checkLogin()) {
      return;
    }
    if (evaluationResults.length === 0) return;
    if (isSaving) return;
    setIsSaving(true);
    try {
      const recordRubric = getActiveRubricCriteria();
      const recordQuestionSet = getActiveQuestionSet();
      const subjectName = recordQuestionSet?.questions?.[0]?.subject || recordRubric?.subject || parsedRubrics?.[0]?.subject || '';
      const saveResponse = await saveScoringRecord({
        rubric_text: rubricText,
        results: evaluationResults,
        cached_answer_texts: mergeCachedAnswerTextsWithResults(cachedAnswerTexts, [], evaluationResults),
        subject: subjectName,
        parsed_rubric: recordRubric || null,
        question_set: recordQuestionSet || null,
        type: recordQuestionSet ? 'multi-question-single' : 'single',
        ...getScoringRecordMeta(),
        ...(reScoreCount > 0 ? { re_score_count: reScoreCount } : {}),
      });
      if (saveResponse?.record_id) {
        setSavedScoringRecordId(saveResponse.record_id);
      }
      setIsSaved(true);
      mirrorRubricToLibrary(subjectName, recordRubric);
      if (!silent) {
        showToast('저장했어요', 'success', 4000, {
          label: '채점 기록 보기',
          onClick: () => navigate('/scoring-history'),
        });
      }
    } catch (err) {
      showToast(
        silent ? '채점 결과를 자동으로 저장하지 못했어요. 저장하기를 눌러주세요.' : '저장에 실패했어요. 다시 시도해주세요.',
        'error',
      );
    } finally {
      setIsSaving(false);
    }
  }, [evaluationResults, cachedAnswerTexts, rubricText, parsedRubrics, reScoreCount, isSaving, currentUser, isAnonymous, checkLogin, showToast, navigate, getActiveRubricCriteria, getActiveQuestionSet, getScoringRecordMeta, mirrorRubricToLibrary]);

  const handleSave = useCallback(() => persistSingleScoringRecord(), [persistSingleScoringRecord]);

  // 채점 응답이 기준표·채점 옵션 상태를 갱신하므로, 그 상태가 반영된 뒤 수동 저장과 같은 내용으로 저장한다.
  // 기록이 있어야 제출물 위 근거 위치 계산이 시작되기 때문에 파일 제출 결과만 자동 저장한다.
  useEffect(() => {
    if (!singleAutoSavePendingRef.current) return;
    if (isLoading || evaluationResults.length === 0) return;
    singleAutoSavePendingRef.current = false;
    persistSingleScoringRecord({ silent: true });
  }, [isLoading, evaluationResults, persistSingleScoringRecord]);

  // 일괄 결과 저장 (기존 인라인 핸들러를 분리, 동작 동일 + isSaving 가드)
  const handleSaveBatch = useCallback(async () => {
    if (!currentUser || isAnonymous) {
      setShowLoginModal(true);
      return;
    }
    if (isSaving) return;
    setIsSaving(true);
    try {
      const recordRubric = getActiveRubricCriteria();
      const subjectName = recordRubric?.subject || parsedRubrics?.[0]?.subject || '';
      const usesMethodC = batchAnswerStructure === 'singleFileStudents'
        && (hasMethodCSplitStudents(batchStudents) || batchResults.some(result => result.split_confidence || result.source_file_name));
      const saveResponse = await saveScoringRecord({
        rubric_text: rubricText,
        results: batchResults,
        cached_answer_texts: mergeCachedAnswerTextsWithResults(cachedAnswerTexts, [], batchResults),
        subject: subjectName,
        parsed_rubric: recordRubric || null,
        type: usesMethodC ? 'batch-split' : 'batch',
        student_count: batchResults.length,
        ...getScoringRecordMeta(),
        ...getMethodCRecordMeta(batchStudents),
        ...getScanRecordMeta(),
        ...(reScoreCount > 0 ? { re_score_count: reScoreCount } : {}),
      });
      if (saveResponse?.record_id) {
        setSavedScoringRecordId(saveResponse.record_id);
      }
      setIsSaved(true);
      mirrorRubricToLibrary(subjectName, recordRubric);
      showToast('저장했어요', 'success', 4000, {
        label: '채점 기록 보기',
        onClick: () => navigate('/scoring-history'),
      });
    } catch (err) {
      showToast('저장에 실패했어요', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [currentUser, isAnonymous, isSaving, batchAnswerStructure, batchStudents, batchResults, rubricText, parsedRubrics, cachedAnswerTexts, reScoreCount, getActiveRubricCriteria, getScoringRecordMeta, getMethodCRecordMeta, getScanRecordMeta, showToast, navigate, mirrorRubricToLibrary]);

  const renderSaveLabel = () => (
    isSaving ? (
      <span className="scoring-rescore-loading">
        <span className="scoring-rescore-spinner" />
        저장 중
      </span>
    ) : isSaved ? '저장 완료' : '저장하기'
  );

  const handleEditResult = useCallback((resultIndex, field, value) => {
    setEvaluationResults(prev => prev.map((r, i) => {
      if (i !== resultIndex) return r;
      return { ...r, [field]: value };
    }));
    setIsSaved(false);
  }, []);

  const handleEditBatchResult = useCallback((resultIndex, field, value) => {
    setBatchResults(prev => prev.map((r, i) => {
      if (i !== resultIndex) return r;
      return { ...r, [field]: value };
    }));
    setIsSaved(false);
  }, []);

  const handleEditStudentName = useCallback((source, resultIndex, value) => {
    const updater = (prev) => prev.map((r, i) => {
      if (i !== resultIndex) return r;
      return { ...r, studentName: value, student_name: value };
    });
    if (source === 'batch') {
      setBatchResults(updater);
    } else {
      setEvaluationResults(updater);
    }
    setIsSaved(false);
  }, []);

  const handleFinishStudentNameEdit = useCallback(async (source, resultIndex) => {
    const isBatchSource = source === 'batch';
    const results = isBatchSource ? batchResults : evaluationResults;
    const result = results[resultIndex];
    const nextName = String(result?.studentName || result?.student_name || '').trim();

    if (!nextName) {
      showToast('학생 이름을 입력해주세요', 'warning');
      return;
    }

    const normalizedResult = { ...result, studentName: nextName, student_name: nextName };
    if (isBatchSource) {
      setBatchResults(prev => prev.map((r, i) => (i === resultIndex ? normalizedResult : r)));
    } else {
      setEvaluationResults(prev => prev.map((r, i) => (i === resultIndex ? normalizedResult : r)));
    }
    setEditingField(null);

    if (!savedScoringRecordId || !currentUser || isAnonymous) {
      setIsSaved(false);
      return;
    }

    try {
      const response = await updateScoringResult(savedScoringRecordId, resultIndex, {
        studentName: nextName,
        student_name: nextName,
        revision: {
          mode: 'manual_edit',
          field: 'studentName',
          instruction: '채점 직후 화면에서 학생 이름 수정',
        },
      });
      if (response?.result) {
        if (isBatchSource) {
          setBatchResults(prev => prev.map((r, i) => (i === resultIndex ? response.result : r)));
        } else {
          setEvaluationResults(prev => prev.map((r, i) => (i === resultIndex ? response.result : r)));
        }
      }
      setIsSaved(true);
      showToast('학생 이름을 저장했어요', 'success');
    } catch (error) {
      console.error('학생 이름 저장 실패:', error);
      setIsSaved(false);
      showToast('화면에는 반영했어요. 기록 저장은 다시 시도해주세요', 'warning');
    }
  }, [batchResults, evaluationResults, savedScoringRecordId, currentUser, isAnonymous, showToast]);

  const handleFinishResultEdit = useCallback(async (source, resultIndex, field) => {
    const isBatchSource = source === 'batch';
    const results = isBatchSource ? batchResults : evaluationResults;
    const result = results[resultIndex];
    const value = result?.[field];
    setEditingField(null);

    if (!savedScoringRecordId || !currentUser || isAnonymous) {
      setIsSaved(false);
      return;
    }

    try {
      const response = await updateScoringResult(savedScoringRecordId, resultIndex, {
        [field]: value,
        revision: {
          mode: 'manual_edit',
          field,
          instruction: '채점 직후 화면에서 직접 수정',
        },
      });
      if (response?.result) {
        if (isBatchSource) {
          setBatchResults(prev => prev.map((r, i) => (i === resultIndex ? response.result : r)));
        } else {
          setEvaluationResults(prev => prev.map((r, i) => (i === resultIndex ? response.result : r)));
        }
      }
      setIsSaved(true);
      showToast('기록에 저장했어요', 'success');
    } catch (error) {
      console.error('채점 결과 수정 저장 실패:', error);
      setIsSaved(false);
      showToast('화면에는 반영했어요. 기록 저장은 다시 시도해주세요', 'warning');
    }
  }, [batchResults, evaluationResults, savedScoringRecordId, currentUser, isAnonymous, showToast]);

  const handleWorkspaceSaveResult = useCallback(async (resultIndex, updates) => {
    const isBatchSource = reviewModal?.source === 'batch';
    const setResults = isBatchSource ? setBatchResults : setEvaluationResults;
    const replaceResult = (nextResult) => setResults(prev => prev.map((r, i) => (
      i === resultIndex ? mergeScoringResultMeta(r, nextResult, i) : r
    )));

    if (!savedScoringRecordId || !currentUser || isAnonymous) {
      const sourceResults = isBatchSource ? batchResults : evaluationResults;
      const localResult = applyResultEditLocally(sourceResults[resultIndex], updates);
      replaceResult(localResult);
      setIsSaved(false);
      return localResult;
    }

    const response = await updateScoringResult(savedScoringRecordId, resultIndex, updates);
    if (response?.result) {
      replaceResult(response.result);
    }
    setIsSaved(true);
    showToast('기록에 저장했어요', 'success');
    return response?.result;
  }, [reviewModal?.source, savedScoringRecordId, currentUser, isAnonymous, batchResults, evaluationResults, showToast]);

  const handleStartItemEdit = useCallback((source, resultIndex, itemIndex, item) => {
    setEditingItemField({ source, resultIndex, itemIndex });
    setEditingItemDraft({
      score: item?.score ?? '',
      reason: item?.reason || '',
      matched_level: item?.matched_level || '',
    });
  }, []);

  const handleCancelItemEdit = useCallback(() => {
    setEditingItemField(null);
    setEditingItemDraft({ score: '', reason: '', matched_level: '' });
  }, []);

  const handleSaveItemEdit = useCallback(async () => {
    if (!editingItemField) return;
    const { source, resultIndex, itemIndex } = editingItemField;
    const isBatchSource = source === 'batch';
    const results = isBatchSource ? batchResults : evaluationResults;
    const result = results[resultIndex];
    if (!result?.item_scores?.[itemIndex]) return;

    const nextScore = Number(editingItemDraft.score);
    const maxScore = Number(result.item_scores[itemIndex].max_score || 0);
    if (!Number.isFinite(nextScore)) {
      showToast('점수는 숫자로 입력해주세요', 'warning');
      return;
    }
    if (nextScore < 0 || (maxScore > 0 && nextScore > maxScore)) {
      showToast(`0점부터 ${maxScore}점 사이로 입력해주세요`, 'warning');
      return;
    }

    const itemScores = [...result.item_scores];
    itemScores[itemIndex] = {
      ...itemScores[itemIndex],
      score: nextScore,
      reason: editingItemDraft.reason,
      matched_level: editingItemDraft.matched_level,
    };
    const localTotal = itemScores.reduce((sum, item) => sum + (Number(item.score) || 0), 0);
    const localResult = { ...result, item_scores: itemScores, total_score: localTotal, score: localTotal };

    if (isBatchSource) {
      setBatchResults(prev => prev.map((r, i) => (i === resultIndex ? localResult : r)));
    } else {
      setEvaluationResults(prev => prev.map((r, i) => (i === resultIndex ? localResult : r)));
    }
    setIsSaved(false);

    if (savedScoringRecordId && currentUser && !isAnonymous) {
      try {
        const response = await updateScoringResult(savedScoringRecordId, resultIndex, {
          item_scores: itemScores,
          revision: {
            mode: 'manual_edit',
            field: 'item_scores',
            item_index: itemIndex,
            instruction: '채점 직후 화면에서 항목별 평가 직접 수정',
          },
        });
        if (response?.result) {
          if (isBatchSource) {
            setBatchResults(prev => prev.map((r, i) => (i === resultIndex ? response.result : r)));
          } else {
            setEvaluationResults(prev => prev.map((r, i) => (i === resultIndex ? response.result : r)));
          }
        }
        setIsSaved(true);
        showToast('항목별 평가를 기록에 저장했어요', 'success');
      } catch (error) {
        console.error('항목별 평가 저장 실패:', error);
        showToast('화면에는 반영했어요. 기록 저장은 다시 시도해주세요', 'warning');
      }
    }
    handleCancelItemEdit();
  }, [batchResults, evaluationResults, editingItemDraft, editingItemField, savedScoringRecordId, currentUser, isAnonymous, showToast, handleCancelItemEdit]);

  const getResultByRefineTarget = useCallback((target) => {
    if (!target) return null;
    const sourceResults = target.source === 'batch' ? batchResults : evaluationResults;
    return sourceResults[target.resultIndex] || null;
  }, [batchResults, evaluationResults]);

  const isAiRefineTargetActive = useCallback((target) => (
    aiRefineTarget?.source === target.source
    && aiRefineTarget?.resultIndex === target.resultIndex
    && aiRefineTarget?.targetType === target.targetType
    && aiRefineTarget?.field === target.field
    && aiRefineTarget?.itemIndex === target.itemIndex
  ), [aiRefineTarget]);

  const isAiRefineTargetBlocked = useCallback((target) => (
    isResultInteractionLocked || ((aiRefineLoading || aiRefineApplying) && !isAiRefineTargetActive(target))
  ), [aiRefineApplying, aiRefineLoading, isAiRefineTargetActive, isResultInteractionLocked]);

  const handleOpenAiRefine = useCallback((target) => {
    if (isResultInteractionLocked) {
      showToast('재채점이 끝난 뒤 수정할 수 있어요.', 'info');
      return;
    }
    if (aiRefineLoading || aiRefineApplying) {
      if (!isAiRefineTargetActive(target)) {
        showToast('다른 AI 수정 요청이 진행 중이에요. 끝난 뒤 다시 눌러주세요.', 'info');
      }
      return;
    }
    setAiRefineTarget(target);
    setAiRefineInstruction('');
    setAiRefinePreview(null);
  }, [aiRefineApplying, aiRefineLoading, isAiRefineTargetActive, isResultInteractionLocked, showToast]);

  const handleCloseAiRefine = useCallback(() => {
    setAiRefineTarget(null);
    setAiRefineInstruction('');
    setAiRefinePreview(null);
  }, []);

  const handleRequestAiRefine = useCallback(async () => {
    if (!aiRefineTarget) return;
    const instruction = aiRefineInstruction.trim();
    if (!instruction) {
      showToast('AI에게 요청할 내용을 입력해주세요', 'warning');
      return;
    }
    const result = getResultByRefineTarget(aiRefineTarget);
    if (!result) return;
    const maskedInstruction = instruction;
    const resultsSource = aiRefineTarget.source === 'batch' ? batchResults : evaluationResults;
    const resolvedAnswer = resolveAnswerTextForResult({
      cachedAnswerTexts,
      results: resultsSource,
      result,
      resultIndex: aiRefineTarget.resultIndex,
      batchStudents,
      singleAnswerText: answerText,
    });

    setAiRefineLoading(true);
    setAiRefinePreview(null);
    try {
      const response = await refineScoringResult({
        ...(savedScoringRecordId ? { record_id: savedScoringRecordId, result_index: aiRefineTarget.resultIndex } : {}),
        current_result: result,
        target_type: aiRefineTarget.targetType,
        field: aiRefineTarget.field,
        item_index: aiRefineTarget.itemIndex,
        instruction: maskedInstruction,
        rubric_text: rubricText,
        rubric_criteria: getActiveRubricCriteria(),
        answer_text: resolvedAnswer.answer_text || '',
        answer_text_source: resolvedAnswer.answer_text_source || '',
        scoring_options: scoringOptions,
        active_scoring_policy: buildActiveScoringPolicyPayload(),
        previous_requests: [
          ...(Array.isArray(rescoreHistory) ? rescoreHistory : []),
          ...(result.revision_history || []),
        ],
      });
      setAiRefinePreview(response);
      showToast('AI 수정안을 만들었어요', 'success');
    } catch (error) {
      showToast(error.message || 'AI 수정안을 만들지 못했어요', 'error');
    } finally {
      setAiRefineLoading(false);
    }
  }, [aiRefineInstruction, aiRefineTarget, answerText, batchResults, batchStudents, buildActiveScoringPolicyPayload, cachedAnswerTexts, evaluationResults, getActiveRubricCriteria, getResultByRefineTarget, rescoreHistory, rubricText, savedScoringRecordId, scoringOptions, showToast]);

  const handleApplyAiRefinePreview = useCallback(async () => {
    if (!aiRefineTarget || !aiRefinePreview?.preview_result) return;
    const isBatchSource = aiRefineTarget.source === 'batch';
    setAiRefineApplying(true);
    try {
      let nextResult = aiRefinePreview.preview_result;
      if (savedScoringRecordId && currentUser && !isAnonymous) {
        const response = await updateScoringResult(savedScoringRecordId, aiRefineTarget.resultIndex, {
          ...aiRefinePreview.updates,
          revision: aiRefinePreview.revision,
        });
        if (response?.result) {
          nextResult = response.result;
          setIsSaved(true);
        }
      } else {
        setIsSaved(false);
      }

      if (isBatchSource) {
        setBatchResults(prev => prev.map((r, i) => (
          i === aiRefineTarget.resultIndex ? mergeScoringResultMeta(r, nextResult, i) : r
        )));
      } else {
        setEvaluationResults(prev => prev.map((r, i) => (
          i === aiRefineTarget.resultIndex ? mergeScoringResultMeta(r, nextResult, i) : r
        )));
      }
      showToast(savedScoringRecordId ? 'AI 수정안을 기록에 적용했어요' : 'AI 수정안을 화면에 적용했어요', 'success');
      handleCloseAiRefine();
    } catch (error) {
      showToast('AI 수정안 적용에 실패했어요', 'error');
    } finally {
      setAiRefineApplying(false);
    }
  }, [aiRefinePreview, aiRefineTarget, currentUser, handleCloseAiRefine, isAnonymous, savedScoringRecordId, showToast]);

  const getAiRefinePreviewText = useCallback(() => {
    if (!aiRefinePreview?.preview_result || !aiRefineTarget) return '';
    const result = aiRefinePreview.preview_result;
    if (aiRefineTarget.targetType === 'item') {
      const item = result.item_scores?.[aiRefineTarget.itemIndex];
      if (!item) return '';
      return `${item.score}/${item.max_score}점\n${item.reason || ''}`;
    }
    const value = result[aiRefineTarget.field];
    return Array.isArray(value) ? value.join('\n') : (value || '');
  }, [aiRefinePreview, aiRefineTarget]);

  const renderAiRefinePanel = useCallback((target) => {
    if (!isAiRefineTargetActive(target)) return null;
    const previewText = getAiRefinePreviewText();
    return (
      <div className={`scoring-ai-refine-panel ${aiRefineLoading || aiRefineApplying ? 'is-busy' : ''}`} aria-busy={aiRefineLoading || aiRefineApplying}>
        <p className="scoring-ai-refine-title">AI에게 어떻게 고칠지 알려주세요</p>
        <p className="scoring-ai-refine-subtitle">현재 항목과 채점 기준, 학생 답안, 이전 수정 이력을 함께 보고 수정안을 만들어요.</p>
        <textarea
          className="scoring-editable-textarea scoring-ai-refine-textarea"
          value={aiRefineInstruction}
          onChange={(e) => setAiRefineInstruction(e.target.value)}
          rows={3}
          disabled={aiRefineLoading || aiRefineApplying}
          placeholder="예: 이 항목은 근거를 더 엄격하게 보고 점수를 낮춰줘"
        />
        <div className="scoring-edit-actions">
          <button type="button" className="scoring-item-edit-cancel" onClick={handleCloseAiRefine} disabled={aiRefineLoading || aiRefineApplying}>닫기</button>
          <button type="button" className="scoring-item-edit-save" onClick={handleRequestAiRefine} disabled={aiRefineLoading || aiRefineApplying || !aiRefineInstruction.trim()}>
            {aiRefineLoading ? '요청 중...' : 'AI에게 요청'}
          </button>
        </div>
        {aiRefineLoading && (
          <div className="scoring-ai-refine-status">수정안을 만들고 있어요. 잠시만 기다려주세요.</div>
        )}
        {aiRefinePreview && (
          <div className="scoring-ai-refine-preview">
            <div className="scoring-ai-refine-preview-head">
              <strong>수정안</strong>
              {aiRefinePreview.revision?.summary && <span>{aiRefinePreview.revision.summary}</span>}
            </div>
            <div className="scoring-ai-refine-preview-body">{previewText}</div>
            <div className="scoring-edit-actions">
              <button type="button" className="scoring-item-edit-cancel" onClick={() => setAiRefinePreview(null)} disabled={aiRefineLoading || aiRefineApplying}>다시 요청</button>
              <button type="button" className="scoring-item-edit-save" onClick={handleApplyAiRefinePreview} disabled={aiRefineApplying}>
                {aiRefineApplying ? '적용 중...' : '수정안 적용'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }, [aiRefineApplying, aiRefineInstruction, aiRefineLoading, aiRefinePreview, getAiRefinePreviewText, handleApplyAiRefinePreview, handleCloseAiRefine, handleRequestAiRefine, isAiRefineTargetActive]);

  const handleCopyResult = useCallback(() => {
    if (evaluationResults.length === 0) return;
    const lines = evaluationResults.map((r) => {
      const parts = [`총점: ${getResultTotalScore(r)}점`];
      if (r.question_results?.length > 0) {
        parts.push('문항별: ' + r.question_results.map((q) => `${q.question_title || q.question_id} ${q.score}/${q.max_score}`).join(', '));
      }
      if (r.item_scores?.length > 0) {
        parts.push('항목별: ' + r.item_scores.map((i) => `${i.item_name} ${i.score}/${i.max_score}`).join(', '));
      }
      if (r.strengths?.length > 0) parts.push('잘한 점: ' + r.strengths.join(', '));
      if (r.improvements?.length > 0) parts.push('보완할 점: ' + r.improvements.join(', '));
      if (r.detailed_feedback) parts.push('총평: ' + r.detailed_feedback);
      if (r.teacher_comment) parts.push('생기부: ' + r.teacher_comment);
      return parts.join('\n');
    });
    navigator.clipboard.writeText(lines.join('\n\n')).then(() => {
      showToast('클립보드에 복사했어요', 'success');
    }).catch(() => {
      showToast('복사에 실패했어요', 'error');
    });
  }, [evaluationResults, showToast]);

  // ── 제출방 관련 핸들러 ──

  const fetchActiveRooms = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(getApiUrl('/api/scoring/room/list'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const open = (data.rooms || []).filter(r => r.status === 'open');
        setActiveRooms(open);
      }
    } catch (err) {
      console.error('제출방 목록 조회 실패:', err);
    }
  }, [currentUser]);

  useEffect(() => {
    if (scoringMode === 'batch') fetchActiveRooms();
  }, [scoringMode, fetchActiveRooms]);

  const createSubmissionRoom = useCallback(async (customTitle) => {
    if (!currentUser || !rubricText) return;
    try {
      setRoomCreating(true);
      const token = await currentUser.getIdToken();
      const formData = new FormData();
      const roomRubric = getActiveRubricCriteria();
      formData.append('title', customTitle || roomTitle || roomRubric?.subject || selectedRubric?.subject || '과제 제출');
      formData.append('rubric_text', rubricText);
      appendScoringOptions(formData);
      if (roomRubric) {
        formData.append('rubric_criteria', JSON.stringify(roomRubric));
      }

      const res = await fetch(getApiUrl('/api/scoring/room/create'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || '제출방을 만들지 못했어요. 잠시 후 다시 시도해주세요');
      }

      const data = await res.json();
      applyScoringContextFromResponse(data);
      setSubmissionRoom(data);
      setRoomSubmissions([]);
      setRoomRosterStatus(null);
      setRoomTitle('');
      showToast('제출방이 생성되었어요', 'success');
    } catch (err) {
      showToast(err.message || '제출방 생성 실패', 'error');
    } finally {
      setRoomCreating(false);
    }
  }, [currentUser, rubricText, roomTitle, selectedRubric, showToast, getActiveRubricCriteria, appendScoringOptions, applyScoringContextFromResponse]);

  const fetchRoomSubmissions = useCallback(async (roomId) => {
    if (!currentUser || !roomId) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(getApiUrl(`/api/scoring/room/${roomId}/submissions`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRoomSubmissions(data.submissions || []);
        setRoomRosterStatus(data.roster_status || null);
      }
    } catch (err) {
      console.error('제출 현황 조회 실패:', err);
    }
  }, [currentUser]);

  const handleLinkRoomRoster = useCallback(async (rosterId) => {
    if (!currentUser || !submissionRoom?.room_id) return false;
    try {
      const token = await currentUser.getIdToken();
      const form = new FormData();
      form.append('roster_id', rosterId || '');
      const res = await fetch(getApiUrl(`/api/scoring/room/${submissionRoom.room_id}/roster`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error('link failed');
      if (!rosterId) setRoomRosterStatus(null);
      await fetchRoomSubmissions(submissionRoom.room_id);
      showToast(rosterId ? '명단을 연결했어요' : '명단 연결을 끊었어요', 'success');
      return true;
    } catch {
      showToast('명단을 연결하지 못했어요. 다시 시도해주세요', 'error');
      return false;
    }
  }, [currentUser, submissionRoom?.room_id, fetchRoomSubmissions, showToast]);

  useEffect(() => {
    if (!submissionRoom?.room_id) return;
    fetchRoomSubmissions(submissionRoom.room_id);
    roomPollRef.current = setInterval(() => {
      fetchRoomSubmissions(submissionRoom.room_id);
    }, 5000);
    return () => { if (roomPollRef.current) clearInterval(roomPollRef.current); };
  }, [submissionRoom?.room_id, fetchRoomSubmissions]);

  const handleLoadRoomForScoring = useCallback(async (room) => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(getApiUrl(`/api/scoring/room/${room.room_id}/submissions`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        applyScoringContextFromResponse(data.room || room);
        setSubmissionRoom({ room_id: room.room_id, code: room.code, title: room.title });
        setRoomSubmissions(data.submissions || []);
        setRoomRosterStatus(data.roster_status || null);
        setBatchUploadMode('student');
        if (data.room?.rubric_text && !rubricText) {
          setRubricText(data.room.rubric_text);
          if (
            data.room?.rubric_criteria &&
            typeof data.room.rubric_criteria === 'object' &&
            !Array.isArray(data.room.rubric_criteria) &&
            Object.keys(data.room.rubric_criteria).length > 0
          ) {
            setConfirmedRubric(data.room.rubric_criteria);
            setSelectedRubric(data.room.rubric_criteria);
            setParsedRubrics([data.room.rubric_criteria]);
            sessionStorage.setItem('scoring-confirmed-rubric', JSON.stringify({
              rubric_text: data.room.rubric_text,
              rubric: data.room.rubric_criteria,
            }));
          }
        }
      }
    } catch (err) {
      showToast('제출 현황을 불러올 수 없어요', 'error');
    }
  }, [currentUser, showToast, rubricText, applyScoringContextFromResponse]);

  const getSubmitUrl = useCallback(() => {
    if (!submissionRoom?.code) return '';
    const origin = window.location.origin;
    return `${origin}/submit/${submissionRoom.code}`;
  }, [submissionRoom?.code]);

  const handleCopyLink = useCallback(() => {
    const url = getSubmitUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      showToast('링크가 복사되었어요', 'success');
    }).catch(() => {
      showToast('복사에 실패했어요', 'error');
    });
  }, [getSubmitUrl, showToast]);

  const handlePreviewSubmission = useCallback(async (sub) => {
    if (!currentUser || !submissionRoom?.room_id) return;
    setPreviewSubmission(sub);
    setPreviewUrl('');
    setPreviewZoomed(false);
    setPreviewLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(
        getApiUrl(`/api/scoring/room/${submissionRoom.room_id}/submissions/${sub.submission_id}/preview`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setPreviewUrl(data.preview_image_url || data.preview_pdf_url || data.preview_url);
        if (data.preview_image_url || data.preview_pdf_url) {
          setPreviewSubmission((prev) => prev ? {
            ...prev,
            file_type: data.preview_image_url ? 'image/png' : 'application/pdf',
          } : prev);
        }
      } else {
        showToast('파일을 불러올 수 없어요', 'error');
        setPreviewSubmission(null);
      }
    } catch {
      showToast('파일을 불러올 수 없어요', 'error');
      setPreviewSubmission(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [currentUser, submissionRoom?.room_id, showToast]);

  // 텍스트만 입력해서 채점한 케이스(파일 없음) — 모달에 추출 텍스트 표시.
  // storage_path가 없어도 answer_text가 있으면 호출됨.
  const handlePreviewText = useCallback((result) => {
    const text = (result?.answer_text || '').trim();
    if (!text) {
      showToast('볼 수 있는 답안 텍스트가 없어요', 'info');
      return;
    }
    setPreviewSubmission({
      student_name: result.studentName || result.student_name || '학생',
      filename: '학생이 입력한 답안',
      file_type: 'text/plain',
      text_content: text,
    });
    setPreviewUrl('');
    setPreviewZoomed(false);
    setPreviewLoading(false);
  }, [showToast]);

  // 단일/일괄 채점 결과(history 저장 전)에서 storage_path로 미리보기.
  // 제출방 모드가 아닌 일반 채점 결과 카드의 "제출물 보기" 버튼이 호출.
  const handlePreviewByPath = useCallback(async (result) => {
    if (!currentUser || !result?.storage_path) {
      showToast('원본 파일이 저장되지 않았어요', 'info');
      return;
    }
    // 모달 헤더용 임시 객체 — 제출방 sub 객체와 같은 키 사용해 모달 코드 재사용.
    setPreviewSubmission({
      student_name: result.studentName || result.student_name || '학생',
      filename: result.fileName || (result.storage_path.split('/').pop() || ''),
      file_type: result.file_type || '',
      pages: result.pages || '',
    });
    setPreviewUrl('');
    setPreviewZoomed(false);
    setPreviewLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(getApiUrl('/api/scoring/preview-by-path'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ storage_path: result.storage_path }),
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewUrl(data.preview_image_url || data.preview_pdf_url || data.preview_url);
        // 백엔드가 blob.content_type을 돌려주면 모달 분기에 사용
        if (data.preview_image_url || data.preview_pdf_url || data.file_type) {
          setPreviewSubmission((prev) => prev ? {
            ...prev,
            file_type: data.preview_image_url ? 'image/png' : (data.preview_pdf_url ? 'application/pdf' : data.file_type),
          } : prev);
        }
      } else {
        showToast('파일을 불러올 수 없어요', 'error');
        setPreviewSubmission(null);
      }
    } catch {
      showToast('파일을 불러올 수 없어요', 'error');
      setPreviewSubmission(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [currentUser, showToast]);

  const getWorkspaceMatchedSubmission = useCallback((result) => {
    if (batchUploadMode !== 'student') return null;
    const studentName = getScoringStudentName(result);
    return roomSubmissions.find(
      sub => normalizeStudentNameForMatch(sub.student_name) === normalizeStudentNameForMatch(studentName)
    ) || null;
  }, [batchUploadMode, roomSubmissions]);

  const getWorkspaceSubmissionLabel = useCallback((result) => {
    const matchedSub = getWorkspaceMatchedSubmission(result);
    if (matchedSub?.filename) return matchedSub.filename;
    if (result?.fileName || result?.filename) return result.fileName || result.filename;
    if (result?.pages) return `스캔 PDF p.${result.pages}`;
    if (result?.answer_text) return '직접 입력한 답안';
    return '제출물';
  }, [getWorkspaceMatchedSubmission]);

  const buildWorkspaceResults = useCallback((resultsSource) => (
    (Array.isArray(resultsSource) ? resultsSource : []).map((result, index) => {
      const resolvedAnswer = resolveAnswerTextForResult({
        cachedAnswerTexts,
        results: resultsSource,
        result,
        resultIndex: index,
        batchStudents,
        singleAnswerText: answerText,
      });
      return {
        ...result,
        answer_text: resolvedAnswer.answer_text || '',
      };
    })
  ), [answerText, batchStudents, cachedAnswerTexts]);

  const loadWorkspaceSubmissionPreview = useCallback(async (result) => {
    const matchedSub = getWorkspaceMatchedSubmission(result);
    if (matchedSub) {
      if (!currentUser || !submissionRoom?.room_id) return null;
      const token = await currentUser.getIdToken();
      const res = await fetch(
        getApiUrl(`/api/scoring/room/${submissionRoom.room_id}/submissions/${matchedSub.submission_id}/preview`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('preview failed');
      const data = await res.json();
      return {
        previewUrl: data.preview_image_url || data.preview_pdf_url || data.preview_url,
        previewHtml: data.preview_html || '',
        fileType: data.preview_image_url ? 'image/png' : (data.preview_pdf_url ? 'application/pdf' : (data.file_type || matchedSub.file_type || '')),
        filename: matchedSub.filename || '제출물',
        pages: matchedSub.pages || '',
      };
    }

    if (result?.storage_path) {
      if (!currentUser) return null;
      const token = await currentUser.getIdToken();
      const res = await fetch(getApiUrl('/api/scoring/preview-by-path'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ storage_path: result.storage_path }),
      });
      if (!res.ok) throw new Error('preview failed');
      const data = await res.json();
      return {
        previewUrl: data.preview_image_url || data.preview_pdf_url || data.preview_url,
        previewHtml: data.preview_html || '',
        fileType: data.preview_image_url ? 'image/png' : (data.preview_pdf_url ? 'application/pdf' : (data.file_type || result.file_type || '')),
        filename: result.fileName || result.filename || result.storage_path.split('/').pop() || '제출물',
        pages: result.pages || '',
      };
    }

    return null;
  }, [currentUser, getWorkspaceMatchedSubmission, submissionRoom?.room_id]);

  const handleDeleteRoom = useCallback(async (roomId, e) => {
    if (e) e.stopPropagation();
    if (!currentUser) return;
    const ok = await confirmDialog({
      title: '이 제출방을 삭제할까요?',
      description: '학생들이 제출한 파일도 모두 함께 삭제돼요',
      confirmLabel: '삭제하기',
      danger: true,
    });
    if (!ok) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(getApiUrl(`/api/scoring/room/${roomId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const remaining = activeRooms.filter(r => r.room_id !== roomId);
        setActiveRooms(remaining);
        if (submissionRoom?.room_id === roomId) {
          setSubmissionRoom(null);
          setRoomSubmissions([]);
          setRoomRosterStatus(null);
          if (remaining.length === 0) resetDirectBatchUpload();
          setBatchUploadMode(remaining.length > 0 ? 'student-rooms' : null);
          if (roomPollRef.current) clearInterval(roomPollRef.current);
        } else if (remaining.length === 0 && batchUploadMode === 'student-rooms') {
          resetDirectBatchUpload();
          setBatchUploadMode(null);
        }
        showToast('제출방을 삭제했어요', 'success');
      } else {
        showToast('제출방을 삭제하지 못했어요. 다시 시도해주세요', 'error');
      }
    } catch {
      showToast('제출방을 삭제하지 못했어요. 다시 시도해주세요', 'error');
    }
  }, [currentUser, submissionRoom?.room_id, activeRooms, batchUploadMode, showToast, resetDirectBatchUpload, confirmDialog]);

  // 일괄 채점 실패 메시지를 사용자가 다음 행동을 할 수 있는 형태로 변환.
  // 백엔드 detail 문자열, fetchWithRetry의 AbortError 메시지, generic 메시지를 분류한다.
  // handleRoomScoring/handleBatchSubmit 양쪽에서 참조되므로 이 위치(useCallback 정의 앞)에 둔다.
  const buildBatchErrorInfo = useCallback((error, studentCount = 0) => {
    const message = (error && error.message) ? String(error.message) : '';
    const lower = message.toLowerCase();

    // 네트워크 단계 실패 (Cloud Run 32MiB 본문 한도 초과나 인터넷 단절 시 브라우저가
    // 'Failed to fetch' / 'Load failed'를 던진다). 명시적으로 매핑하지 않으면
    // unknown 으로 떨어져 사용자가 원인을 알 수 없다.
    const isNetworkLevel = (
      lower.includes('failed to fetch') ||
      lower.includes('load failed') ||
      lower.includes('networkerror') ||
      lower.includes('network request failed')
    );
    if (isNetworkLevel) {
      const splitHint = studentCount > 10
        ? `${studentCount}명을 한 번에 업로드하면 파일 크기 한도(약 25MB)를 넘길 수 있어요. 10명씩 나눠서 시도해보세요.`
        : '네트워크 연결이 잠시 끊겼어요. 인터넷을 확인하고 다시 시도해주세요.';
      return {
        type: 'network',
        title: '서버에 요청을 보내지 못했어요',
        message: splitHint,
        toastMessage: '네트워크/업로드 한도 문제 — 학생을 10명씩 나눠서 시도해보세요',
        actionLabel: '다시 시도',
        canRetry: true,
      };
    }

    if (message.includes('서버 응답 시간이 초과') || lower.includes('aborted') || lower.includes('timeout')) {
      const splitHint = studentCount > 10
        ? `${studentCount}명을 한 번에 채점하다 시간이 초과됐어요. 이미지 답안이 많다면 5~10명씩 나눠서 다시 시도해주세요.`
        : '이미지 인식이 평소보다 오래 걸리고 있어요. 같은 파일이 반복 실패하면 해당 이미지만 따로 다시 시도해주세요.';
      return {
        type: 'timeout',
        title: '채점 시간이 초과됐어요',
        message: splitHint,
        toastMessage: '채점 시간이 초과됐어요. 이미지 답안은 더 작게 나눠서 시도해보세요.',
        actionLabel: '다시 채점하기',
        canRetry: true,
      };
    }
    if (message.includes('파싱') || message.includes('AI 응답')) {
      return {
        type: 'parse_error',
        title: 'AI 응답 처리 중 문제가 생겼어요',
        message: 'AI가 응답을 만드는 도중 일시적인 문제가 있었어요. 같은 파일 그대로 한 번 더 시도하면 보통 잘 돼요.',
        toastMessage: 'AI 응답 처리 실패 - 다시 시도해주세요',
        actionLabel: '다시 채점하기',
        canRetry: true,
      };
    }
    if (message.includes('429') || message.includes('많아') || message.includes('지연')) {
      return {
        type: 'rate_limit',
        title: '지금 사용자가 너무 많아요',
        message: '잠시(약 30초~1분) 기다린 뒤 다시 시도하면 정상적으로 채점돼요.',
        toastMessage: '잠시 후 다시 시도해주세요',
        actionLabel: '다시 시도',
        canRetry: true,
      };
    }
    return {
      type: 'unknown',
      title: '채점 중 문제가 발생했어요',
      message: message || '잠시 후 다시 시도해주세요.',
      toastMessage: message || '일괄 채점 중 오류가 발생했어요',
      actionLabel: '다시 시도',
      canRetry: true,
    };
  }, []);

  const handleRoomScoring = useCallback(async () => {
    if (!submissionRoom?.room_id || !currentUser || roomSubmissions.length === 0) return;
    if (!ensureRubricReady()) return;

    if (batchRequestRef.current) {
      showToast('이미 채점이 진행 중이에요. 잠시만 기다려주세요.', 'warning');
      return;
    }
    batchRequestRef.current = true;

    try {
      setRoomScoringLoading(true);
      setBatchLoading(true);
      setBatchProgress(0);
      setBatchRetryCount(0);
      setBatchError(null);

      const token = await currentUser.getIdToken();
      const filesRes = await fetch(getApiUrl(`/api/scoring/room/${submissionRoom.room_id}/files`), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!filesRes.ok) {
        const err = await filesRes.json().catch(() => null);
        throw new Error(err?.detail || '파일을 불러올 수 없어요');
      }

      const filesData = await filesRes.json();
      applyScoringContextFromResponse(filesData);
      const files = filesData.files || [];

      if (files.length === 0) {
        throw new Error('다운로드할 제출물이 없어요');
      }

      setBatchProgress(10);

      const downloadedFiles = [];
      const studentNames = [];
      const studentSubmissionIds = [];

      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        try {
          const downloadUrl = f.download_url.startsWith('/api/')
            ? getApiUrl(f.download_url)
            : f.download_url;
          const res = await fetch(downloadUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const file = new File([blob], f.filename || `${f.student_name}.jpg`, {
            type: f.file_type || 'image/jpeg',
          });
          downloadedFiles.push(file);
          studentNames.push(f.student_name);
          studentSubmissionIds.push(f.submission_id || createClientStudentId('room'));
        } catch (err) {
          console.error(`파일 다운로드 실패: ${f.student_name}`, err);
        }
        setBatchProgress(10 + Math.round((i / files.length) * 20));
      }

      if (downloadedFiles.length === 0) {
        throw new Error('파일 다운로드에 실패했어요');
      }

      setBatchProgress(35);
      const roomStudents = downloadedFiles.map((file, idx) => ({
        file,
        name: studentNames[idx] || `학생 ${idx + 1}`,
        filename: file.name,
        submission_id: studentSubmissionIds[idx] || null,
        client_student_id: studentSubmissionIds[idx] || createClientStudentId('room'),
      }));
      const chunkSubmitter = submitBatchInChunksRef.current;
      if (!chunkSubmitter) {
        throw new Error('일괄 채점 준비가 아직 끝나지 않았어요. 잠시 후 다시 시도해주세요.');
      }
      const roomScoringOptions = filesData.scoring_options
        ? normalizeScoringOptionsFromPayload(filesData.scoring_options)
        : scoringOptions;
      const { results: rawResults, extractedTexts, errors, totalChunks } = await chunkSubmitter(roomStudents, {
        scoringOptions: roomScoringOptions,
      });
      const results = (rawResults || []).map((r, idx) => ({
        ...r,
        studentName: r.studentName || r.student_name || studentNames[idx] || `학생 ${idx + 1}`,
      }));

      setBatchResults(results);
      setBatchProgress(100);
      setReScoreInstructions('');
      setReScoreCount(0);
      setRescoreHistory([]);
      setActiveScoringPolicy(buildActiveScoringPolicyPayload(roomScoringOptions));
      setPreviousResults(null);
      if (extractedTexts?.length > 0) {
        setCachedAnswerTexts(extractedTexts);
      }
      if (errors?.length > 0) {
        setBatchFailedChunks(errors);
        showToast(`${results.length}명 채점 완료, ${errors.length}개 청크는 다시 시도할 수 있어요`, 'warning');
      }
      showToast(`${results.length}명 채점을 마쳤어요`, 'success');
      trackRiseFeatureAction('scoring', 'generate', {
        mode: getActiveQuestionSet() ? 'multi-question-room' : 'batch-room',
        student_count: results.length,
        chunk_count: totalChunks,
      });

      try {
        const recordRubric = getActiveRubricCriteria();
        const recordQuestionSet = getActiveQuestionSet();
        const subjectName = recordQuestionSet?.questions?.[0]?.subject || recordRubric?.subject || parsedRubrics?.[0]?.subject || '';
        const saveResponse = await saveScoringRecord({
          rubric_text: rubricText,
          results,
          cached_answer_texts: mergeCachedAnswerTextsWithResults(extractedTexts || [], [], results),
          subject: subjectName,
          parsed_rubric: recordRubric || null,
          question_set: recordQuestionSet || null,
          type: recordQuestionSet ? 'multi-question-room' : 'room',
          student_count: results.length,
          room_id: submissionRoom?.room_id,
          room_title: submissionRoom?.title || '',
          ...getScoringRecordMeta(roomScoringOptions, filesData),
        });
        if (saveResponse?.record_id) {
          setSavedScoringRecordId(saveResponse.record_id);
        }
        setIsSaved(true);
      } catch (saveErr) {
        console.error('채점 기록 저장 실패:', saveErr);
      }

    } catch (error) {
      console.error('제출물 채점 실패:', error);
      const errorInfo = buildBatchErrorInfo(error, roomSubmissions.length);
      setBatchError({ ...errorInfo, retryFn: () => handleRoomScoring() });
      showToast(errorInfo.toastMessage, 'error');
    } finally {
      setBatchLoading(false);
      setRoomScoringLoading(false);
      setBatchRetryCount(0);
      batchRequestRef.current = false;
    }
  }, [submissionRoom?.room_id, submissionRoom?.title, currentUser, roomSubmissions.length, rubricText, parsedRubrics, showToast, buildActiveScoringPolicyPayload, buildBatchErrorInfo, getActiveRubricCriteria, getActiveQuestionSet, ensureRubricReady, applyScoringContextFromResponse, getScoringRecordMeta, scoringOptions]);

  // ── 일괄 채점 관련 핸들러 ──

  const pendingBatchFilesRef = useRef(null);

  const actualBatchFilesUpload = useCallback((files, filesStats = []) => {
    const newStudents = files.map(f => {
      const name = extractStudentNameFromFilename(f.name);
      return {
        file: f,
        name,
        name_source: name ? NAME_SOURCE.FILENAME : NAME_SOURCE.PLACEHOLDER,
        filename: f.name,
        client_student_id: createClientStudentId('file'),
      };
    });
    setBatchFiles(prev => [...prev, ...files]);
    setBatchStudents(prev => [...prev, ...newStudents]);
    setBatchFilesStats(prev => [...prev, ...filesStats]);
  }, []);

  actualBatchFilesUploadRef.current = actualBatchFilesUpload;

  const openBatchMethodSelection = useCallback(() => {
    const filesToCarry = answerFiles.slice(0, BATCH_STUDENT_FILE_MAX);
    const statsToCarry = answerFilesStats.slice(0, BATCH_STUDENT_FILE_MAX);

    resetDirectBatchUpload();
    setBatchCarryoverFiles(filesToCarry);
    setBatchCarryoverFilesStats(statsToCarry);
    setScoringMode('batch');
    setBatchUploadMode('teacher');
    setBatchAnswerStructure(null);
  }, [answerFiles, answerFilesStats, resetDirectBatchUpload]);

  const switchPendingAnswerFilesToBatch = useCallback(() => {
    const filesToMove = [...answerFiles, ...(pendingAnswerFiles || [])];
    if (filesToMove.length === 0) return;
    if (filesToMove.length > BATCH_STUDENT_FILE_MAX) {
      setShowPreCheckModal(false);
      setPreCheckResult(null);
      setPendingAnswerFiles(null);
      setScoringMode('batch');
      setBatchUploadMode('teacher');
      setBatchAnswerStructure('studentFiles');
      showToast(`여러 명 채점은 한 번에 최대 ${BATCH_STUDENT_FILE_MAX}명까지 가능해요. 파일을 나눠서 올려주세요`, 'warning', 8000);
      return;
    }

    setShowPreCheckModal(false);
    setPreCheckResult(null);
    setPendingAnswerFiles(null);
    resetDirectBatchUpload();
    setScoringMode('batch');
    setBatchUploadMode('teacher');
    setBatchAnswerStructure('studentFiles');
    actualBatchFilesUpload(filesToMove, []);
    setAnswerFiles([]);
    setAnswerFilesStats([]);
    showToast(`${filesToMove.length}개 파일을 여러 명 채점으로 옮겼어요`, 'success');
  }, [actualBatchFilesUpload, answerFiles, pendingAnswerFiles, resetDirectBatchUpload, showToast]);

  const performBatchFilesPreCheck = useCallback(async (files) => {
    setPreCheckLoading(true);
    setShowPreCheckModal(true);

    const totalFileCount = batchFiles.length + files.length;
    if (totalFileCount > BATCH_STUDENT_FILE_MAX) {
      const available = Math.max(0, BATCH_STUDENT_FILE_MAX - batchFiles.length);
      setPreCheckResult({
        can_proceed: false,
        needs_confirmation: false,
        overall_status: 'error',
        total_files: files.length,
        total_size: files.reduce((sum, f) => sum + f.size, 0),
        total_size_display: `${(files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(1)}MB`,
        files_info: files.map(f => ({
          filename: f.name, status: 'ok', file_size: f.size,
          file_size_display: `${(f.size / 1024).toFixed(1)}KB`, issues: []
        })),
        recommendations: available > 0
          ? [`지금은 ${available}개까지만 더 올릴 수 있어요`]
          : ['기존 파일을 지운 뒤 다시 올려주세요'],
        file_count_error: `일괄 채점은 파일 ${BATCH_STUDENT_FILE_MAX}개까지 올릴 수 있어요 (현재 ${batchFiles.length}개, ${files.length}개 추가 시도)`
      });
      pendingBatchFilesRef.current = null;
      setPreCheckLoading(false);
      return;
    }

    try {
      // chatApi.preCheckFiles 는 25MB/10개 단위 자동 청크 분할 → 32MB 한도 우회.
      // onChunkProgress 콜백으로 모달에 "1/3 그룹" 진행률 표시.
      const result = await preCheckFiles(files, 'batch_scoring', isPro, {
        onChunkProgress: (current, total) => {
          setPrecheckChunkProgress({ current, total });
        },
      });

      const newContentLength = result.files_info?.reduce((sum, f) => sum + (f.content_length || 0), 0) || 0;
      const existingContentLength = batchFilesStats.reduce((sum, f) => sum + (f.contentLength || 0), 0);
      const totalContentLength = existingContentLength + newContentLength;

      const exceededLimits = [];
      if (totalContentLength > BATCH_TOTAL_CONTENT_MAX) {
        const perStudentLimit = Math.floor(BATCH_TOTAL_CONTENT_MAX / totalFileCount);
        exceededLimits.push(
          `총 분석량: ${totalContentLength.toLocaleString()}자 (최대 ${BATCH_TOTAL_CONTENT_MAX.toLocaleString()}자)` +
          ` → 학생 ${totalFileCount}명 기준 1인당 약 ${perStudentLimit.toLocaleString()}자까지 처리 가능`
        );
      }

      if (exceededLimits.length > 0) {
        result.can_proceed = true;
        result.needs_confirmation = true;
        result.overall_status = 'warning';
        result.exceeded_limits = exceededLimits;
        result.recommendations = [
          '분량이 많은 파일은 AI가 일부만 분석할 수 있어요',
          ...exceededLimits
        ];
      }

      const filesStats = normalizePreCheckStats(result.files_info || []);
      result._newFilesStats = filesStats;

      const hasNoIssues = result.can_proceed &&
                          !result.needs_confirmation &&
                          result.overall_status === 'ok';

      if (hasNoIssues) {
        setShowPreCheckModal(false);
        setPreCheckLoading(false);
        setPreCheckResult(null);
        actualBatchFilesUpload(files, filesStats);
      } else {
        setPreCheckResult(result);
        pendingBatchFilesRef.current = files;
        setPreCheckLoading(false);
      }
    } catch (error) {
      console.error('배치 파일 사전 검사 오류:', error);
      setShowPreCheckModal(false);
      setPreCheckLoading(false);
      setPreCheckResult(null);
      actualBatchFilesUpload(files, []);
    } finally {
      setPrecheckChunkProgress(null);
    }
  }, [batchFiles.length, batchFilesStats, actualBatchFilesUpload, isPro]);

  const detectScanPdf = useCallback(async (file) => {
    try {
      const pdfjsPageCount = await getPdfPageCount(file);
      if (pdfjsPageCount > 0) {
        return pdfjsPageCount;
      }
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let pageCount = 0;
      const text = new TextDecoder('latin1').decode(bytes);
      const matches = text.match(/\/Type\s*\/Page[^s]/g);
      pageCount = matches ? matches.length : 0;
      if (pageCount < 2) {
        const countMatch = text.match(/\/Count\s+(\d+)/);
        if (countMatch) pageCount = parseInt(countMatch[1], 10);
      }
      return pageCount;
    } catch {
      return 0;
    }
  }, []);

  useEffect(() => {
    setScanPdfDescriptor(null);
    setScanStorageUploadProgress(0);
  }, [scanPdfFile]);

  useEffect(() => {
    setSplitPreviewDescriptor(null);
  }, [splitPreviewFile]);

  const ensureScanPdfStorageDescriptor = useCallback(async (file) => {
    if (!file) throw new Error('스캔 PDF 파일이 없어요');
    if (
      scanPdfDescriptor
      && scanPdfDescriptor.name === file.name
      && Number(scanPdfDescriptor.size) === Number(file.size)
    ) {
      return scanPdfDescriptor;
    }
    const userId = currentUser?.uid;
    if (!userId || isAnonymous) {
      throw new Error('Storage 업로드를 위해 로그인이 필요해요');
    }
    setScanStorageUploadProgress(1);
    const descriptor = await uploadScoringAttachment(
      file,
      userId,
      'scoring_scan_pdf',
      setScanStorageUploadProgress
    );
    setScanPdfDescriptor(descriptor);
    setScanStorageUploadProgress(100);
    return descriptor;
  }, [currentUser?.uid, isAnonymous, scanPdfDescriptor]);

  const ensureBatchStorageDescriptor = useCallback(async (file) => {
    if (!file) throw new Error('업로드할 파일이 없어요');
    const cached = scoringDescriptorCacheRef.current.get(file);
    if (cached) return cached;
    const userId = currentUser?.uid;
    if (!userId || isAnonymous) {
      throw new Error('Storage 업로드를 위해 로그인이 필요해요');
    }
    const descriptor = await uploadScoringAttachment(
      file,
      userId,
      'scoring_batch'
    );
    scoringDescriptorCacheRef.current.set(file, descriptor);
    return descriptor;
  }, [currentUser?.uid, isAnonymous]);

  const handleBatchFilesWithScanDetect = useCallback(async (files, requestedStructure = batchAnswerStructure) => {
    // Storage 직접 업로드 상한(50MB)에 가까운 PDF만 선택적으로 압축한다.
    // 압축은 pdfjs-dist + pdf-lib 으로 기본 250DPI/JPEG92 변환 (채점용 OCR 품질 우선).
    // 압축 후 흐름이 그대로 detectScanPdf → 스캔 모드 카드로 이어진다 (사용자 체감 변경 0).
    let workingFiles = files;
    // 압축 시 알고있는 페이지 수를 detectScanPdf fallback 으로 사용하기 위해 보존.
    // (pdf-lib 으로 만든 PDF 는 useObjectStreams=false 이지만 만일을 대비한 안전망)
    let knownPageCount = null;
    if (
      files.length === 1 &&
      files[0].name.toLowerCase().endsWith('.pdf') &&
      files[0].size > 48 * 1024 * 1024 && // Storage 50MB 한도에 2MB 안전 마진
      batchFiles.length === 0
    ) {
      const original = files[0];
      const originalMB = (original.size / 1024 / 1024).toFixed(1);
      try {
        // 진행 토스트 제거 — 압축 모달이 같은 정보를 더 명확히 보여줌 (One Thing 원칙)
        setPdfCompressionProgress({
          current: 0,
          total: 0,
          filename: original.name,
          originalMB,
        });

        const result = await maybeCompressScanPdf(original, {
          thresholdBytes: 48 * 1024 * 1024,
          onProgress: (current, total) => {
            setPdfCompressionProgress({
              current,
              total,
              filename: original.name,
              originalMB,
            });
          },
        });

        setPdfCompressionProgress(null);

        // 압축 시 pdfjs 가 이미 페이지 수를 알았으므로 detectScanPdf fallback 으로 보존
        if (typeof result.pageCount === 'number' && result.pageCount > 0) {
          knownPageCount = result.pageCount;
        }

        if (result.compressed) {
          const compressedMB = (result.compressedSize / 1024 / 1024).toFixed(1);
          // 안전망: 압축 후에도 Storage 50MB 초과면 미리 차단.
          if (result.compressedSize > 50 * 1024 * 1024) {
            showToast('PDF가 너무 커요. 두 번에 나눠 올려주세요', 'warning', 8000);
            return;
          }
          const profile = result.compressionProfile
            ? ` (${result.compressionProfile.targetDpi}DPI · JPEG ${Math.round(result.compressionProfile.jpegQuality * 100)}%)`
            : '';
          showToast(`PDF를 ${compressedMB}MB로 압축했어요${profile}`, 'success', 4000);
          workingFiles = [result.file];
        } else {
          // 압축이 의미 없거나 더 커진 경우 — 원본 그대로 사용.
          if (result.file.size > 50 * 1024 * 1024) {
            showToast('PDF가 너무 커요. 페이지를 줄여서 다시 올려주세요', 'warning', 8000);
            return;
          }
          workingFiles = [result.file];
        }
      } catch (err) {
        setPdfCompressionProgress(null);
        console.error('PDF 자동 압축 실패:', err);
        showToast('PDF를 압축하지 못했어요. 다시 시도해주세요', 'error', 7000);
        return;
      }
    }

    const isSinglePdfUpload =
      workingFiles.length === 1 &&
      workingFiles[0].name.toLowerCase().endsWith('.pdf') &&
      batchFiles.length === 0;

    if (requestedStructure === 'scanPdf' && !isSinglePdfUpload) {
      showToast('방법 B는 PDF 1개만 올릴 수 있어요', 'warning');
      return;
    }

    if (isSinglePdfUpload) {
      // knownPageCount(압축 시 pdfjs 가 정확히 카운트한 값) 가 있으면 우선 사용.
      // 없으면 raw text 정규식 기반 detectScanPdf 사용 (외부 PDF 호환).
      const pageCount = knownPageCount ?? (await detectScanPdf(workingFiles[0]));
      if (pageCount >= 2 && pageCount <= SCAN_PDF_MAX_PAGES) {
        if (requestedStructure === 'scanPdf' || requestedStructure === null) {
          setBatchAnswerStructure('scanPdf');
          setScanPdfDetected(true);
          setScanPdfFile(workingFiles[0]);
          setScanPdfPageCount(pageCount);
          setScanRangePreview(null);
          setScanPageRanges([]);
          setScanRangeError('');
          return;
        }
      }
      if (requestedStructure === 'scanPdf' && pageCount < 2) {
        showToast('방법 B는 2페이지 이상의 PDF만 사용할 수 있어요', 'warning', 7000);
        return;
      }
      if (pageCount > SCAN_PDF_MAX_PAGES) {
        // 최대 페이지 초과 PDF는 일괄 채점 모드 진입 자체를 차단 (이전 회귀: 토스트만 띄우고
        // 일괄 채점 슬롯에 1학생 답안으로 들어가버려 일반 Vision 페이지 제한만 적용되는 사고).
        // 사용자가 직접 PDF를 분할하거나 학생별 파일로 다시 올리도록 안내.
        showToast(
          `한 PDF에 담을 수 있는 답안은 최대 ${SCAN_PDF_MAX_PAGES}페이지예요 (현재 ${pageCount}페이지). PDF를 두 번에 나눠 올리시거나, 학생별로 따로 올려주세요.`,
          'warning',
          10000
        );
        // 스캔 모드 상태도 초기화하고 업로드 자체를 차단
        setScanPdfDetected(false);
        setScanPdfFile(null);
        setScanPdfPageCount(0);
        setScanRangePreview(null);
        setScanPageRanges([]);
        setScanRangeError('');
        return;
      }
    }
    setScanPdfDetected(false);
    setScanPdfFile(null);
    setScanPdfPageCount(0);
    setScanRangePreview(null);
    setScanPageRanges([]);
    setScanRangeError('');
    performBatchFilesPreCheck(workingFiles);
  }, [batchAnswerStructure, batchFiles.length, detectScanPdf, performBatchFilesPreCheck, showToast]);

  const validateBatchFiles = useCallback((newFiles) => {
    if (!Array.isArray(newFiles) || newFiles.length === 0) return false;

    // 단일 PDF는 Storage 50MB 경로를 사용하며 48MB 초과 시 압축 흐름이 처리한다.
    // (handleBatchFilesWithScanDetect 가 압축 흐름을 책임진다)
    const isSinglePdf =
      newFiles.length === 1 && /\.pdf$/i.test(newFiles[0].name || '');

    const oversized = newFiles.filter(f => {
      if (isSinglePdf && /\.pdf$/i.test(f.name || '')) return false;
      return f.size > PER_FILE_MAX_SIZE;
    });
    if (oversized.length > 0) {
      showToast('파일이 너무 커요. 50MB 이하로 줄여주세요', 'warning', 6000);
      return false;
    }
    const oversizedImages = newFiles.filter(
      f => hasImageFilename(f.name) && f.size > SINGLE_IMAGE_MAX_SIZE
    );
    if (oversizedImages.length > 0) {
      showToast('이미지 파일은 20MB 이하로 줄여주세요', 'warning', 6000);
      return false;
    }

    return true;
  }, [showToast]);

  const handleBatchFilesUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!checkProAccess()) return;
    if (!validateBatchFiles(files)) return;

    handleBatchFilesWithScanDetect(files, batchAnswerStructure);
  }, [checkProAccess, handleBatchFilesWithScanDetect, validateBatchFiles, batchAnswerStructure]);

  const processSplitFile = useCallback(async (file) => {
    if (splitPreviewLoading) return;
    if (!file) return;
    if (!checkProAccess()) return;
    if (!validateBatchFiles([file])) return;
    if (hasImageFilename(file.name) && file.size > SINGLE_IMAGE_MAX_SIZE) {
      showToast('이미지 파일은 20MB 이하로 올려주세요', 'warning', 6000);
      return;
    }

    setSplitPreviewLoading(true);
    setSplitPreviewProgress(8);
    setSplitPreviewMessage('파일을 읽고 있어요');
    setSplitPreview(null);
    setSplitPreviewFile(file);
    setSplitPreviewDescriptor(null);
    setRestoredSplitMetadata(null);
    setBatchStudents([]);
    setBatchFiles([]);
    setBatchFilesStats([]);
    setScanPdfDetected(false);
    setScanPdfFile(null);
    setScanPdfPageCount(0);
    setScanRangePreview(null);
    setScanPageRanges([]);
    setScanRangeError('');

    const startedAt = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const nextProgress = Math.min(92, 8 + Math.round(elapsed / 900));
      setSplitPreviewProgress(nextProgress);
      if (elapsed < 12000) {
        setSplitPreviewMessage('파일에서 내용을 읽고 있어요');
      } else if (elapsed < 36000) {
        setSplitPreviewMessage('AI가 학생별 답안 경계를 찾고 있어요');
      } else {
        setSplitPreviewMessage('확인할 목록을 정리하고 있어요');
      }
    }, 800);

    try {
      const token = currentUser && !isAnonymous ? await currentUser.getIdToken() : null;
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const descriptor = await ensureBatchStorageDescriptor(file);
      setSplitPreviewDescriptor(descriptor);
      const formData = new FormData();
      formData.append('file_descriptor', JSON.stringify(descriptor));

      const response = await fetchWithRetry(
        getApiUrl('/api/lab/split-student-answers-preview'),
        { method: 'POST', headers, body: formData },
        {
          maxRetries: 1,
          timeoutMs: calcApiTimeout('student-split'),
        }
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.detail || '학생 답안을 나누지 못했어요');
      }

      const detectedStudents = (data?.students || [])
        .filter((student) => (student.answer_text || '').trim().length > 0)
        .map((student, index) => {
          const previewName = studentNameForSubmit(student.student_name || student.display_name);
          return {
            name: previewName,
            name_source: previewName ? NAME_SOURCE.AI_PREVIEW : NAME_SOURCE.PLACEHOLDER,
            filename: `${previewName || placeholderStudentLabel(index)} · ${student.source_pages?.length ? `${student.source_pages.join(', ')}쪽` : file.name}`,
            source_file_name: file.name,
            answer_text: student.answer_text,
            answer_excerpt: student.answer_excerpt || compactAnswerPreview(student.answer_text),
            source_pages: student.source_pages || [],
            split_student_id: student.student_id,
            client_student_id: student.student_id || createClientStudentId('split'),
            split_confidence: student.confidence,
            split_warnings: student.warnings || [],
            split_strategy: data?.strategy || 'ai_text',
          };
        });
      const shortAnswerCount = (data?.students || []).filter(
        (student) => (student.answer_text || '').trim().length <= 10
      ).length;
      const splitWarnings = [
        ...(Array.isArray(data?.warnings) ? data.warnings : []),
        ...(shortAnswerCount > 0 ? [`${shortAnswerCount}명의 답안 내용이 비어 있거나 너무 짧아요. 원본 파일에서 해당 학생 답안을 확인해 주세요.`] : []),
      ];

      if (data?.success && detectedStudents.length > 0) {
        const { students: _serverStudents, ...previewData } = data;
        setSplitPreview({
          ...previewData,
          warnings: splitWarnings,
          initial_pages: collectSplitPages(detectedStudents),
          initial_detected_count: detectedStudents.length,
          removed_students: [],
          merged_count: 0,
        });
        setBatchStudents(detectedStudents);
        setSplitPreviewProgress(100);
        setSplitPreviewMessage('학생 목록을 만들었어요');
        showToast(`${detectedStudents.length}명으로 나눴어요. 확인 후 채점해 주세요`, 'success');
        if (shortAnswerCount > 0) {
          showToast(`${shortAnswerCount}명의 답안이 비어 있거나 너무 짧아요. 채점 전 원본을 확인해 주세요.`, 'warning', 8000);
        }
      } else {
        setSplitPreview(null);
        setSplitPreviewFile(null);
        setBatchStudents([]);
        setSplitPreviewProgress(0);
        setSplitPreviewMessage('');
        showToast('여러 학생 답안을 충분히 찾지 못했어요. 학생별 파일 방식으로 다시 올려주세요', 'warning', 7000);
      }
    } catch (error) {
      console.error('학생 답안 나누기 실패:', error);
      showToast(error.message || '학생 답안을 나누지 못했어요', 'error');
      setSplitPreview(null);
      setBatchStudents([]);
      setSplitPreviewProgress(0);
      setSplitPreviewMessage('');
    } finally {
      clearInterval(progressInterval);
      setSplitPreviewLoading(false);
    }
  }, [checkProAccess, currentUser, ensureBatchStorageDescriptor, isAnonymous, showToast, splitPreviewLoading, validateBatchFiles]);

  const handleSplitFileUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    const file = files[0];
    await processSplitFile(file);
    if (e.target) e.target.value = '';
  }, [processSplitFile]);

  const processScanPageRanges = useCallback(async (file, pageCount) => {
    if (!file || scanRangeLoading) return;
    if (!checkProAccess()) return;

    setScanRangeLoading(true);
    setScanRangeError('');
    setScanRangePreview(null);
    setScanPageRanges([]);

    try {
      const token = currentUser && !isAnonymous ? await currentUser.getIdToken() : null;
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const descriptor = await ensureScanPdfStorageDescriptor(file);
      const formData = new FormData();
      formData.append('file_descriptor', JSON.stringify(descriptor));

      const response = await fetchWithRetry(
        getApiUrl('/api/lab/split-scan-pdf-pages-preview'),
        { method: 'POST', headers, body: formData },
        {
          maxRetries: 1,
          timeoutMs: calcApiTimeout('student-split'),
          retryOnTimeout: false,
        }
      );
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.detail || '학생 페이지 범위를 나누지 못했어요');
      }

      const ranges = normalizeScanPageRangesForClient(data?.page_ranges || [], pageCount);
      if (data?.success && ranges.length > 0) {
        setScanRangePreview(data);
        setScanPageRanges(ranges);
        showToast(`${ranges.length}명 범위로 나눴어요. 확인 후 채점해 주세요`, 'success', 5000);
      } else {
        const fallback = buildEvenScanPageRanges(pageCount, pageCount > 40 ? 2 : 1);
        setScanRangePreview({
          success: true,
          confidence: 'low',
          warnings: ['AI가 학생 범위를 충분히 찾지 못해 기본 분할안을 만들었어요.'],
          page_count: pageCount,
          detected_count: fallback.length,
        });
        setScanPageRanges(fallback);
        setScanRangeError('AI가 학생 범위를 충분히 찾지 못했어요. 아래 범위를 직접 확인해 주세요.');
      }
    } catch (error) {
      console.error('스캔 PDF 학생 범위 나누기 실패:', error);
      const fallback = buildEvenScanPageRanges(pageCount, pageCount > 40 ? 2 : 1);
      setScanPageRanges(fallback);
      setScanRangeError(error.message || '학생 페이지 범위를 나누지 못했어요. 아래 기본 범위를 직접 확인해 주세요.');
      showToast(error.message || '학생 페이지 범위를 나누지 못했어요', 'warning', 7000);
    } finally {
      setScanRangeLoading(false);
    }
  }, [checkProAccess, currentUser, ensureScanPdfStorageDescriptor, isAnonymous, scanRangeLoading, showToast]);

  useEffect(() => {
    if (!scanPdfDetected || !scanPdfFile || !scanPdfPageCount) return;
    if (scanRangePreview || scanRangeLoading || scanRangeError || scanPageRanges.length > 0) return;
    processScanPageRanges(scanPdfFile, scanPdfPageCount);
  }, [
    processScanPageRanges,
    scanPageRanges.length,
    scanPdfDetected,
    scanPdfFile,
    scanPdfPageCount,
    scanRangeError,
    scanRangeLoading,
    scanRangePreview,
  ]);

  const handleBatchStructureSelect = useCallback(async (structure) => {
    const carryoverFiles = batchCarryoverFiles.filter(Boolean);
    const carryoverStats = batchCarryoverFilesStats || [];

    if (carryoverFiles.length === 0) {
      setBatchAnswerStructure(structure);
      return;
    }

    if (structure === 'studentFiles') {
      setBatchAnswerStructure('studentFiles');
      actualBatchFilesUpload(carryoverFiles, carryoverStats);
      setAnswerFiles([]);
      setAnswerFilesStats([]);
      setBatchCarryoverFiles([]);
      setBatchCarryoverFilesStats([]);
      return;
    }

    if (structure === 'scanPdf') {
      const file = carryoverFiles[0];
      if (carryoverFiles.length !== 1 || !file.name.toLowerCase().endsWith('.pdf')) {
        showToast('방법 B는 PDF 1개만 올릴 수 있어요', 'warning', 7000);
        return;
      }

      const pageCount = await getPdfPageCount(file);
      if (pageCount < 2) {
        showToast('방법 B는 2페이지 이상의 PDF만 사용할 수 있어요', 'warning', 7000);
        return;
      }
      if (pageCount > SCAN_PDF_MAX_PAGES) {
        showToast(`한 PDF에 담을 수 있는 답안은 최대 ${SCAN_PDF_MAX_PAGES}페이지예요. PDF를 나눠서 올려주세요.`, 'warning', 8000);
        return;
      }

      setBatchAnswerStructure('scanPdf');
      setScanPdfDetected(true);
      setScanPdfFile(file);
      setScanPdfPageCount(pageCount);
      setScanRangePreview(null);
      setScanPageRanges([]);
      setScanRangeError('');
      setAnswerFiles([]);
      setAnswerFilesStats([]);
      setBatchCarryoverFiles([]);
      setBatchCarryoverFilesStats([]);
      return;
    }

    if (structure === 'singleFileStudents') {
      if (carryoverFiles.length !== 1) {
        showToast('방법 C는 여러 학생 답안이 들어있는 파일 1개를 올려주세요', 'warning', 7000);
        return;
      }

      setBatchAnswerStructure('singleFileStudents');
      await processSplitFile(carryoverFiles[0]);
      setAnswerFiles([]);
      setAnswerFilesStats([]);
      setBatchCarryoverFiles([]);
      setBatchCarryoverFilesStats([]);
    }
  }, [
    actualBatchFilesUpload,
    batchCarryoverFiles,
    batchCarryoverFilesStats,
    processSplitFile,
    showToast,
  ]);

  const handleBatchFileDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (!checkProAccess()) return;
    if (batchUploadMode === 'teacher' && !batchAnswerStructure) {
      showToast('먼저 업로드 방식을 선택해 주세요', 'warning');
      return;
    }
    if (batchAnswerStructure === 'singleFileStudents') {
      if (splitPreviewLoading) {
        showToast('AI가 학생별로 나누는 중이에요. 잠시만 기다려주세요', 'info');
        return;
      }
      showToast('한 파일 나누기는 클릭해서 파일을 선택해 주세요', 'info');
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (!validateBatchFiles(files)) return;

    handleBatchFilesWithScanDetect(files, batchAnswerStructure);
  }, [checkProAccess, handleBatchFilesWithScanDetect, validateBatchFiles, batchAnswerStructure, batchUploadMode, splitPreviewLoading, showToast]);

  const handleBatchStudentNameChange = useCallback((index, newName) => {
    setBatchStudents(prev => prev.map((s, i) => i === index ? {
      ...s,
      name: newName,
      name_source: nameSourceForTeacherEdit(newName),
      _recognitionEdited: true,
    } : s));
  }, []);

  const batchRosterItems = useMemo(() => batchStudents.map((student, index) => ({
    key: batchStudentRosterKey(student, index),
    name: student.name,
  })), [batchStudents]);

  const handleBatchRosterRename = useCallback((key, newName) => {
    setBatchStudents(prev => prev.map((s, i) => batchStudentRosterKey(s, i) === key ? {
      ...s,
      name: newName,
      name_source: nameSourceForTeacherEdit(newName),
      _recognitionEdited: true,
    } : s));
  }, []);

  const handleBatchStudentAnswerChange = useCallback((index, newAnswerText) => {
    setBatchStudents(prev => prev.map((s, i) => i === index ? {
      ...s,
      answer_text: newAnswerText,
      answer_excerpt: compactAnswerPreview(newAnswerText),
      _recognitionEdited: true,
    } : s));
  }, []);

  const handleBatchStudentRemove = useCallback((index) => {
    setBatchFiles(prev => prev.filter((_, i) => i !== index));
    setBatchStudents(prev => prev.filter((_, i) => i !== index));
    setBatchFilesStats(prev => prev.filter((_, i) => i !== index));
  }, []);

  // 학생 제외: 바로 제외하고 5초 동안 '실행 취소'로 되돌릴 수 있게 한다 (확인창 대신)
  const handleBatchStudentRemoveWithConfirm = useCallback((index, student) => {
    const removedStudent = batchStudents[index];
    const removedStats = batchFilesStats[index];
    const name = student?.name || removedStudent?.name || `학생 ${index + 1}`;
    const removedPages = formatPageList(removedStudent?.source_pages);

    handleBatchStudentRemove(index);
    if (removedStudent && removedPages) {
      setSplitPreview(prev => (prev ? {
        ...prev,
        removed_students: [...(prev.removed_students || []), removedStudent],
      } : prev));
    }

    const insertAt = (list, item) => {
      if (item === undefined) return list;
      const next = [...list];
      next.splice(Math.min(index, next.length), 0, item);
      return next;
    };

    let restored = false;
    let toastId = null;
    const removeMessage = removedPages
      ? `${name} 칸을 지웠어요. ${removedPages}쪽은 채점되지 않아요`
      : `${name} 학생을 목록에서 제외했어요`;
    toastId = showToast(removeMessage, 'success', removedPages ? 7000 : 5000, {
      label: '실행 취소',
      onClick: () => {
        if (restored) return;
        restored = true;
        const alreadyBack = Boolean(removedStudent?.client_student_id)
          && batchStudentsRef.current.some(item => item.client_student_id === removedStudent.client_student_id);
        if (alreadyBack) {
          if (toastId) removeToast?.(toastId);
          return;
        }
        setBatchStudents(prev => insertAt(prev, removedStudent));
        setBatchFilesStats(prev => insertAt(prev, removedStats));
        setSplitPreview(prev => (prev?.removed_students ? {
          ...prev,
          removed_students: prev.removed_students.filter(item => (
            removedStudent?.client_student_id
              ? item.client_student_id !== removedStudent.client_student_id
              : item !== removedStudent
          )),
        } : prev));
        if (toastId) removeToast?.(toastId);
        showToast(`${name} 칸을 다시 넣었어요`, 'success');
      },
    });
  }, [batchStudents, batchFilesStats, handleBatchStudentRemove, showToast, removeToast]);

  const splitDroppedPages = useMemo(() => (
    batchAnswerStructure === 'singleFileStudents' && splitPreview
      ? findDroppedSplitPages(splitPreview.initial_pages, batchStudents)
      : []
  ), [batchAnswerStructure, batchStudents, splitPreview]);

  const splitUnassignedPages = useMemo(() => {
    if (batchAnswerStructure !== 'singleFileStudents' || !splitPreview) return [];
    const assigned = new Set(collectSplitPages(batchStudents));
    return collectSplitPages([{ source_pages: splitPreview.unassigned_pages }]).filter(page => !assigned.has(page));
  }, [batchAnswerStructure, batchStudents, splitPreview]);

  const splitUnassignedPagesAreCover = useMemo(() => {
    const firstAnswerPage = collectSplitPages(batchStudents)[0];
    return splitUnassignedPages.length > 0
      && firstAnswerPage !== undefined
      && splitUnassignedPages.every(page => page < firstAnswerPage);
  }, [batchStudents, splitUnassignedPages]);

  const handleRestoreRemovedSplitStudents = useCallback(() => {
    const present = new Set(batchStudents.map(item => item.client_student_id).filter(Boolean));
    const removed = (splitPreview?.removed_students || []).filter(item => !present.has(item.client_student_id));
    setSplitPreview(prev => (prev ? { ...prev, removed_students: [] } : prev));
    if (removed.length === 0) return;
    setBatchStudents(restoreSplitStudentsByPage(batchStudents, removed));
    showToast(`지운 칸 ${removed.length}개를 다시 넣었어요`, 'success');
  }, [batchStudents, showToast, splitPreview]);

  const handleBatchStudentMergePrevious = useCallback((index) => {
    const result = mergeSplitStudentIntoPrevious(batchStudents, index, {
      buildExcerpt: compactAnswerPreview,
      buildFilename: (merged, mergedIndex) => (
        `${merged.name || placeholderStudentLabel(mergedIndex)} · ${merged.source_pages.length ? `${merged.source_pages.join(', ')}쪽` : merged.source_file_name || ''}`
      ),
    });
    if (!result) return;
    const { students: nextStudents, merged, previous, absorbed } = result;
    const dropAt = list => (list.length > index ? list.filter((_, i) => i !== index) : list);
    setBatchStudents(nextStudents);
    setBatchFiles(dropAt);
    setBatchFilesStats(dropAt);

    const previousName = previous.name || `학생 ${index}`;
    const pages = formatPageList(merged.source_pages);
    let undone = false;
    let toastId = null;
    toastId = showToast(
      `${previousName} 학생 답안으로 이어 붙였어요${pages ? ` · ${pages}쪽` : ''}`,
      'success',
      6000,
      {
        label: '실행 취소',
        onClick: () => {
          if (undone) return;
          undone = true;
          if (toastId) removeToast?.(toastId);
          const position = batchStudentsRef.current.findIndex(item => item.client_student_id === merged.client_student_id);
          if (position < 0) return;
          setBatchStudents(prev => {
            const next = [...prev];
            next.splice(position, 1, previous, absorbed);
            return next;
          });
          showToast('다시 두 칸으로 나눴어요', 'success');
        },
      },
    );
  }, [batchStudents, removeToast, showToast]);

  const handleDownloadSplitRecognition = useCallback(async () => {
    if (batchStudents.length === 0) {
      showToast('다운로드할 인식 답안이 없어요.', 'info');
      return;
    }

    try {
      const XLSX = await import('xlsx');
      const rows = buildRecognitionExportRows(batchStudents, {
        contentColumnLabel: 'AI가 읽은 답안 원문',
        getContent: student => student.answer_text || '',
        getSourceFileName: student => student.source_file_name || splitPreviewFile?.name || '',
      });
      writeRecognitionWorkbook(XLSX, rows, {
        filename: `채점_인식답안_${getRecognitionExportDate()}.xlsx`,
        sheetName: '인식 답안',
      });
      showToast('인식 답안을 다운로드 폴더에 저장했어요.', 'success', 6000);
    } catch (error) {
      console.error('채점 인식 답안 엑셀 다운로드 실패:', error);
      showToast(error.message || '인식 답안을 다운로드하지 못했어요.', 'error');
    }
  }, [batchStudents, showToast, splitPreviewFile]);

  const handleScanRangeChange = useCallback((index, field, value) => {
    setScanPageRanges(prev => prev.map((range, i) => {
      if (i !== index) return range;
      if (field === 'student_name') {
        return { ...range, student_name: value, name_source: nameSourceForTeacherEdit(value) };
      }
      const nextValue = String(value || '').replace(/[^\d]/g, '');
      return {
        ...range,
        [field]: nextValue,
        confidence: range.confidence === 'manual' ? 'manual' : `${range.confidence || 'medium'}_edited`,
      };
    }));
  }, []);

  const scanRosterItems = useMemo(() => scanPageRanges.map((range, index) => ({
    key: `scan_${index}`,
    name: range.student_name,
  })), [scanPageRanges]);

  const handleScanRosterRename = useCallback((key, newName) => {
    setScanPageRanges(prev => prev.map((range, i) => (
      `scan_${i}` === key ? { ...range, student_name: newName, name_source: nameSourceForTeacherEdit(newName) } : range
    )));
  }, []);

  const handleScanRangeRemove = useCallback((index) => {
    setScanPageRanges(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleScanPdfSubmit = async () => {
    if (!ensureRubricReady()) return;
    if (!scanPdfFile) {
      showToast('스캔 PDF 파일이 없어요', 'error');
      return;
    }
    const rangeError = validateScanPageRanges(scanPageRanges, scanPdfPageCount);
    if (rangeError) {
      showToast(rangeError, 'warning', 7000);
      return;
    }
    const submittedScanPageRanges = buildNormalizedScanPageRangesForSubmit(scanPageRanges, scanPdfPageCount);

    if (batchRequestRef.current) {
      showToast('이미 채점이 진행 중이에요. 잠시만 기다려주세요.', 'warning');
      return;
    }
    batchRequestRef.current = true;

    setScanPdfLoading(true);
    setBatchLoading(true);
    setBatchProgress(0);
    setBatchRetryCount(0);
    setBatchError(null);
    const tipInterval = startTeacherTipTimer(setCurrentTip);

    const scanTimeoutMs = calcApiTimeout('scan-pdf-scoring', {
      pageCount: scanPdfPageCount,
      studentCount: scanPageRanges.length || scanPdfPageCount,
    });
    const progressDuration = scanTimeoutMs * 0.9;
    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(Math.round((elapsed / progressDuration) * 95), 95);
      setBatchProgress(progress);
    }, 500);

    try {
      const descriptor = await ensureScanPdfStorageDescriptor(scanPdfFile);
      const formData = new FormData();
      formData.append('rubric_text', rubricText);
      appendScoringOptions(formData);
      const activeQuestionSet = getActiveQuestionSet();
      if (activeQuestionSet) {
        formData.append('question_set', JSON.stringify(activeQuestionSet));
      } else {
        appendActiveRubricCriteria(formData);
      }
      formData.append('file_descriptor', JSON.stringify(descriptor));
      formData.append('page_ranges_json', JSON.stringify(submittedScanPageRanges));

      const token = currentUser && !isAnonymous ? await currentUser.getIdToken() : null;
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      setBatchMaxRetries(2);
      const response = await fetchWithRetry(
        getApiUrl('/api/lab/evaluate-answers-batch-pdf'),
        { method: 'POST', headers, body: formData },
        {
          maxRetries: 2,
          timeoutMs: scanTimeoutMs,
          retryOnTimeout: false,
          onRetry: (attempt) => setBatchRetryCount(attempt),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || '일괄 스캔 채점 중 오류가 발생했어요');
      }

      const data = await response.json();
      setBatchProgress(100);

      if (data.success) {
        applyScoringContextFromResponse(data);
        const batchResultsList = data.results || [];
        setBatchResults(batchResultsList);
        setReScoreInstructions('');
        setReScoreCount(0);
        setRescoreHistory([]);
        setActiveScoringPolicy(buildActiveScoringPolicyPayload());

        const rawScanAnswerTexts = Array.isArray(data.extracted_answer_texts) && data.extracted_answer_texts.length > 0
          ? normalizeScanAnswerTextCache(data.extracted_answer_texts)
          : batchResultsList.map(r => ({
              student_name: r.studentName,
              answer_text: '',
              answer_text_source: 'scan_ocr_failed',
              answer_text_warning: '스캔 답안 원문을 인식하지 못했어요.',
              pages: r.pages,
              source_pages: r.source_pages,
              storage_path: r.storage_path,
              file_type: r.file_type,
            }));
        const scanAnswerTexts = mergeCachedAnswerTextsWithResults(
          rawScanAnswerTexts,
          [],
          batchResultsList,
        );
        setCachedAnswerTexts(scanAnswerTexts);

        trackRiseFeatureAction('scoring', 'generate', {
          mode: activeQuestionSet ? 'multi-question-batch-scan' : 'batch-scan',
          student_count: batchResultsList.length,
          question_count: activeQuestionSet?.question_count || 1,
        });

        if (data.warning_low_student_count && data.warning_message) {
          showToast(data.warning_message, 'warning', 8000);
        }

        setTimeout(() => {
          resultsTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);

        if (currentUser && !isAnonymous) {
          try {
            const recordRubric = data.rubric_criteria || getActiveRubricCriteria();
            const recordQuestionSet = getActiveQuestionSet();
            const subjectName = recordQuestionSet?.questions?.[0]?.subject || recordRubric?.subject || parsedRubrics?.[0]?.subject || '';
            const saveResponse = await saveScoringRecord({
              rubric_text: rubricText,
              results: batchResultsList,
              cached_answer_texts: scanAnswerTexts,
              subject: subjectName,
              parsed_rubric: recordRubric || null,
              question_set: recordQuestionSet || null,
              type: recordQuestionSet ? 'multi-question-batch-scan' : 'batch-scan',
              student_count: batchResultsList.length,
              input_method: 'scan_pdf_page_ranges',
              answer_structure: 'scanPdfPageRanges',
              scan_metadata: data.scan_metadata || {
                mode: 'teacher_confirmed_ranges',
                page_count: scanPdfPageCount,
                student_count: submittedScanPageRanges.length,
                confidence: scanRangePreview?.confidence || '',
                warnings: scanRangePreview?.warnings || [],
                page_ranges: submittedScanPageRanges,
              },
              ...getScoringRecordMeta(),
            });
            if (saveResponse?.record_id) {
              setSavedScoringRecordId(saveResponse.record_id);
            }
            setIsSaved(true);
          } catch (saveErr) {
            console.error('채점 기록 저장 실패:', saveErr);
            setIsSaved(false);
          }
        }
      } else {
        throw new Error(data.message || '일괄 스캔 채점 결과를 받을 수 없어요');
      }
    } catch (error) {
      console.error('일괄 스캔 채점 실패:', error);
      const errorInfo = buildBatchErrorInfo(error, scanPageRanges.length || scanPdfPageCount);
      setBatchError({ ...errorInfo, retryFn: () => handleScanPdfSubmit() });
      showToast(errorInfo.toastMessage || error.message || '일괄 스캔 채점 중 오류가 발생했어요', 'error');
    } finally {
      clearInterval(progressInterval);
      clearInterval(tipInterval);
      setBatchLoading(false);
      setScanPdfLoading(false);
      setBatchProgress(0);
      setBatchRetryCount(0);
      setCurrentTip('');
      batchRequestRef.current = false;
    }
  };

  const handleScanPdfDismiss = useCallback(() => {
    setScanPdfDetected(false);
    const file = scanPdfFile;
    setScanPdfFile(null);
    setScanPdfPageCount(0);
    setScanRangePreview(null);
    setScanPageRanges([]);
    setScanRangeError('');
    if (file) {
      performBatchFilesPreCheck([file]);
    }
  }, [scanPdfFile, performBatchFilesPreCheck]);

  // 청크 단위로 일괄 채점. 학생 수가 chunkSize 이하면 단일 호출, 초과하면 분할.
  // 각 청크는 기존 /api/lab/evaluate-answers-batch 엔드포인트 사용 → 텍스트 추출+채점 통합.
  // 한 청크 실패해도 나머지 청크는 진행 → 부분 결과 보장.
  const submitBatchInChunks = useCallback(async (students, options = {}) => {
    const requestScoringOptions = options.scoringOptions || scoringOptions;
    // 재시도 등에서 기존 결과를 유지한 채 새 결과를 이어 붙이기 위한 기준 목록.
    // (없으면 새 채점 → 화면 결과를 이번 호출 결과로만 구성)
    const baseResults = Array.isArray(options.baseResults) ? options.baseResults : [];
    const CHUNK_SIZE = 10;
    const chunks = [];
    for (let i = 0; i < students.length; i += CHUNK_SIZE) {
      chunks.push(students.slice(i, i + CHUNK_SIZE));
    }
    const totalChunks = chunks.length;

    const token = currentUser && !isAnonymous ? await currentUser.getIdToken() : null;
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const aggregatedResults = [];
    const aggregatedExtractedTexts = [];
    const chunkErrors = [];
    let latestRubricCriteria = null;
    // 실제 채점 완료 인원 (표시용 — 추정치가 아니라 응답으로 확인된 사실)
    let completedCount = 0;
    setBatchLiveCounts({ completed: 0, total: students.length });

    for (let ci = 0; ci < chunks.length; ci++) {
      const chunk = chunks[ci];
      const chunkNumberOffset = (options.numberOffset || 0) + ci * CHUNK_SIZE;
      const chunkHasImages = chunk.some(s => hasImageFilename(s.filename || ''));

      // 진행률: 청크 시작 = (ci/total)*100, 완료 = ((ci+1)/total)*100
      // base/target ref를 갱신하면 useEffect가 1초마다 청크 내부 추정 진행률을 끌어올린다.
      const chunkStartProgress = Math.round((ci / totalChunks) * 100);
      const chunkEndProgress = Math.round(((ci + 1) / totalChunks) * 100);
      // 0%로 멈춰 있다는 인상을 안 주기 위해 시작 즉시 살짝 더해준다(전체 1%).
      const visibleStart = Math.max(chunkStartProgress, ci === 0 ? 1 : chunkStartProgress);
      batchProgressBaseRef.current = visibleStart;
      batchProgressTargetRef.current = chunkEndProgress;
      batchChunkExpectedMsRef.current = chunkHasImages
        ? BATCH_IMAGE_CHUNK_PROGRESS_MS
        : BATCH_TEXT_CHUNK_PROGRESS_MS;
      setBatchProgress(visibleStart);
      setChunkStartTime(Date.now());

      try {
        const activeQuestionSet = getActiveQuestionSet();

        const chunkTimeoutMs = calcApiTimeout('batch-scoring', {
          fileCount: chunk.length,
          hasImages: chunkHasImages,
        });

        setBatchMaxRetries(1);
        let data;
        if (activeQuestionSet) {
          // 다문항 채점은 청크 안에서 학생을 한 명씩 순차 호출한다.
          // 진행 구간/기대 시간도 학생 단위로 리셋해야 실제 속도와 맞는다.
          // (기존에는 청크 전체 기대 시간을 첫 학생이 다 소진해 나머지 학생 동안 얼어붙었음)
          const chunkRange = chunkEndProgress - chunkStartProgress;
          batchChunkExpectedMsRef.current = Math.max(
            15_000,
            Math.round(batchChunkExpectedMsRef.current / chunk.length),
          );
          const multiResults = [];
          const multiExtractedTexts = [];
          for (let si = 0; si < chunk.length; si++) {
            const student = chunk[si];
            const usesMethodCText = batchAnswerStructure === 'singleFileStudents'
              || Boolean(student.split_student_id || student.source_file_name);
            const formData = new FormData();
            formData.append('rubric_text', rubricText);
            formData.append('question_set', JSON.stringify(activeQuestionSet));
            appendScoringOptions(formData, requestScoringOptions);
            const studentNumber = chunkNumberOffset + si + 1;
            formData.append('student_name', studentNameForSubmit(student.name, student.filename));
            formData.append(
              'student_name_source',
              studentNameSourceForSubmit(student.name, student.name_source, student.filename),
            );
            formData.append('student_number', String(studentNumber));
            formData.append(
              'client_student_id',
              student.client_student_id
                || student.submission_id
                || student.split_student_id
                || createClientStudentId('multi'),
            );
            if (usesMethodCText) {
              if (String(student.answer_text || '').trim().length <= 10) {
                throw new Error(`${student.name || placeholderStudentLabel(chunkNumberOffset + si)} 답안이 너무 짧거나 비어 있어요.`);
              }
              formData.append('answer_text', student.answer_text);
            } else if (student.answer_text) {
              formData.append('answer_text', student.answer_text);
            } else {
              const descriptor = await ensureBatchStorageDescriptor(student.file);
              formData.append('file_descriptors', JSON.stringify([descriptor]));
            }
            const response = await fetchWithRetry(
              getApiUrl('/api/lab/evaluate-answers-multi-question'),
              { method: 'POST', headers, body: formData },
              {
                maxRetries: 1,
                timeoutMs: chunkTimeoutMs,
                retryOnTimeout: false,
                onRetry: (attempt) => setBatchRetryCount(attempt),
              }
            );
            if (!response.ok) {
              const errorData = await response.json().catch(() => null);
              throw new Error(errorData?.detail || `${student.name || student.filename} 채점 실패`);
            }
            const one = await response.json();
            const result = (one.results || [])[0];
            if (result) {
              result.studentName = result.studentName || student.name || placeholderStudentLabel(studentNumber - 1);
              result.fileName = student.filename || result.fileName;
              multiResults.push(attachMethodCResultMeta(result, student));
              completedCount += 1;
              setBatchLiveCounts({ completed: completedCount, total: students.length });
            }
            if (Array.isArray(one.extracted_answer_texts)) {
              multiExtractedTexts.push(...one.extracted_answer_texts.map(t => ({
                ...t,
                student_name: result?.studentName || t.student_name,
              })));
            }
            // 학생 한 명 완료 → 진행 구간을 실제 완료 지점으로 이동 + 타이머 리셋
            const intraProgress = chunkStartProgress + (chunkRange * (si + 1)) / chunk.length;
            batchProgressBaseRef.current = Math.max(batchProgressBaseRef.current, intraProgress);
            setBatchProgress(prev => Math.max(prev, Math.round(intraProgress)));
            setChunkStartTime(Date.now());
            // 부분 결과 즉시 반영 (로딩 중에도 완료된 학생이 보이도록)
            setBatchResults([...baseResults, ...aggregatedResults, ...multiResults]);
          }
          data = { success: true, results: multiResults, extracted_answer_texts: multiExtractedTexts };
        } else {
          const formData = new FormData();
          formData.append('rubric_text', rubricText);
          appendScoringOptions(formData, requestScoringOptions);
          appendActiveRubricCriteria(formData);
          const chunkUsesSplitTexts = batchAnswerStructure === 'singleFileStudents'
            || chunk.every(s => Boolean(s.split_student_id || s.source_file_name));
          if (chunkUsesSplitTexts) {
            const invalidStudent = findInvalidMethodCStudent(chunk);
            if (invalidStudent) {
              throw new Error(`${invalidStudent.name || '학생'} 답안이 너무 짧거나 비어 있어요.`);
            }
            formData.append('students_payload', JSON.stringify(chunk.map(buildMethodCStudentPayload)));
            formData.append('student_number_offset', String(chunkNumberOffset));
            if (batchAnswerStructure === 'singleFileStudents' && splitPreviewFile) {
              const sourceDescriptor = splitPreviewDescriptor
                || await ensureBatchStorageDescriptor(splitPreviewFile);
              formData.append('file_descriptors', JSON.stringify([sourceDescriptor]));
            }
          } else {
            const descriptors = [];
            for (const student of chunk) {
              descriptors.push(await ensureBatchStorageDescriptor(student.file));
            }
            formData.append('file_descriptors', JSON.stringify(descriptors));
            formData.append('student_names', JSON.stringify(chunk.map(s => studentNameForSubmit(s.name, s.filename))));
            formData.append('student_name_sources', JSON.stringify(chunk.map(s => (
              studentNameSourceForSubmit(s.name, s.name_source, s.filename)
            ))));
            formData.append('student_number_offset', String(chunkNumberOffset));
            formData.append('student_ids', JSON.stringify(chunk.map(s => (
              s.client_student_id || s.submission_id || createClientStudentId('file')
            ))));
          }
          const hasEmptyNames = chunk.some(s => isPlaceholderStudentName(s.name, s.filename));
          if (hasEmptyNames) formData.append('auto_detect_names', 'true');

          const response = await fetchWithRetry(
            getApiUrl('/api/lab/evaluate-answers-batch'),
            { method: 'POST', headers, body: formData },
            {
              maxRetries: 1,
              timeoutMs: chunkTimeoutMs,
              retryOnTimeout: false,
              onRetry: (attempt) => setBatchRetryCount(attempt),
            }
          );
          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.detail || `청크 ${ci + 1}/${totalChunks} 채점 실패`);
          }
          data = await response.json();
        }
        if (data.success) {
          applyScoringContextFromResponse(data);
          latestRubricCriteria = data.rubric_criteria || latestRubricCriteria;
          const chunkResults = data.results || [];
          aggregatedResults.push(...chunkResults);
          if (Array.isArray(data.extracted_answer_texts)) {
            aggregatedExtractedTexts.push(...data.extracted_answer_texts);
          }
          // 실제 완료 인원 갱신 (다문항 경로는 학생 단위로 이미 갱신됨)
          if (!activeQuestionSet) {
            completedCount += chunkResults.length;
            setBatchLiveCounts({ completed: completedCount, total: students.length });
          }
          // 부분 결과 즉시 반영 (재시도면 기존 결과 뒤에 이어 붙임)
          setBatchResults([...baseResults, ...aggregatedResults]);
          // 청크 완료 → progress를 chunkEndProgress로 끌어올림
          batchProgressBaseRef.current = chunkEndProgress;
          setBatchProgress(chunkEndProgress);
        } else {
          throw new Error(data.message || `청크 ${ci + 1}/${totalChunks} 결과 없음`);
        }
      } catch (e) {
        console.error(`[batch-chunk ${ci + 1}/${totalChunks}] 실패:`, e);
        chunkErrors.push({ chunkIndex: ci, total: totalChunks, message: e.message, students: chunk });
        // 단일 청크일 때는 throw → 호출자가 에러 카드 표시
        if (totalChunks === 1) throw e;
        // 다중 청크면 일단 다음 청크로 넘어감
        showToast(`${ci + 1}/${totalChunks} 청크 채점 실패: ${e.message.slice(0, 60)}`, 'warning');
        // 실패해도 progress는 다음 단계로 이동 → 0%에 멈춰있는 인상을 방지
        batchProgressBaseRef.current = chunkEndProgress;
        setBatchProgress(chunkEndProgress);
      }
    }

    setBatchProgress(100);
    return {
      results: aggregatedResults,
      extractedTexts: aggregatedExtractedTexts,
      errors: chunkErrors,
      totalChunks,
      rubricCriteria: latestRubricCriteria,
    };
  }, [
    currentUser, isAnonymous, rubricText, showToast, appendActiveRubricCriteria, getActiveQuestionSet, appendScoringOptions, applyScoringContextFromResponse, scoringOptions, batchAnswerStructure, ensureBatchStorageDescriptor, splitPreviewDescriptor, splitPreviewFile,
  ]);

  submitBatchInChunksRef.current = submitBatchInChunks;

  const handleBatchSubmit = async () => {
    if (!ensureRubricReady()) return;
    if (batchStudents.length === 0) {
      showToast('학생 답안 파일을 업로드해 주세요', 'warning');
      return;
    }
    if (batchAnswerStructure === 'singleFileStudents') {
      const invalidStudent = findInvalidMethodCStudent(batchStudents);
      if (invalidStudent) {
        showToast(
          `${invalidStudent.name || '학생'} 답안이 너무 짧거나 비어 있어요. 내용을 수정하거나 목록에서 제외해주세요.`,
          'warning',
          8000,
        );
        return;
      }
      if (splitDroppedPages.length > 0) {
        const ok = await confirmDialog({
          title: `${formatPageList(splitDroppedPages)}쪽은 빼고 채점할까요?`,
          description: '지운 칸에 있던 답안이라 어느 학생 점수에도 들어가지 않아요.',
          confirmLabel: '이대로 채점하기',
          cancelLabel: '다시 확인하기',
        });
        if (!ok) return;
      }
    }
    if (!checkProAccess()) return;

    if (batchRequestRef.current) {
      showToast('이미 채점이 진행 중이에요. 잠시만 기다려주세요.', 'warning');
      return;
    }
    batchRequestRef.current = true;

    setBatchLoading(true);
    setBatchResults([]);
    setSavedScoringRecordId(null);
    setBatchProgress(0);
    setBatchRetryCount(0);
    setBatchFailedChunks([]);
    setBatchError(null);
    setChunkStartTime(null);
    setBatchLiveCounts(null);
    setBatchPhaseMessage('');
    batchProgressBaseRef.current = 0;
    batchProgressTargetRef.current = 0;
    batchChunkExpectedMsRef.current = BATCH_TEXT_CHUNK_PROGRESS_MS;
    const tipInterval = startTeacherTipTimer(setCurrentTip);

    try {
      const { results, extractedTexts, errors, totalChunks, rubricCriteria } = await submitBatchInChunks(batchStudents);

      // 채점은 끝났고 이제 정리/저장 단계 — "100%인데 안 끝남" 오인 방지
      setBatchPhaseMessage('채점이 끝났어요. 결과를 정리해서 저장하고 있어요...');
      setBatchResults(results);
      setReScoreInstructions('');
      setReScoreCount(0);
      setRescoreHistory([]);
      setActiveScoringPolicy(buildActiveScoringPolicyPayload());
      if (extractedTexts.length > 0) {
        setCachedAnswerTexts(extractedTexts);
      }
      const usesMethodC = batchAnswerStructure === 'singleFileStudents' && hasMethodCSplitStudents(batchStudents);
      const batchRecordType = (() => {
        const recordQuestionSet = getActiveQuestionSet();
        const base = recordQuestionSet ? 'multi-question-batch' : 'batch';
        const methodSuffix = usesMethodC ? '-split' : '';
        const chunkSuffix = totalChunks > 1 ? '_chunked' : '';
        return `${base}${methodSuffix}${chunkSuffix}`;
      })();
      
      trackRiseFeatureAction('scoring', 'generate', {
        mode: batchRecordType,
        student_count: results.length,
        chunk_count: totalChunks,
      });

      // 스크롤은 로딩 종료 후 결과 DOM 이 생기면 useEffect 가 실행한다
      batchScrollPendingRef.current = true;
      
      if (currentUser && !isAnonymous && results.length > 0) {
        try {
          const recordRubric = rubricCriteria || getActiveRubricCriteria();
          const recordQuestionSet = getActiveQuestionSet();
          const subjectName = recordQuestionSet?.questions?.[0]?.subject || recordRubric?.subject || parsedRubrics?.[0]?.subject || '';
          const saveResponse = await saveScoringRecord({
            rubric_text: rubricText,
            results,
            cached_answer_texts: mergeCachedAnswerTextsWithResults(extractedTexts || [], [], results),
            subject: subjectName,
            parsed_rubric: recordRubric || null,
            question_set: recordQuestionSet || null,
            type: batchRecordType,
            student_count: results.length,
            ...getScoringRecordMeta(),
            ...getMethodCRecordMeta(batchStudents),
          });
          if (saveResponse?.record_id) {
            setSavedScoringRecordId(saveResponse.record_id);
          }
          setIsSaved(true);
        } catch (saveErr) {
          console.error('채점 기록 저장 실패:', saveErr);
          setIsSaved(false);
        }
      } else {
        setIsSaved(false);
      }
      
      if (errors.length > 0) {
        // 일부 청크 실패 시 안내 (전체 실패가 아니므로 batchError 카드는 띄우지 않음)
        const failedCount = errors.reduce((sum, e) => sum + (e.students?.length || 0), 0);
        const successCount = results.length;
        // 실패 청크 정보를 저장 → 결과 화면에 재시도 버튼 노출
        setBatchFailedChunks(errors);
        showToast(
          `${successCount}명 채점 완료, ${failedCount}명 청크 실패 — 결과 화면에서 다시 시도할 수 있어요`,
          'warning',
        );
      } else {
        setBatchFailedChunks([]);
      }
    } catch (error) {
      console.error('일괄 채점 실패:', error);
      const errorInfo = buildBatchErrorInfo(error, batchStudents.length);
      setBatchError({ ...errorInfo, retryFn: () => handleBatchSubmit() });
      showToast(errorInfo.toastMessage, 'error');
    } finally {
      setBatchLoading(false);
      setBatchProgress(0);
      setBatchRetryCount(0);
      setChunkStartTime(null);
      setBatchLiveCounts(null);
      setBatchPhaseMessage('');
      batchProgressBaseRef.current = 0;
      batchProgressTargetRef.current = 0;
      batchChunkExpectedMsRef.current = BATCH_TEXT_CHUNK_PROGRESS_MS;
      clearInterval(tipInterval);
      setCurrentTip('');
      batchRequestRef.current = false;
    }
  };

  // 실패한 청크의 학생들만 다시 채점 → 기존 결과에 합쳐 표시.
  // 부분 결과를 보존하면서 실패 학생들만 재시도하므로 사용자 부담을 크게 줄여준다.
  const handleRetryFailedChunks = async () => {
    if (batchFailedChunks.length === 0) return;
    if (!ensureRubricReady()) return;
    if (batchRequestRef.current) {
      showToast('이미 채점이 진행 중이에요. 잠시만 기다려주세요.', 'warning');
      return;
    }
    batchRequestRef.current = true;

    // 실패 청크의 학생들을 단일 배열로 평탄화
    const failedStudents = batchFailedChunks.flatMap(c => c.students || []);
    if (failedStudents.length === 0) {
      batchRequestRef.current = false;
      return;
    }

    setBatchLoading(true);
    setBatchProgress(0);
    setBatchRetryCount(0);
    setBatchError(null);
    setChunkStartTime(null);
    setBatchLiveCounts(null);
    setBatchPhaseMessage('');
    batchProgressBaseRef.current = 0;
    batchProgressTargetRef.current = 0;
    batchChunkExpectedMsRef.current = BATCH_TEXT_CHUNK_PROGRESS_MS;
    const tipInterval = startTeacherTipTimer(setCurrentTip);

    // 재시도 시작 시점의 기존 결과 스냅샷.
    // submitBatchInChunks 가 부분 결과를 반영할 때 기존 결과 뒤에 이어 붙여
    // 이전에 채점된 학생들이 화면/기록에서 사라지거나 중복되지 않게 한다.
    const existingResultsSnapshot = [...(batchResultsRef.current || [])];

    try {
      const chunkSubmitter = submitBatchInChunksRef.current;
      if (!chunkSubmitter) {
        throw new Error('일괄 채점 준비가 아직 끝나지 않았어요. 잠시 후 다시 시도해주세요.');
      }
      const { results: newResults, extractedTexts: newExtractedTexts, errors: newErrors } = await chunkSubmitter(
        failedStudents,
        { baseResults: existingResultsSnapshot },
      );

      setBatchProgress(100);
      setBatchPhaseMessage('채점이 끝났어요. 결과를 정리해서 저장하고 있어요...');

      // 캐시된 답안 텍스트도 누적
      if (newExtractedTexts.length > 0) {
        setCachedAnswerTexts(prev => [...(prev || []), ...newExtractedTexts]);
      }

      // 실패 청크 갱신: 이번 시도에서도 실패한 학생만 남김
      setBatchFailedChunks(newErrors);

      const successCount = newResults.length;
      const failedCount = newErrors.reduce((sum, e) => sum + (e.students?.length || 0), 0);
      if (successCount > 0 && failedCount === 0) {
        showToast(`${successCount}명 재시도 채점 완료`, 'success');
      } else if (successCount > 0) {
        showToast(`${successCount}명 추가 채점 완료, ${failedCount}명은 여전히 실패`, 'warning');
      } else {
        showToast('재시도 모두 실패했어요. 잠시 후 다시 시도해 주세요.', 'error');
      }

      // 재시도로 새로 채점된 학생이 있다면 history도 갱신해야 한다.
      // saveScoringRecord는 매번 새 record를 만들지만, 부분 결과 → 전체 결과로 보강하는 의미.
      if (currentUser && !isAnonymous && newResults.length > 0) {
        try {
          // 최신 결과 = 재시도 시작 시점 스냅샷 + 새 결과
          // (batchResultsRef 는 부분 반영 중에 갱신되므로 그대로 쓰면 중복이 생긴다)
          const fullResults = [...existingResultsSnapshot, ...newResults];
          const recordRubric = getActiveRubricCriteria();
          const subjectName = recordRubric?.subject || parsedRubrics?.[0]?.subject || '';
          const saveResponse = await saveScoringRecord({
            rubric_text: rubricText,
            results: fullResults,
            cached_answer_texts: mergeCachedAnswerTextsWithResults(
              cachedAnswerTexts,
              newExtractedTexts || [],
              fullResults,
            ),
            subject: subjectName,
            parsed_rubric: recordRubric || null,
            type: 'batch_retry_chunked',
            student_count: fullResults.length,
            ...getScoringRecordMeta(),
          });
          if (saveResponse?.record_id) {
            setSavedScoringRecordId(saveResponse.record_id);
          }
          setIsSaved(true);
        } catch (saveErr) {
          console.error('재시도 후 채점 기록 저장 실패:', saveErr);
        }
      }
    } catch (error) {
      console.error('재시도 실패:', error);
      const errorInfo = buildBatchErrorInfo(error, failedStudents.length);
      showToast(errorInfo.toastMessage, 'error');
    } finally {
      setBatchLoading(false);
      setBatchProgress(0);
      setBatchRetryCount(0);
      setChunkStartTime(null);
      setBatchLiveCounts(null);
      setBatchPhaseMessage('');
      batchProgressBaseRef.current = 0;
      batchProgressTargetRef.current = 0;
      batchChunkExpectedMsRef.current = BATCH_TEXT_CHUNK_PROGRESS_MS;
      clearInterval(tipInterval);
      setCurrentTip('');
      batchRequestRef.current = false;
    }
  };

  const handleBatchDownloadExcel = useCallback(async () => {
    if (batchResults.length === 0) return;
    try {
      trackScoringUxEvent('export', { format: 'xlsx', mode: 'batch', isPro, isAnonymous: isAnonymousUser, sinceOpenMs: Date.now() - pageOpenedAtRef.current });
      const XLSX = await import('xlsx');
      const excelData = buildScoringResultExportRows({
        results: batchResults,
        cachedAnswerTexts,
        batchStudents,
        rubric: getActiveRubricCriteria(),
      });
      writeRecognitionWorkbook(XLSX, excelData, {
        filename: `일괄채점결과_${getRecognitionExportDate()}.xlsx`,
        sheetName: '일괄채점결과',
      });
      showToast('AI가 읽은 답안 원문과 채점 결과를 다운로드 폴더에 저장했어요.', 'success', 10000);
    } catch (error) {
      console.error('일괄 채점 엑셀 다운로드 실패:', error);
      showToast('엑셀 다운로드에 실패했어요', 'error');
    }
  }, [batchResults, batchStudents, cachedAnswerTexts, getActiveRubricCriteria, showToast, isPro, isAnonymousUser]);

  // A-4: 채점 결과 → 생기부 핸드오프. 다중 생기부 폼의 batchRestore 경로를 그대로 재사용한다.
  const handleHandoffToSaenggibu = useCallback((results, mode) => {
    if (!Array.isArray(results) || results.length === 0) return;
    if (isAnonymousUser) {
      setShowLoginModal(true);
      return;
    }
    const rubric = getActiveRubricCriteria();
    const handoff = buildSaenggibuHandoffState({
      results,
      cachedAnswerTexts,
      batchStudents,
      rubric,
      rubricTitle: rubric?.subject || selectedRubric?.subject || '',
    });
    if (!handoff) {
      showToast('생기부로 넘길 채점 내용이 없어요', 'info');
      return;
    }
    trackScoringUxEvent('export', { format: 'saenggibu', mode, isPro, isAnonymous: isAnonymousUser, sinceOpenMs: Date.now() - pageOpenedAtRef.current });
    navigate('/create-saenggibu', { state: handoff });
  }, [isAnonymousUser, getActiveRubricCriteria, cachedAnswerTexts, batchStudents, selectedRubric, showToast, isPro, navigate]);

  // A-5: 자료함에서 기준서 고르기 — 본문(텍스트)을 받아 직접 입력 칸에 넣는다(새 분석 API 없이 기존 텍스트 경로 재사용)
  const handleRubricLibraryPick = useCallback(async (material) => {
    if (!material?.id || !currentUser) return;
    try {
      const detail = await getReferenceMaterial(currentUser, material.id);
      // 응답은 { success, material: { content } } 형태
      const content = String(detail?.material?.content ?? detail?.content ?? '').trim();
      if (!content) {
        showToast('이 자료에는 읽을 수 있는 내용이 없어요', 'info');
        return;
      }
      // 예시 채우기와 같은 직접 리셋 경로. handleRubricTextChange 는 이미 확인된 기준서가 있으면
      // 빈 리뷰 시트를 열어 버리므로(클로저의 이전 상태) 여기서는 쓰지 않는다.
      rubricSourceRef.current = 'library';
      setRubricFile(null);
      setRubricText(content);
      setConfirmedRubric(null);
      setSelectedRubric(null);
      setParsedRubrics([]);
      setQuestionSet(null);
      setSelectedQuestionIds([]);
      setRubricNeedsConfirmation(false);
      sessionStorage.removeItem('scoring-confirmed-rubric');
      sessionStorage.removeItem('scoring-confirmed-question-set');
      setRubricInputMode('text');
      showToast(`자료함의 "${material.title || '자료'}"를 기준서로 불러왔어요`, 'success');
    } catch (error) {
      console.error('자료함 기준서 불러오기 실패:', error);
      showToast('자료함에서 불러오지 못했어요', 'error');
    }
  }, [currentUser, showToast]);

  const handleResultsPdfDownload = useCallback(async (
    excludeTeacherComment,
    targetResults = null,
  ) => {
    if (isGeneratingPdf) return;
    const results = Array.isArray(targetResults)
      ? targetResults
      : (batchResults.length > 0 ? batchResults : evaluationResults);
    const validCount = results.filter(result => !result?.error).length;
    if (validCount === 0) {
      showToast('다운로드할 채점 결과가 없어요.', 'info');
      return;
    }
    const questionSet = getActiveQuestionSet();
    const rubric = getActiveRubricCriteria();
    const subject = questionSet?.questions?.[0]?.subject
      || rubric?.subject
      || parsedRubrics?.[0]?.subject
      || '전체';
    setIsGeneratingPdf(true);
    showToast(`${validCount}명의 PDF를 준비하고 있어요...`, 'info');
    try {
      trackScoringUxEvent('export', { format: 'pdf', mode: batchResults.length > 0 ? 'batch' : 'single', isPro, isAnonymous: isAnonymousUser, sinceOpenMs: Date.now() - pageOpenedAtRef.current });
      await downloadScoringResultsPdf({
        results,
        subject,
        rubric,
        excludeTeacherComment,
        showToast,
      });
    } catch (error) {
      console.error('즉시 채점 결과 PDF 생성 실패:', error);
      showToast(error.message || 'PDF를 만들지 못했어요.', 'error');
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [
    batchResults,
    evaluationResults,
    getActiveQuestionSet,
    getActiveRubricCriteria,
    isGeneratingPdf,
    parsedRubrics,
    showToast,
    isPro,
    isAnonymousUser,
  ]);

  const [batchDetailIndex, setBatchDetailIndex] = useState(null);

  const doNextStudent = useCallback(() => {
    setAnswerFiles([]);
    setAnswerFilesStats([]);
    setAnswerText('');
    setEvaluationResults([]);
    setIsSaved(false);
    setSavedScoringRecordId(null);
    setCachedAnswerTexts([]);
    setReScoreInstructions('');
    setReScoreCount(0);
    setPreviousResults(null);

    if (studentSectionRef.current) {
      studentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setStudentSectionHighlight(true);
      setTimeout(() => setStudentSectionHighlight(false), 2000);
    }
  }, []);

  const handleScoreNextStudent = useCallback(() => {
    if (evaluationResults.length > 0 && !isSaved) {
      setShowResetConfirm(true);
    } else {
      doNextStudent();
    }
  }, [evaluationResults, isSaved, doNextStudent]);

  const activeRubric = getActiveRubricCriteria();
  const activeGradeOnly = isGradeOnlyRubric(activeRubric);
  const hasRubricInput = rubricText.trim().length > 0 && !rubricNeedsConfirmation;
  const shouldShowScoringCalibration = Boolean(
    answerText.trim()
    || answerFiles.length > 0
    || batchStudents.length > 0
    || scanPdfFile
    || submissionRoom?.code
    || roomSubmissions.length > 0
  );

  // ── 단계별 퍼널 계측(C-1): 입력 → 설정 → 실행 → 재채점 → 저장 ──
  // 상태 전이만 감시해 이벤트를 남기므로 기존 핸들러 흐름에는 손대지 않는다.
  const uxBase = useCallback((extra = {}) => ({
    isPro,
    isAnonymous: isAnonymousUser,
    sinceOpenMs: Date.now() - pageOpenedAtRef.current,
    ...extra,
  }), [isPro, isAnonymousUser]);
  const currentScoringUxMode = (() => {
    if (submissionRoom?.code || roomSubmissions.length > 0) return 'room';
    if (batchAnswerStructure === 'scanPdf' || scanPdfFile) return 'scan_pdf';
    if (batchAnswerStructure === 'singleFileStudents') return 'batch_split';
    if (batchAnswerStructure === 'studentFiles' || batchStudents.length > 0) return 'batch';
    return 'single';
  })();
  const currentAnswerInputKind = (() => {
    if (submissionRoom?.code || roomSubmissions.length > 0) return 'room';
    if (scanPdfFile) return 'scan_pdf';
    if (batchAnswerStructure === 'singleFileStudents') return 'files';
    if (batchStudents.length > 0) return 'student_files';
    if (answerFiles.length > 0) return 'single_file';
    if (answerText.trim()) return 'text';
    return 'unknown';
  })();

  // 1) 기준서가 처음 채워진 순간 (예시 채우기·파일·직접 입력 구분)
  useEffect(() => {
    if (!hasRubricInput) { rubricReadyTrackedRef.current = false; return; }
    if (rubricReadyTrackedRef.current) return;
    rubricReadyTrackedRef.current = true;
    const source = rubricSourceRef.current || (rubricFile ? 'file' : 'text');
    rubricSourceRef.current = null;
    trackScoringUxEvent('rubric_ready', uxBase({ source, mode: currentScoringUxMode }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRubricInput]);

  // 2) 답안이 처음 채워진 순간
  useEffect(() => {
    if (!shouldShowScoringCalibration) { answerReadyTrackedRef.current = false; return; }
    if (answerReadyTrackedRef.current) return;
    answerReadyTrackedRef.current = true;
    trackScoringUxEvent('answer_ready', uxBase({
      inputKind: currentAnswerInputKind,
      mode: currentScoringUxMode,
      studentCount: batchStudents.length || roomSubmissions.length || (answerText.trim() || answerFiles.length ? 1 : 0),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowScoringCalibration]);

  // 3) 채점 설정 변경 (어떤 항목을 만졌는지만)
  useEffect(() => {
    const prev = prevScoringOptionsRef.current;
    prevScoringOptionsRef.current = scoringOptions;
    if (!prev || prev === scoringOptions) return;
    const field = ['school_level', 'strictness', 'distribution_target', 'custom_instruction']
      .find((key) => JSON.stringify(prev[key]) !== JSON.stringify(scoringOptions[key]));
    if (!field) return;
    trackScoringUxEvent('calibration_changed', uxBase({ field, mode: currentScoringUxMode }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoringOptions]);

  // 4) 실행 시작/완료/실패 — 로딩 전이 + 결과 개수 증가 여부로 판정
  const anyScoringLoading = isLoading || batchLoading;
  const totalResultCount = evaluationResults.length + batchResults.length;
  useEffect(() => {
    const run = generateRunRef.current;
    if (anyScoringLoading && !run) {
      generateRunRef.current = {
        startedAt: Date.now(),
        singleBefore: evaluationResults,
        batchBefore: batchResults,
        mode: currentScoringUxMode,
      };
      trackScoringUxEvent('generate_started', uxBase({
        mode: currentScoringUxMode,
        studentCount: batchStudents.length || roomSubmissions.length || 1,
      }));
      return;
    }
    if (!anyScoringLoading && run) {
      generateRunRef.current = null;
      const elapsedMs = Date.now() - run.startedAt;
      const resultsChanged = evaluationResults !== run.singleBefore || batchResults !== run.batchBefore;
      if (resultsChanged && totalResultCount > 0) {
        trackScoringUxEvent('generate_completed', uxBase({ mode: run.mode, elapsedMs, studentCount: totalResultCount }));
      } else {
        trackScoringUxEvent('generate_failed', uxBase({
          mode: run.mode,
          elapsedMs,
          errorCode: batchError?.code || batchError?.type || 'unknown',
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyScoringLoading]);

  // 5) 재채점 횟수 증가
  useEffect(() => {
    const prev = prevReScoreCountRef.current;
    prevReScoreCountRef.current = reScoreCount;
    if (prev === null || reScoreCount <= prev) return;
    trackScoringUxEvent('rescore', uxBase({ mode: currentScoringUxMode }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reScoreCount]);

  // 6) 저장 완료 (수동·자동 저장 모두 isSaved 전이로 잡힌다)
  useEffect(() => {
    const prev = prevIsSavedRef.current;
    prevIsSavedRef.current = isSaved;
    if (prev === null || !isSaved || prev) return;
    trackScoringUxEvent('save_completed', uxBase({ mode: currentScoringUxMode, studentCount: totalResultCount }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaved]);

  useEffect(() => {
    if (!shouldShowScoringCalibration) {
      autoOpenedScoringCalibrationRef.current = false;
      return;
    }
    if (
      hasRubricInput
      && shouldShowScoringCalibration
      && evaluationResults.length === 0
      && batchResults.length === 0
      && !isLoading
      && !batchLoading
      && !autoOpenedScoringCalibrationRef.current
    ) {
      autoOpenedScoringCalibrationRef.current = true;
      setIsScoringCalibrationOpen(true);
    }
  }, [
    batchLoading,
    batchResults.length,
    evaluationResults.length,
    hasRubricInput,
    isLoading,
    shouldShowScoringCalibration,
  ]);

  const applyReScorePreset = useCallback((preset) => {
    setReScoreInstructions(preset.instruction);
    if (preset.strictness) {
      setScoringOptions(prev => ({ ...prev, strictness: preset.strictness }));
    }
    if (preset.distribution_target) {
      setScoringOptions(prev => ({ ...prev, distribution_target: normalizeDistributionTarget(preset.distribution_target) }));
    }
  }, []);

  const renderScoringCalibrationPanel = () => (
    <div className="scoring-calibration-panel">
      <button
        type="button"
        className="scoring-calibration-summary"
        onClick={() => setIsScoringCalibrationOpen(prev => !prev)}
        aria-expanded={isScoringCalibrationOpen}
      >
        <span className="scoring-calibration-title">채점 설정</span>
        <span className="scoring-calibration-current">
          {getSchoolLevelLabel(scoringOptions.school_level)} · {getStrictnessLabel(scoringOptions.strictness)}
          {scoringMode === 'batch' ? ` · ${getDistributionLabel(scoringOptions.distribution_target)}` : ''}
        </span>
        <span className="scoring-calibration-change">
          {isScoringCalibrationOpen ? '접기' : '열기'}
        </span>
      </button>

      {isScoringCalibrationOpen && (
        <div className="scoring-calibration-body">
          <div className="scoring-calibration-group">
            <span className="scoring-calibration-label">학생 수준</span>
            <div className="scoring-calibration-options">
              {SCHOOL_LEVEL_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`scoring-calibration-option scoring-calibration-option--simple ${scoringOptions.school_level === option.value ? 'active' : ''}`}
                  onClick={() => setScoringOptions(prev => ({ ...prev, school_level: option.value }))}
                >
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="scoring-calibration-group">
            <span className="scoring-calibration-label">채점 엄격도</span>
            <div className="scoring-calibration-options">
              {STRICTNESS_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`scoring-calibration-option ${scoringOptions.strictness === option.value ? 'active' : ''}`}
                  onClick={() => setScoringOptions(prev => ({ ...prev, strictness: option.value }))}
                >
                  <span>{option.label}</span>
                  <small>{option.hint}</small>
                </button>
              ))}
            </div>
          </div>
          {scoringMode === 'batch' && (
            <div className="scoring-calibration-group">
              <span className="scoring-calibration-label">점수 분포 참고</span>
              <div className="scoring-calibration-options scoring-calibration-options--distribution">
                {DISTRIBUTION_OPTIONS.map(option => {
                  const currentValue = getDistributionOptionValue(scoringOptions.distribution_target);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`scoring-calibration-option ${currentValue === option.value ? 'active' : ''}`}
                      onClick={() => setScoringOptions(prev => {
                        const nextTarget = option.value === 'custom'
                          ? {
                              ...normalizeDistributionTarget(prev.distribution_target),
                              mode: 'custom',
                            }
                          : option.target;
                        return { ...prev, distribution_target: nextTarget };
                      })}
                    >
                      <span>{option.label}</span>
                      <small>{option.hint}</small>
                    </button>
                  );
                })}
              </div>
              {getDistributionOptionValue(scoringOptions.distribution_target) === 'custom' && (
                <textarea
                  className="scoring-calibration-custom scoring-calibration-custom--compact"
                  value={scoringOptions.distribution_target?.custom_text || ''}
                  onChange={(e) => setScoringOptions(prev => ({
                    ...prev,
                    distribution_target: {
                      ...normalizeDistributionTarget(prev.distribution_target),
                      mode: 'custom',
                      custom_text: e.target.value.slice(0, 300),
                    },
                  }))}
                  placeholder="예: 최상 10%, 상 20%, 중 40%, 하 20%, 미흡 10%로 구분해줘"
                  rows={2}
                />
              )}
            </div>
          )}
          <div className="scoring-calibration-group">
            <label className="scoring-calibration-label" htmlFor="scoring-calibration-custom">
              AI에게 추가로 요청하기
            </label>
            <textarea
              id="scoring-calibration-custom"
              className="scoring-calibration-custom"
              value={scoringOptions.custom_instruction || ''}
              onChange={(e) => setScoringOptions(prev => ({
                ...prev,
                custom_instruction: e.target.value.slice(0, 2000),
              }))}
              placeholder={'예: 모범답안은 "…"이야. \'광합성\'·\'엽록체\'가 없으면 3점 이하로. 성실히 쓴 답은 기본 2점부터. 표현이 서툴러도 핵심 개념이 맞으면 인정해줘.'}
              rows={3}
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderReScorePresetButtons = () => (
    <div className="scoring-rescore-presets" aria-label="재채점 프리셋">
      {RESCORE_PRESETS.map((preset) => (
        <button
          key={preset.label}
          type="button"
          onClick={() => applyReScorePreset(preset)}
          disabled={reScoreLoading || !canReScoreCurrentResults}
        >
          {preset.label}
        </button>
      ))}
    </div>
  );

  const renderReScoreBusyOverlay = () => (
    reScoreLoading ? (
      <div className="scoring-page-busy-overlay" role="status" aria-live="polite">
        <div className="scoring-page-busy-card">
          <span className="scoring-page-busy-spinner" />
          <strong>재채점 중이에요</strong>
          <p>{currentTip || '결과가 바뀌는 동안 수정할 수 없어요. 잠시만 기다려주세요.'}</p>
        </div>
      </div>
    ) : null
  );

  return (
    <>
      <Helmet>
        <title>AI 서술형 채점 | 손글씨·타이핑 답안 자동 채점 | 왓퀴즈</title>
        <meta name="description" content="AI가 서술형·논술형 답안을 루브릭(채점기준표)에 따라 정확하게 채점합니다. 손글씨 답안도 사진으로 채점 가능. 국어, 영어, 수학, 과학, 사회 전 과목 지원. 채점 시간 90% 절약." />
        <meta name="keywords" content="서술형 채점, AI 채점, 루브릭 채점, 손글씨 채점, 논술형 채점, 자동 채점, 채점기준표, 교사 채점 도구, OCR 채점" />
        <link rel="canonical" href="https://whatquiz.co.kr/scoring" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://whatquiz.co.kr/scoring" />
        <meta property="og:title" content="AI 서술형 채점 | 손글씨·타이핑 답안 자동 채점 | 왓퀴즈" />
        <meta property="og:description" content="AI가 루브릭에 따라 서술형·논술형 답안을 정확하게 채점. 손글씨도 사진으로 채점 가능. 전 과목 지원." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png?v=20260210" />
        <meta property="og:locale" content="ko_KR" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SoftwareApplication",
                "name": "왓퀴즈 AI 서술형 채점",
                "description": "AI가 루브릭(채점기준표)에 따라 서술형·논술형 답안을 채점하는 교사 전용 도구. 손글씨 답안도 사진으로 채점 가능.",
                "applicationCategory": "EducationalApplication",
                "operatingSystem": "Web Browser",
                "offers": { "@type": "Offer", "price": "0", "priceCurrency": "KRW" },
                "author": { "@type": "Organization", "name": "왓퀴즈" }
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "AI 서술형 채점이란 무엇인가요?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "AI 서술형 채점은 채점 기준표와 학생 답안을 바탕으로 AI가 점수, 항목별 평가 근거, 피드백 초안을 생성하는 방식입니다."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "손글씨 답안도 채점할 수 있나요?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "네, 왓퀴즈는 손글씨 답안 사진과 스캔 PDF를 업로드해 채점할 수 있도록 지원합니다."
                    }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="scoring-page">
        <div className="scoring-header">
          <button
            className="scoring-back-button"
            onClick={async () => {
              if (batchResults.length > 0) {
                const ok = await confirmDialog({
                  title: '새로 채점할까요?',
                  description: '지금 결과는 사라져요. 엑셀로 먼저 저장해두세요',
                  confirmLabel: '새로 채점하기',
                  secondaryAction: { label: '엑셀 저장하기', onClick: handleBatchDownloadExcel },
                });
                if (ok) {
                  setBatchResults([]);
                  setSavedScoringRecordId(null);
                  setBatchFiles([]);
                  setBatchStudents([]);
                  setBatchFilesStats([]);
                  setBatchDetailIndex(null);
                  setCachedAnswerTexts([]);
                  setReScoreInstructions('');
                  setReScoreCount(0);
                  setPreviousResults(null);
                }
              } else if (evaluationResults.length > 0) {
                if (!isSaved) {
                  setShowResetConfirm(true);
                } else {
                  setEvaluationResults([]);
                  setIsSaved(false);
                  setSavedScoringRecordId(null);
                  setCachedAnswerTexts([]);
                  setReScoreInstructions('');
                  setReScoreCount(0);
                  setPreviousResults(null);
                }
              } else {
                navigate('/home');
              }
            }}
          >
            {evaluationResults.length > 0 || batchResults.length > 0 ? '← 새로 채점하기' : '← 홈으로'}
          </button>
          <div>
            <h1 className="scoring-title">AI 서술형 채점</h1>
            <p className="scoring-subtitle">
              채점 기준표를 올리면 AI가 자동으로 채점해요
            </p>
            <span className="scoring-handwriting-badge">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
              손글씨 사진도 채점돼요
            </span>
          </div>
        </div>

        <div className="scoring-content">
          <div className="scoring-section" style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', whiteSpace: 'normal', border: 0 }}>
            <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#3182f6' }}>핵심 답변</p>
            <h2 className="scoring-section-title" style={{ fontSize: 22, marginBottom: 12 }}>
              AI 서술형 채점이란?
            </h2>
            <p style={{ margin: '0 0 18px', fontSize: 15, lineHeight: 1.75, color: '#4e5968' }}>
              AI 서술형 채점은 채점 기준표와 학생 답안을 바탕으로 AI가 점수, 항목별 평가 근거, 잘한 점, 보완할 점을 생성하는 방식입니다. 왓퀴즈는 텍스트 답안뿐 아니라 손글씨 사진과 스캔 PDF도 업로드해 교사가 검토할 채점 초안을 만들 수 있게 합니다.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              {[
                { label: '필요 자료', value: '채점 기준표 + 학생 답안' },
                { label: '지원 답안', value: '텍스트, 사진, PDF, 스캔본' },
                { label: '출력 결과', value: '점수, 평가 근거, 피드백 초안' },
              ].map((item) => (
                <div key={item.label} style={{ background: '#f8fafc', borderRadius: 14, padding: 16 }}>
                  <strong style={{ display: 'block', fontSize: 12, color: '#8b95a1', marginBottom: 6 }}>{item.label}</strong>
                  <span style={{ fontSize: 14, color: '#191f28', lineHeight: 1.5 }}>{item.value}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #e5e8eb' }}>
              <p style={{ margin: '0 0 8px', fontSize: 14, lineHeight: 1.6, color: '#4e5968' }}>
                <strong>AI 서술형 채점이란 무엇인가요?</strong> 채점 기준표와 학생 답안을 바탕으로 AI가 점수와 평가 근거, 피드백 초안을 생성하는 방식입니다.
              </p>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: '#4e5968' }}>
                <strong>손글씨 답안도 채점할 수 있나요?</strong> 네, 왓퀴즈는 손글씨 답안 사진과 스캔 PDF 업로드를 지원합니다.
              </p>
            </div>
          </div>

          {/* Value First: 결과 UI 미리보기 (비로그인 항상 + 로그인 3회까지) */}
          {evaluationResults.length === 0 && batchResults.length === 0 && !isLoading && !batchLoading && (isAnonymousUser || showPreview) && (
            <div className="scoring-preview-card">
              <ScoringScanDemo surface="scoring_page" title="서술형 채점" />
            </div>
          )}

          {/* ── 일괄 채점 에러 카드: 실패 시 입력 영역 위에 표시 ── */}
          {/* 기존: 실패 시 토스트만 잠깐 뜨고 자동으로 입력 화면으로 돌아가서
              사용자가 "처음 화면으로 돌아왔다"고 느꼈음.
              개선: 에러 정보 + 재시도 버튼을 명시적으로 보여줌. */}
          {batchError && !batchLoading && (
            <div className="scoring-batch-error-card" role="alert">
              <div className="scoring-batch-error-icon" aria-hidden="true">
                <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
                  <circle cx="12" cy="12" r="10" fill="#f04452" />
                  <path d="M12 7v6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
                  <circle cx="12" cy="16.5" r="1.3" fill="#fff" />
                </svg>
              </div>
              <div className="scoring-batch-error-body">
                <h3 className="scoring-batch-error-title">{batchError.title}</h3>
                <p className="scoring-batch-error-message">{batchError.message}</p>
                <div className="scoring-batch-error-actions">
                  {batchError.canRetry && (
                    <button
                      type="button"
                      className="scoring-batch-error-retry-btn"
                      onClick={() => {
                        const fn = batchError.retryFn;
                        setBatchError(null);
                        if (typeof fn === 'function') fn();
                      }}
                    >
                      {batchError.actionLabel || '다시 시도'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="scoring-batch-error-dismiss-btn"
                    onClick={() => setBatchError(null)}
                  >
                    닫기
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── 입력 영역: 결과가 없을 때만 ── */}
          {evaluationResults.length === 0 && batchResults.length === 0 && !isLoading && !batchLoading && (
            <>
              {/* Section 1: 채점 기준서 */}
              <div className="scoring-section">
                <h2 className="scoring-section-title">
                  <ScoringHelpLabel help={SCORING_HELP.rubric}>채점 기준서</ScoringHelpLabel>
                </h2>

                <div className="scoring-input-tabs">
                  <button
                    className={`scoring-input-tab ${rubricInputMode === 'text' ? 'active' : ''}`}
                    onClick={() => setRubricInputMode('text')}
                  >
                    직접 입력
                  </button>
                  <button
                    className={`scoring-input-tab ${rubricInputMode === 'file' ? 'active' : ''}`}
                    onClick={() => setRubricInputMode('file')}
                  >
                    파일 업로드
                    {!isPro && <span className="scoring-pro-badge">PRO</span>}
                  </button>
                </div>

                <div className="scoring-rubric-container">
                  {rubricInputMode === 'text' && (
                    <textarea
                      ref={rubricTextareaRef}
                      value={rubricText}
                      onChange={(e) => {
                        handleRubricTextChange(e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.max(e.target.scrollHeight, 120) + 'px';
                      }}
                      placeholder="채점 기준표를 붙여넣으세요"
                      className="scoring-textarea"
                      rows={4}
                      disabled={rubricFileUploading}
                    />
                  )}

                  {rubricInputMode === 'file' && (
                    !isPro ? (
                      <div className="scoring-dropzone-compact scoring-dropzone-pro-lock">
                        <div className="scoring-pro-lock-content">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8b95a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                          </svg>
                          <span className="scoring-pro-lock-text">PRO에서 파일을 업로드할 수 있어요</span>
                          <span className="scoring-pro-lock-hint">PDF, HWP, PPT, 이미지, Excel 지원</span>
                          <button className="scoring-pro-lock-cta" onClick={() => setShowProRequiredModal(true)}>
                            PRO 시작하기
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`scoring-dropzone-compact ${isRubricDragging ? 'dragging' : ''} ${isFileChecking ? 'checking' : ''}`}
                        onDragOver={handleRubricDragOver}
                        onDragLeave={handleRubricDragLeave}
                        onDrop={handleRubricDrop}
                      >
                        {isFileChecking ? (
                          <div className="scoring-file-checking">
                            <div className="scoring-file-checking-spinner" />
                            <span className="scoring-file-checking-name">{checkingFileName}</span>
                            <span className="scoring-file-checking-status">파일 확인 중...</span>
                          </div>
                        ) : rubricFileUploading ? (
                          <div className="scoring-rubric-uploading">
                            <div className="scoring-rubric-progress-container">
                              <div
                                className="scoring-rubric-progress-bar"
                                style={{ width: `${rubricLoadingProgress}%` }}
                              />
                            </div>
                            <div className="scoring-spinner"></div>
                            <span className="scoring-rubric-loading-text">{rubricLoadingMessage}</span>
                          </div>
                        ) : rubricFile ? (
                          <div className="scoring-file-attached">
                            <span className="scoring-file-name">{rubricFile.name}</span>
                            <button onClick={handleRubricFileRemove} className="scoring-file-remove-btn">✕</button>
                          </div>
                        ) : (
                          <SaenggibuAttachMenu
                            currentUser={currentUser}
                            category="scoring"
                            onPick={handleRubricLibraryPick}
                            onTrackPick={() => {}}
                            onUploadClick={() => document.getElementById('scoring-rubric-file-input')?.click()}
                            renderTrigger={(triggerProps) => (
                          <div
                            {...triggerProps}
                            role="button"
                            tabIndex={0}
                            className="scoring-dropzone-label"
                            data-testid="scoring-rubric-dropzone"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); triggerProps.onClick(e); }
                            }}
                          >
                            <input
                              id="scoring-rubric-file-input"
                              type="file"
                              accept=".pdf,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.heic,.heif,.hwp,.hwpx,.docx,.xlsx,.xls"
                              onChange={handleRubricFileUpload}
                              onClick={(e) => e.stopPropagation()}
                              className="scoring-file-input-hidden"
                            />
                            <div className="scoring-dropzone-content-compact">
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="17 8 12 3 7 8"></polyline>
                                <line x1="12" y1="3" x2="12" y2="15"></line>
                              </svg>
                              <span className="scoring-dropzone-text-compact">파일을 그대로 올리세요</span>
                              <span className="scoring-dropzone-hint-compact">
                                PDF, HWP, PPT, 이미지, Excel 지원
                                <FieldHelpTooltip title={SCORING_HELP.rubricFile.title} items={SCORING_HELP.rubricFile.items} ariaLabel="채점 기준서 파일 안내" variant="compact" />
                              </span>
                            </div>
                          </div>
                            )}
                          />
                        )}
                      </div>
                    )
                  )}

                  {rubricInputMode === 'file' && rubricText && rubricFile && (
                    <textarea
                      value={rubricText}
                      onChange={(e) => {
                        handleRubricTextChange(e.target.value);
                      }}
                      className="scoring-textarea scoring-textarea-extracted"
                      rows={6}
                      placeholder="AI가 추출한 채점 기준"
                    />
                  )}
                </div>

                {activeGradeOnly && (
                  <div className="scoring-grade-only-notice">
                    <strong>배점 없는 등급제 기준서로 인식했어요</strong>
                    <span>채점 결과는 점수 대신 수행수준(상·중·하) 중심으로 보여드려요. 점수제가 필요하면 기준서에 배점을 넣어주세요.</span>
                  </div>
                )}

                {/* 처음 사용자 도움 */}
                {!rubricText.trim() && !rubricFile && (
                  <div className="scoring-empty-assist">
                    <details ref={scoringAssistDetailsRef}>
                      <summary>처음이라 막막하신가요?</summary>
                      <div className="scoring-empty-assist-card">
                        <button type="button" className="assist-sheet-close" onClick={closeScoringAssistGuide} aria-label="도움말 닫기">닫기</button>
                        <strong>기준표를 그대로 넣어도 괜찮아요</strong>
                        <span>루브릭, 배점표, 수행평가 기준표를 복사해서 붙여넣거나 파일로 올리면 AI가 채점 기준으로 정리해요.</span>
                        <div className="scoring-empty-assist-actions">
                          <button
                            type="button"
                            onClick={() => {
                              closeScoringAssistGuide();
                              setRubricText(`[수행평가] 독서 감상문 (총 40점)

1. 내용 이해 (15점)
- 매우잘함(15): 책의 핵심 내용을 정확히 파악하고 자신의 관점에서 재해석함
- 잘함(12): 책의 주요 내용을 이해하고 요약할 수 있음
- 보통(8): 줄거리를 파악하나 핵심 내용의 이해가 부분적임
- 노력요함(4): 책의 내용을 충분히 이해하지 못함

2. 논리적 서술 (15점)
- 매우잘함(15): 주장과 근거가 명확하고 단락 구성이 체계적임
- 잘함(12): 자신의 생각을 논리적으로 서술함
- 보통(8): 서술이 있으나 논리적 연결이 부족함
- 노력요함(4): 감상이 단편적이고 논리적 구성이 어려움

3. 표현력 (10점)
- 매우잘함(10): 다양한 어휘와 표현을 사용하여 풍부하게 서술함
- 잘함(8): 적절한 어휘를 사용하여 명확하게 표현함
- 보통(5): 기본적인 표현으로 서술함
- 노력요함(2): 어휘 사용이 제한적이고 표현이 부정확함`);
                              setConfirmedRubric(null);
                              setSelectedRubric(null);
                              setParsedRubrics([]);
                              setRubricNeedsConfirmation(false);
                              sessionStorage.removeItem('scoring-confirmed-rubric');
                              setRubricInputMode('text');
                              rubricSourceRef.current = 'example';
                              // 기준서 예시를 쓰는 사용자는 답안도 예시로 바로 써볼 확률이 높다 — 비어 있으면 함께 채운다
                              if (!answerText.trim() && answerFiles.length === 0) {
                                setAnswerText(EXAMPLE_STUDENT_ANSWER);
                              }
                            }}
                          >
                            예시 채우기
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              closeScoringAssistGuide();
                              setRubricInputMode('file');
                            }}
                          >
                            파일로 올리기
                          </button>
                        </div>
                      </div>
                    </details>
                  </div>
                )}
              </div>

              {/* 여러 과목/수행평가 감지 → 과목 선택 (Assessment Plan 교과 선택 패턴) */}
              {multiRubric && detectedSubjects.length > 1 && (
                <div className="scoring-section">
                  <h2 className="scoring-section-title">
                    {detectedSubjects.length}개 서술형 평가를 감지했어요
                  </h2>
                  <p className="scoring-rubric-detect-desc">
                    여러 채점 항목이 포함된 문서예요. 기본은 전체 문항을 한 번에 채점하고, 필요하면 한 문항만 선택할 수 있어요.
                  </p>
                  {rubricDetailLoading ? (
                    <div className="scoring-file-checking">
                      <div className="scoring-file-checking-spinner" />
                      <span>선택한 채점 기준을 분석하고 있어요...</span>
                    </div>
                  ) : (
                    <>
                      <button
                        className="scoring-rubric-card scoring-rubric-card-all"
                        onClick={() => handleSubjectSelect({ id: '__all__', name: '__all__' })}
                      >
                        <span className="scoring-rubric-card-name">전체 문항 한 번에 채점하기</span>
                        <div className="scoring-rubric-card-info">
                          <span className="scoring-rubric-card-subject">추천</span>
                          <span className="scoring-rubric-card-points">{detectedSubjects.reduce((sum, subj) => sum + (Number(subj.total_points) || 0), 0)}점</span>
                          <span className="scoring-rubric-card-items">{detectedSubjects.length}개 문항</span>
                        </div>
                      </button>
                      <div className="scoring-rubric-cards">
                        {detectedSubjects.map((subj) => (
                          <button
                            key={subj.id}
                            className="scoring-rubric-card"
                            onClick={() => handleSubjectSelect(subj)}
                          >
                            <span className="scoring-rubric-card-name">{subj.name || `수행평가 ${subj.id}`}</span>
                            <div className="scoring-rubric-card-info">
                              {subj.subject && <span className="scoring-rubric-card-subject">{subj.subject}</span>}
                              {subj.total_points > 0 && <span className="scoring-rubric-card-points">{subj.total_points}점</span>}
                              {subj.item_count > 0 && <span className="scoring-rubric-card-items">{subj.item_count}개 항목</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* 채점 모드 토글 */}
              {hasRubricInput && (
                <div className="scoring-mode-toggle">
                  <button
                    className={`scoring-mode-tab ${scoringMode === 'single' ? 'active' : ''}`}
                    onClick={() => setScoringMode('single')}
                  >
                    <span className="scoring-mode-tab-main">1명 채점</span>
                    <span className="scoring-mode-tab-sub">결과 1개</span>
                  </button>
                  <button
                    className={`scoring-mode-tab ${scoringMode === 'batch' ? 'active' : ''}`}
                    onClick={() => setScoringMode('batch')}
                  >
                    <span className="scoring-mode-tab-main">
                      여러 명 채점
                      {!isPro && <span className="scoring-pro-badge">PRO</span>}
                    </span>
                    <span className="scoring-mode-tab-sub">학생별 결과</span>
                  </button>
                </div>
              )}

              {/* Section 2A: 개별 채점 모드 */}
              {hasRubricInput && scoringMode === 'single' && (
                <div
                  ref={studentSectionRef}
                  className={`scoring-section scoring-section-sequential ${studentSectionHighlight ? 'scoring-section-highlight' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="scoring-auto-improvements">
                    <div className="scoring-auto-check">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                      <span>여기는 1명 채점이에요. 여러 파일을 올려도 결과는 1개만 나와요</span>
                    </div>
                    <div className="scoring-auto-check scoring-auto-check-action">
                      <div className="scoring-auto-check-copy">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        <span>학생 여러 명을 채점하려면 여러 명 채점으로 이동해 주세요</span>
                      </div>
                      <button
                        type="button"
                        className="scoring-auto-check-button"
                        onClick={openBatchMethodSelection}
                      >
                        여러 명 채점으로 가기
                      </button>
                    </div>
                  </div>

                  <h2 className="scoring-section-title">
                    <ScoringHelpLabel help={SCORING_HELP.singleAnswer}>학생 과제</ScoringHelpLabel>
                  </h2>

                  <textarea
                    ref={answerTextareaRef}
                    value={answerText}
                    onChange={(e) => {
                      setAnswerText(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.max(e.target.scrollHeight, 120) + 'px';
                    }}
                    placeholder="학생 과제 내용을 붙여넣으세요"
                    className={`scoring-textarea ${isDragging ? 'scoring-textarea-drag-active' : ''}`}
                    rows={4}
                  />

                  <div className="scoring-attach-row">
                    <label
                      className="scoring-attach-button"
                      onClick={(e) => {
                        if (!checkProAccess()) {
                          e.preventDefault();
                        }
                      }}
                    >
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.heic,.heif,.hwp,.hwpx,.docx,.xlsx,.xls"
                        onChange={handleAnswerFilesUpload}
                        className="scoring-file-input-hidden"
                      />
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                      </svg>
                      파일 첨부{!isPro && ' PRO'}
                    </label>
                    <FieldHelpTooltip title={SCORING_HELP.answerFile.title} items={SCORING_HELP.answerFile.items} ariaLabel="학생 과제 파일 안내" variant="compact" />
                    {isDragging && (
                      <span className="scoring-drag-hint">여기에 파일을 놓으세요</span>
                    )}
                  </div>

                  {!answerText.trim() && answerFiles.length === 0 && rubricText.trim() && (
                    <button
                      className="scoring-try-example scoring-try-example-answer"
                      onClick={() => {
                        setAnswerText(EXAMPLE_STUDENT_ANSWER);
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                      </svg>
                      예시 답안 넣기
                    </button>
                  )}

                  {answerFiles.length > 0 && (
                    <>
                      <div className="scoring-file-list">
                        {answerFiles.map((file, index) => (
                          <div key={index} className="scoring-file-item">
                            <span className="scoring-file-item-name">{file.name}</span>
                            <button onClick={() => handleAnswerFileRemove(index)} className="scoring-file-item-remove">✕</button>
                          </div>
                        ))}
                      </div>

                    </>
                  )}
                </div>
              )}

              {/* Section 2B: 일괄 채점 모드 */}
              {hasRubricInput && scoringMode === 'batch' && (
                <div className="scoring-section scoring-section-sequential">

                  {/* 모드 선택 (아직 선택하지 않은 경우) */}
                  {!batchUploadMode && (
                    <>
                      <h2 className="scoring-section-title">
                        <ScoringHelpLabel help={SCORING_HELP.batchMode}>학생 과제는 어떻게 모을까요?</ScoringHelpLabel>
                      </h2>
                      <div className="scoring-batch-mode-cards">
                        <button
                          className="scoring-batch-mode-card"
                          onClick={() => {
                            resetDirectBatchUpload();
                            setBatchUploadMode('teacher');
                          }}
                        >
                          <div className="scoring-batch-mode-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="17 8 12 3 7 8" />
                              <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                          </div>
                          <span className="scoring-batch-mode-title">내가 직접 올리기</span>
                          <span className="scoring-batch-mode-desc">파일을 받아서 한꺼번에 업로드</span>
                        </button>
                        <button
                          className="scoring-batch-mode-card scoring-batch-mode-card--highlight"
                          onClick={() => {
                            if (!isPro) { setShowProRequiredModal(true); return; }
                            resetDirectBatchUpload();
                            if (activeRooms.length > 0) {
                              setBatchUploadMode('student-rooms');
                            } else {
                              setBatchUploadMode('student-setup');
                            }
                          }}
                        >
                          <div className="scoring-batch-mode-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                            </svg>
                          </div>
                          <span className="scoring-batch-mode-title">학생이 직접 올리기</span>
                          <span className="scoring-batch-mode-desc">QR코드로 사진 촬영 + 제출</span>
                          {!isPro && <span className="scoring-pro-badge">PRO</span>}
                        </button>
                      </div>
                    </>
                  )}

                  {/* 모드: 기존 제출방 목록 */}
                  {batchUploadMode === 'student-rooms' && (
                    <div className="scoring-room-list-view">
                      <div className="scoring-batch-mode-back">
                        <button
                          onClick={() => {
                            resetDirectBatchUpload();
                            setBatchUploadMode(null);
                          }}
                          className="scoring-batch-mode-back-btn"
                        >
                          ← 다른 방법 선택
                        </button>
                      </div>
                      <h2 className="scoring-section-title">제출방 선택</h2>
                      <p className="scoring-room-setup-desc">이전에 만든 제출방을 이어서 사용하거나, 새로 만들 수 있어요</p>

                      <div className="scoring-room-list">
                        {activeRooms.map(room => (
                          <div key={room.room_id} className="scoring-room-list-item">
                            {renamingRoomId === room.room_id ? (
                              // 이름 인라인 수정 (window.prompt 대체)
                              <form
                                className="scoring-room-list-main"
                                style={{ gap: 10, cursor: 'default' }}
                                onSubmit={async (e) => {
                                  e.preventDefault();
                                  const nextTitle = renameRoomDraft.trim();
                                  if (!nextTitle || nextTitle === room.title) {
                                    setRenamingRoomId(null);
                                    return;
                                  }
                                  try {
                                    const token = await currentUser.getIdToken();
                                    const fd = new FormData();
                                    fd.append('title', nextTitle);
                                    const res = await fetch(getApiUrl(`/api/scoring/room/${room.room_id}/rename`), {
                                      method: 'POST',
                                      headers: { Authorization: `Bearer ${token}` },
                                      body: fd,
                                    });
                                    if (!res.ok) throw new Error('rename failed');
                                    const data = await res.json();
                                    setActiveRooms(prev => prev.map(r =>
                                      r.room_id === room.room_id ? { ...r, title: data.title } : r
                                    ));
                                    setRenamingRoomId(null);
                                    showToast('제출방 이름을 바꿨어요', 'success');
                                  } catch {
                                    showToast('이름을 바꾸지 못했어요. 다시 시도해주세요', 'error');
                                  }
                                }}
                              >
                                <input
                                  className="scoring-room-title-input"
                                  style={{ padding: '10px 12px', fontSize: 14 }}
                                  type="text"
                                  value={renameRoomDraft}
                                  maxLength={60}
                                  autoFocus
                                  aria-label="제출방 이름"
                                  placeholder="제출방 이름"
                                  onChange={(e) => setRenameRoomDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      setRenamingRoomId(null);
                                    }
                                  }}
                                />
                                <div className="scoring-room-list-actions" style={{ borderLeft: 'none' }}>
                                  <button
                                    type="submit"
                                    className="scoring-room-list-action-text scoring-room-list-rename"
                                    disabled={!renameRoomDraft.trim()}
                                  >
                                    저장
                                  </button>
                                  <button
                                    type="button"
                                    className="scoring-room-list-action-text"
                                    style={{ color: '#6b7684', background: '#f2f4f6' }}
                                    onClick={() => setRenamingRoomId(null)}
                                  >
                                    취소
                                  </button>
                                </div>
                              </form>
                            ) : (
                              <>
                                <button
                                  className="scoring-room-list-main"
                                  onClick={() => handleLoadRoomForScoring(room)}
                                >
                                  <div className="scoring-room-list-info">
                                    <span className="scoring-room-list-title">{room.title}</span>
                                    <span className="scoring-room-list-meta">{room.submission_count}명 제출 · {room.code}</span>
                                  </div>
                                  <span className="scoring-room-list-chevron">›</span>
                                </button>
                                <div className="scoring-room-list-actions">
                                  <button
                                    className="scoring-room-list-action-text scoring-room-list-rename"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRenameRoomDraft(room.title || '');
                                      setRenamingRoomId(room.room_id);
                                    }}
                                  >
                                    수정
                                  </button>
                                  <button
                                    className="scoring-room-list-action-text scoring-room-list-remove"
                                    onClick={(e) => handleDeleteRoom(room.room_id, e)}
                                  >
                                    삭제
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>

                      <button
                        className="scoring-room-create-new-btn"
                        onClick={() => setBatchUploadMode('student-setup')}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        새 제출방 만들기
                      </button>
                    </div>
                  )}

                  {/* 모드: 세션 이름 설정 */}
                  {batchUploadMode === 'student-setup' && (
                    <div className="scoring-room-setup">
                      <div className="scoring-batch-mode-back">
                        <button onClick={() => {
                          if (activeRooms.length > 0) {
                            setBatchUploadMode('student-rooms');
                          } else {
                            resetDirectBatchUpload();
                            setBatchUploadMode(null);
                          }
                        }} className="scoring-batch-mode-back-btn">
                          ← {activeRooms.length > 0 ? '제출방 목록' : '다른 방법 선택'}
                        </button>
                      </div>
                      <h2 className="scoring-section-title">제출방 이름을 정해주세요</h2>
                      <p className="scoring-room-setup-desc">반 이름이나 과제명을 입력하면 나중에 쉽게 구분할 수 있어요</p>
                      <input
                        type="text"
                        className="scoring-room-title-input"
                        placeholder={activeRubric?.subject || selectedRubric?.subject || '예: 3반 수행평가'}
                        value={roomTitle}
                        onChange={e => setRoomTitle(e.target.value)}
                        maxLength={30}
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            setBatchUploadMode('student');
                            createSubmissionRoom();
                          }
                        }}
                      />
                      <button
                        className="scoring-room-setup-btn"
                        onClick={() => {
                          setBatchUploadMode('student');
                          createSubmissionRoom();
                        }}
                        disabled={roomCreating}
                      >
                        {roomCreating ? '만드는 중...' : '제출방 만들기'}
                      </button>
                    </div>
                  )}

                  {/* 모드: 내가 직접 올리기 (기존 배치 업로드) */}
                  {batchUploadMode === 'teacher' && (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleBatchFileDrop}
                    >
                      <div className="scoring-batch-mode-back">
                        <button
                          onClick={() => {
                            setBatchUploadMode(null);
                            resetDirectBatchUpload();
                          }}
                          className="scoring-batch-mode-back-btn"
                        >
                          ← 다른 방법 선택
                        </button>
                      </div>

                      {!batchAnswerStructure && !scanPdfDetected && batchStudents.length === 0 && (
                        <div className="scoring-batch-mode-guide" role="group" aria-label="일괄 채점 업로드 방식 안내">
                          <button
                            type="button"
                            className="scoring-batch-mode-guide-card scoring-batch-mode-guide-card--selectable"
                            onClick={() => handleBatchStructureSelect('studentFiles')}
                          >
                            <div className="scoring-batch-mode-guide-header">
                              <span className="scoring-batch-mode-guide-badge">방법 A</span>
                              <span className="scoring-batch-mode-guide-title">학생별 파일</span>
                            </div>
                            <BatchModeExample type="studentFiles" />
                            <p className="scoring-batch-mode-guide-desc">학생마다 파일이 따로 있을 때 사용해요.</p>
                          </button>
                          <button
                            type="button"
                            className="scoring-batch-mode-guide-card scoring-batch-mode-guide-card--selectable"
                            onClick={() => handleBatchStructureSelect('scanPdf')}
                          >
                            <div className="scoring-batch-mode-guide-header">
                              <span className="scoring-batch-mode-guide-badge">방법 B</span>
                              <span className="scoring-batch-mode-guide-title">스캔 PDF 한 파일</span>
                            </div>
                            <BatchModeExample type="scanPdf" />
                            <p className="scoring-batch-mode-guide-desc">여러 학생 답안지가 한 PDF에 이어져 있을 때 좋아요.</p>
                          </button>
                          <button
                            type="button"
                            className="scoring-batch-mode-guide-card scoring-batch-mode-guide-card--selectable"
                            onClick={() => handleBatchStructureSelect('singleFileStudents')}
                          >
                            <div className="scoring-batch-mode-guide-header">
                              <span className="scoring-batch-mode-guide-badge">방법 C</span>
                              <span className="scoring-batch-mode-guide-title">한 파일에 여러 답안</span>
                            </div>
                            <BatchModeExample type="singleFileStudents" />
                            <p className="scoring-batch-mode-guide-desc">한 파일 안에 여러 학생 답안이 섞여 있을 때 사용해요.</p>
                          </button>
                        </div>
                      )}

                      {batchAnswerStructure === 'studentFiles' && !scanPdfDetected && (
                        <>
                          <h2 className="scoring-section-title">
                            <ScoringHelpLabel help={SCORING_HELP.batchStudentFiles}>학생 답안 파일 ({batchStudents.length}/{BATCH_STUDENT_FILE_MAX})</ScoringHelpLabel>
                          </h2>
                          <div className="scoring-mode-inline-guide">
                            <BatchModeExample type="studentFiles" />
                            <div>
                              <strong>학생마다 파일이 따로 있을 때</strong>
                              <p>최대 {BATCH_STUDENT_FILE_MAX}명까지 올릴 수 있고, 학생별 PDF/PPT는 80페이지·80슬라이드까지 읽어요.</p>
                            </div>
                          </div>

                          <div className={`scoring-batch-dropzone ${isDragging ? 'dragging' : ''}`}>
                            <label className="scoring-dropzone-label">
                              <input
                                type="file"
                                multiple
                                accept=".pdf,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.heic,.heif,.hwp,.hwpx,.docx,.xlsx,.xls"
                                onChange={handleBatchFilesUpload}
                                className="scoring-file-input-hidden"
                              />
                              <div className="scoring-dropzone-content-compact">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="17 8 12 3 7 8"></polyline>
                                  <line x1="12" y1="3" x2="12" y2="15"></line>
                                </svg>
                                <span className="scoring-dropzone-text-compact">답안 파일을 드래그하거나 클릭하여 선택</span>
                                <span className="scoring-dropzone-hint-compact">학생별 파일 최대 {BATCH_STUDENT_FILE_MAX}개</span>
                              </div>
                            </label>
                          </div>
                        </>
                      )}

                      {batchAnswerStructure === 'scanPdf' && !scanPdfDetected && (
                        <>
                          <h2 className="scoring-section-title">
                            <ScoringHelpLabel help={SCORING_HELP.scanPdf}>한 PDF로 스캔된 답안</ScoringHelpLabel>
                          </h2>
                          <div className="scoring-mode-inline-guide">
                            <BatchModeExample type="scanPdf" />
                            <div>
                              <strong>한 PDF에 여러 학생 답안지가 이어져 있을 때</strong>
                              <p>최대 80페이지까지 올릴 수 있어요. AI가 먼저 페이지 범위를 나누고, 선생님이 마지막으로 확인해요.</p>
                            </div>
                          </div>

                          <div className={`scoring-batch-dropzone ${isDragging ? 'dragging' : ''}`}>
                            <label className="scoring-dropzone-label">
                              <input
                                type="file"
                                accept=".pdf"
                                onChange={handleBatchFilesUpload}
                                className="scoring-file-input-hidden"
                              />
                              <div className="scoring-dropzone-content-compact">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                  <polyline points="14 2 14 8 20 8" />
                                  <line x1="12" y1="11" x2="12" y2="17" />
                                  <polyline points="9 14 12 17 15 14" />
                                </svg>
                                <span className="scoring-dropzone-text-compact">스캔 PDF 1개를 올려주세요</span>
                                <span className="scoring-dropzone-hint-compact">2~80페이지, AI 범위 확인 후 채점</span>
                              </div>
                            </label>
                          </div>
                        </>
                      )}

                      {batchAnswerStructure === 'singleFileStudents' && !splitPreview && (
                        <>
                          <h2 className="scoring-section-title">AI로 학생 답안 나누기</h2>
                          <div className="scoring-mode-inline-guide">
                            <BatchModeExample type="singleFileStudents" />
                            <div>
                              <strong>한 파일 안에 여러 답안이 섞여 있을 때</strong>
                              <p>PDF/PPT는 80페이지·80슬라이드까지 읽어요. 손글씨 스캔 PDF라면 방법 B가 더 정확해요.</p>
                            </div>
                          </div>

                          <div className={`scoring-batch-dropzone ${isDragging ? 'dragging' : ''}`}>
                            {splitPreviewLoading ? (
                              <div className="scoring-split-loading" role="status" aria-live="polite">
                                <div className="scoring-split-loading-icon">
                                  <span className="scoring-file-checking-spinner" />
                                </div>
                                <strong>{splitPreviewMessage || 'AI가 학생별 답안을 나누고 있어요'}</strong>
                                <span>{splitPreviewFile?.name || '파일 분석 중'}</span>
                                <div className="scoring-split-loading-progress">
                                  <div
                                    className="scoring-split-loading-progress-bar"
                                    style={{ width: `${splitPreviewProgress}%` }}
                                  />
                                </div>
                                {/* 실측 /lab/split-student-answers-preview N=14 p50 42초·p75 164초·60초 초과 43% */}
                                <p>보통 1분 안에 끝나요. 파일이 크거나 이미지가 많으면 3분까지 걸릴 수 있어요.</p>
                              </div>
                            ) : (
                              <label className="scoring-dropzone-label">
                                <input
                                  type="file"
                                  accept=".pdf,.xlsx,.xls,.docx,.hwp,.hwpx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.webp,.heic,.heif"
                                  onChange={handleSplitFileUpload}
                                  className="scoring-file-input-hidden"
                                />
                                <div className="scoring-dropzone-content-compact">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <path d="M8 13h8" />
                                    <path d="M8 17h5" />
                                  </svg>
                                  <span className="scoring-dropzone-text-compact">여러 답안이 들어있는 파일을 올려주세요</span>
                                  <span className="scoring-dropzone-hint-compact">PDF, 이미지, Excel, 한글, PPT, 텍스트 지원</span>
                                </div>
                              </label>
                            )}
                          </div>
                        </>
                      )}

                      {scanPdfDetected && (
                        <div className="scoring-scan-pdf-detected">
                          <div className="scoring-scan-pdf-card">
                            <div className="scoring-scan-pdf-icon">
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                              </svg>
                            </div>
                            <div className="scoring-scan-pdf-info">
                              <span className="scoring-scan-pdf-name">{scanPdfFile?.name}</span>
                              <span className="scoring-scan-pdf-pages">
                                {scanPdfPageCount}페이지 스캔 PDF 감지
                                {scanPageRanges.length > 0 && (
                                  <span className="scoring-scan-pdf-estimate"> · {scanPageRanges.length}명으로 나눴어요</span>
                                )}
                              </span>
                              <span className="scoring-scan-pdf-pages">
                                AI가 먼저 학생별 페이지 범위를 추천하고, 선생님이 확인한 뒤 채점해요.
                              </span>
                            </div>
                            <button
                              onClick={() => {
                                setScanPdfDetected(false);
                                setScanPdfFile(null);
                                setScanPdfPageCount(0);
                                setScanRangePreview(null);
                                setScanPageRanges([]);
                                setScanRangeError('');
                              }}
                              style={{ flexShrink: 0, width: 32, height: 32, minWidth: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: '#e2ecf8', borderRadius: 8, cursor: 'pointer', padding: 0 }}
                            >
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M11 3L3 11M3 3l8 8" stroke="#6b7684" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </button>
                          </div>
                          <p className="scoring-scan-pdf-desc">
                            페이지 범위가 맞는지 확인해 주세요. 예: 1-2페이지는 학생 1, 3-4페이지는 학생 2처럼 묶어 채점해요.
                          </p>
                          <div className="scoring-split-preview-card scoring-scan-range-card" style={{ marginTop: 12 }}>
                            <div className="scoring-split-preview-head">
                              <div className="scoring-split-preview-title-stack">
                                <span className="scoring-split-preview-kicker">스캔 PDF 학생 범위</span>
                                <strong>
                                  {scanRangeLoading
                                    ? 'AI가 학생별 페이지 범위를 찾고 있어요'
                                    : scanPageRanges.length > 0
                                      ? `${scanPageRanges.length}명 범위로 나눴어요`
                                      : '학생 범위를 확인해 주세요'}
                                </strong>
                                <p>
                                  {scanRangeLoading
                                    ? '학생 이름, 번호, 양식 반복을 보고 범위를 추천하는 중이에요.'
                                    : '틀린 범위는 바로 수정할 수 있어요.'}
                                </p>
                              </div>
                            </div>

                            {scanRangeLoading && (
                              <div className="scoring-split-loading" role="status" aria-live="polite">
                                <div className="scoring-split-loading-icon">
                                  <span className="scoring-file-checking-spinner" />
                                </div>
                                <strong>AI가 페이지 경계를 찾고 있어요</strong>
                                <span>{scanPdfFile?.name}</span>
                                {/* 실측 /lab/split-scan-pdf-pages-preview N=71 p50 9.5초·p90 18.8초·30초 초과 1% */}
                                <p>보통 10~20초 걸려요.</p>
                              </div>
                            )}

                            {!scanRangeLoading && (
                              <>
                                {scanRangeError && (
                                  <p className="scoring-scan-pdf-desc" style={{ color: '#d97706' }}>
                                    {scanRangeError}
                                  </p>
                                )}
                                <ScoringRosterMatch items={scanRosterItems} onRename={handleScanRosterRename} />
                                <div className="scoring-split-review-list">
                                  {scanPageRanges.map((range, index) => (
                                    <div key={`${range.start_page}-${range.end_page}-${index}`} className="scoring-split-review-card">
                                      <div className="scoring-split-review-summary-row">
                                        <div className="scoring-split-review-summary-main">
                                          <span className="scoring-split-review-number">{index + 1}</span>
                                          <div>
                                            <strong>{range.student_name || placeholderStudentLabel(index)}</strong>
                                            <span>{`${range.start_page || '?'}-${range.end_page || '?'}`}쪽</span>
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          className="scoring-split-review-remove"
                                          onClick={() => handleScanRangeRemove(index)}
                                          aria-label={`${index + 1}번 범위 삭제`}
                                        >
                                          ✕
                                        </button>
                                      </div>
                                      <div className="scoring-split-review-edit-area">
                                        <label className="scoring-split-review-name-label">
                                          학생 이름
                                          <input
                                            className="scoring-split-review-name-input"
                                            value={range.student_name}
                                            placeholder={placeholderStudentLabel(index)}
                                            onChange={(e) => handleScanRangeChange(index, 'student_name', e.target.value)}
                                          />
                                        </label>
                                        <label className="scoring-split-review-name-label">
                                          시작 페이지
                                          <input
                                            className="scoring-split-review-name-input"
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            min="1"
                                            max={scanPdfPageCount}
                                            value={range.start_page}
                                            onChange={(e) => handleScanRangeChange(index, 'start_page', e.target.value)}
                                          />
                                        </label>
                                        <label className="scoring-split-review-name-label">
                                          끝 페이지
                                          <input
                                            className="scoring-split-review-name-input"
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            min="1"
                                            max={scanPdfPageCount}
                                            value={range.end_page}
                                            onChange={(e) => handleScanRangeChange(index, 'end_page', e.target.value)}
                                          />
                                        </label>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                          {batchAnswerStructure !== 'scanPdf' && (
                            <button
                              className="scoring-scan-pdf-dismiss"
                              onClick={handleScanPdfDismiss}
                            >
                              아니요, 1명의 답안이에요
                            </button>
                          )}
                        </div>
                      )}

                      {batchAnswerStructure === 'singleFileStudents' && splitPreview && (
                        <div className="scoring-split-preview-card">
                          <div className="scoring-split-preview-head">
                            <div className="scoring-split-preview-title-stack">
                              <span className="scoring-split-preview-kicker">방법 C 결과</span>
                              <strong>{batchStudents.length}명으로 나눴어요</strong>
                              <p>아래에서 이름과 답안 내용을 확인한 뒤 채점해 주세요.</p>
                            </div>
                            <button
                              type="button"
                              className="scoring-split-preview-reset"
                              onClick={() => {
                                setSplitPreview(null);
                                setSplitPreviewFile(null);
                                setBatchStudents([]);
                                setSplitPreviewProgress(0);
                                setSplitPreviewMessage('');
                              }}
                            >
                              다시 올리기
                            </button>
                          </div>

                          <div className="scoring-split-preview-summary">
                            <div className="scoring-split-preview-metric">
                              <span>파일</span>
                              <strong>{splitPreviewFile?.name || splitPreview.fileName}</strong>
                            </div>
                            <div className="scoring-split-preview-metric">
                              <span>페이지</span>
                              <strong>{splitPreview.page_count || '-'}쪽</strong>
                            </div>
                            <div className="scoring-split-preview-metric">
                              <span>감지한 학생</span>
                              <strong>{batchStudents.length}명</strong>
                            </div>
                          </div>

                          {splitDroppedPages.length > 0 && (
                            <div className="scoring-split-dropped-pages" role="status">
                              <div className="scoring-split-dropped-pages-text">
                                <strong>{formatPageList(splitDroppedPages)}쪽은 지금 채점되지 않아요</strong>
                                <p>방금 지운 칸에 있던 답안이에요. 실수로 지웠다면 다시 넣어 주세요.</p>
                              </div>
                              {(splitPreview.removed_students || []).length > 0 && (
                                <button
                                  type="button"
                                  className="scoring-split-dropped-pages-restore"
                                  onClick={handleRestoreRemovedSplitStudents}
                                >
                                  지운 칸 다시 넣기
                                </button>
                              )}
                            </div>
                          )}

                          {splitUnassignedPages.length > 0 && (
                            <p className="scoring-split-unassigned-pages">
                              {splitUnassignedPagesAreCover
                                ? `${formatPageList(splitUnassignedPages)}쪽은 표지로 보고 빼고 채점해요.`
                                : `${formatPageList(splitUnassignedPages)}쪽은 답안이 없는 쪽으로 보고 빼고 채점해요. 학생 답안이 있다면 다시 올려 주세요.`}
                            </p>
                          )}
                        </div>
                      )}

                      {batchStudents.length > 0 && batchAnswerStructure === 'singleFileStudents' && splitPreview ? (
                        <div className="scoring-split-review-panel">
                          <div className="scoring-split-review-head">
                            <div>
                              <span className="scoring-split-preview-kicker">확인 목록</span>
                              <strong>이름과 답안을 한눈에 확인하세요</strong>
                            </div>
                            <div className="scoring-split-review-head-actions">
                              <p>한 학생 답안이 두 칸으로 나뉘었다면 아래 칸에서 &lsquo;위와 같은 학생&rsquo;을 눌러 주세요.</p>
                              <button
                                type="button"
                                onClick={handleDownloadSplitRecognition}
                              >
                                인식 답안 엑셀로 받기
                              </button>
                            </div>
                          </div>

                          <ScoringRosterMatch items={batchRosterItems} onRename={handleBatchRosterRename} />

                          <StudentRecognitionReviewList
                            students={batchStudents}
                            getContent={student => student.answer_text || ''}
                            getMetaItems={student => (
                              student.source_pages?.length
                                ? [`${formatPageList(student.source_pages)}쪽`]
                                : ['쪽 정보 없음']
                            )}
                            getKey={(student, index) => student.split_student_id || index}
                            onNameChange={handleBatchStudentNameChange}
                            onContentChange={handleBatchStudentAnswerChange}
                            onRemove={handleBatchStudentRemoveWithConfirm}
                            onMergePrevious={handleBatchStudentMergePrevious}
                            contentLabel="AI가 읽은 답안 원문"
                            contentPlaceholder="AI가 읽은 답안을 확인하고 잘못된 부분을 바로 수정해주세요."
                            rows={5}
                            ariaLabel="채점 전 학생별 인식 답안 확인"
                          />
                        </div>
                      ) : batchStudents.length > 0 ? (
                        <>
                        <ScoringRosterMatch items={batchRosterItems} onRename={handleBatchRosterRename} />
                        <div className="scoring-batch-student-list">
                          <div className="scoring-batch-list-header">
                            <span>학생명</span>
                            <span>파일명</span>
                            <span></span>
                          </div>
                          {batchStudents.map((student, index) => (
                            <div key={index} className="scoring-batch-student-row">
                              <input
                                className="scoring-batch-student-name-input"
                                value={student.name}
                                placeholder={placeholderStudentLabel(index)}
                                onChange={(e) => handleBatchStudentNameChange(index, e.target.value)}
                              />
                              <span className="scoring-batch-student-filename">{student.filename}</span>
                              <button
                                className="scoring-file-item-remove"
                                onClick={() => handleBatchStudentRemove(index)}
                              >✕</button>
                            </div>
                          ))}
                        </div>
                        </>
                      ) : null}
                    </div>
                  )}

                  {/* 모드: 학생이 직접 올리기 (제출방) */}
                  {batchUploadMode === 'student' && (
                    <div className="scoring-room-container">
                      <div className="scoring-batch-mode-back">
                        <button onClick={() => {
                          setBatchUploadMode(null);
                          setSubmissionRoom(null);
                          setRoomSubmissions([]);
                          setRoomRosterStatus(null);
                          resetDirectBatchUpload();
                          if (roomPollRef.current) clearInterval(roomPollRef.current);
                        }} className="scoring-batch-mode-back-btn">
                          ← 다른 방법 선택
                        </button>
                      </div>

                      {roomCreating ? (
                        <div className="scoring-room-creating">
                          <div className="submit-loading-spinner" style={{ width: 24, height: 24 }} />
                          <span>제출방 만드는 중...</span>
                        </div>
                      ) : submissionRoom ? (
                        <>
                          <div className="scoring-room-qr-section">
                            <div
                              className="scoring-room-qr-card"
                              onClick={() => setShowQrFullscreen(true)}
                            >
                              <QRCodeSVG
                                value={getSubmitUrl()}
                                size={140}
                                level="M"
                                bgColor="#ffffff"
                                fgColor="#191f28"
                              />
                            </div>

                            <div className="scoring-room-quick-actions">
                              <button className="scoring-room-quick-btn scoring-room-quick-btn--primary" onClick={() => setShowQrFullscreen(true)}>
                                QR 크게 보기
                              </button>
                              <button className="scoring-room-quick-btn" onClick={handleCopyLink}>
                                링크 복사
                              </button>
                              <button
                                className="scoring-room-quick-btn scoring-room-quick-btn--close"
                                onClick={async () => {
                                  try {
                                    const token = await currentUser.getIdToken();
                                    const res = await fetch(getApiUrl(`/api/scoring/room/${submissionRoom.room_id}/close`), {
                                      method: 'POST',
                                      headers: { Authorization: `Bearer ${token}` },
                                    });
                                    if (res.ok) {
                                      showToast('제출을 마감했어요');
                                      setSubmissionRoom(prev => ({ ...prev, status: 'closed' }));
                                    } else {
                                      showToast('마감에 실패했어요. 다시 시도해 주세요');
                                    }
                                  } catch { showToast('마감에 실패했어요'); }
                                }}
                              >
                                마감하기
                              </button>
                            </div>
                          </div>

                          <RoomRosterStatus key={submissionRoom.room_id} status={roomRosterStatus} onLink={handleLinkRoomRoster} />

                          <div className="scoring-room-submissions-section">
                            <div className="scoring-room-submissions-header">
                              <h3>제출 현황 ({roomSubmissions.length}명)</h3>
                              <span className="scoring-room-live-badge">실시간</span>
                            </div>

                            {roomSubmissions.length === 0 ? (
                              <div className="scoring-room-empty">
                                <span>아직 제출한 학생이 없어요</span>
                                <span className="scoring-room-empty-sub">QR코드를 학생들에게 보여주세요</span>
                              </div>
                            ) : (
                              <div className="scoring-room-submissions-list">
                                {roomSubmissions.map((sub, idx) => (
                                  <div
                                    key={sub.submission_id || idx}
                                    className="scoring-room-submission-row scoring-room-submission-row--clickable"
                                    onClick={() => handlePreviewSubmission(sub)}
                                  >
                                    <span className="scoring-room-submission-num">{idx + 1}</span>
                                    <span className="scoring-room-submission-name">{sub.student_name}</span>
                                    <span className="scoring-room-submission-filename">{sub.filename}</span>
                                    <button
                                      className="scoring-room-submission-delete"
                                      title="제출물 삭제"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const ok = await confirmDialog({
                                          title: `${sub.student_name} 학생의 제출물을 삭제할까요?`,
                                          description: '삭제한 파일은 되돌릴 수 없어요',
                                          confirmLabel: '삭제하기',
                                          danger: true,
                                        });
                                        if (!ok) return;
                                        try {
                                          const token = await currentUser.getIdToken();
                                          const res = await fetch(
                                            getApiUrl(`/api/scoring/room/${submissionRoom.room_id}/submissions/${sub.submission_id}`),
                                            { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
                                          );
                                          if (!res.ok) throw new Error('delete failed');
                                          setRoomSubmissions(prev => prev.filter(s => s.submission_id !== sub.submission_id));
                                          showToast(`${sub.student_name} 학생의 제출물을 삭제했어요`, 'success');
                                        } catch { showToast('제출물을 삭제하지 못했어요. 다시 시도해주세요', 'error'); }
                                      }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                      </svg>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="scoring-room-empty">
                          <span>제출방을 만들 수 없어요</span>
                          <button
                            className="submit-btn submit-btn--secondary"
                            onClick={() => {
                              resetDirectBatchUpload();
                              setBatchUploadMode(null);
                            }}
                          >
                            돌아가기
                          </button>
                        </div>
                      )}

                      {/* QR 전체화면 모달 */}
                      {showQrFullscreen && submissionRoom && (
                        <div className="scoring-qr-fullscreen-overlay" onClick={() => setShowQrFullscreen(false)}>
                          <div className="scoring-qr-fullscreen-content" onClick={(e) => e.stopPropagation()}>
                            <div className="scoring-qr-fullscreen-title">{submissionRoom.title || '과제 제출'}</div>
                            <QRCodeSVG
                              value={getSubmitUrl()}
                              size={Math.min(window.innerWidth * 0.7, 400)}
                              level="M"
                              bgColor="#ffffff"
                              fgColor="#191f28"
                            />
                            <p className="scoring-qr-fullscreen-desc">QR을 스캔해서 과제를 제출하세요</p>
                            <button className="scoring-qr-fullscreen-close" onClick={() => setShowQrFullscreen(false)}>
                              닫기
                            </button>
                          </div>
                        </div>
                      )}

                      {/* 제출물 미리보기 모달은 단일/일괄/제출방 모든 모드에서 사용하므로
                          컴포넌트 최상위(return </>) 직전으로 이동시켰음. */}
                    </div>
                  )}
                </div>
              )}

              {hasRubricInput && shouldShowScoringCalibration && renderScoringCalibrationPanel()}

              {/* 채점 시작 버튼 */}
              <div className="scoring-submit-area">
                {scoringMode === 'single' ? (
                  <>
                    <button
                      onClick={handleEvaluationSubmit}
                      disabled={isLoading || !hasRubricInput || (answerFiles.length === 0 && !answerText.trim()) || rubricFileUploading || preCheckLoading}
                      className="scoring-submit-button"
                    >
                      {isMultiQuestionScoring
                        ? `${getActiveQuestionSet()?.question_count || 0}개 문항 채점 시작`
                        : answerFiles.length > 1
                          ? '한 학생으로 채점 시작'
                          : '채점 시작'}
                    </button>
                    {!isLoading && !rubricFileUploading && !preCheckLoading && (
                      <p className="scoring-submit-hint">
                        {!hasRubricInput
                          ? '채점 기준서를 입력하면 시작할 수 있어요'
                          : (answerFiles.length === 0 && !answerText.trim())
                            ? '학생 과제를 입력하면 시작할 수 있어요'
                            : answerFiles.length > 1
                              ? '이대로 시작하면 1명 결과로 나와요'
                              : '1명 결과로 나와요'}
                      </p>
                    )}
                  </>
                ) : batchUploadMode === 'student' ? (
                  <>
                    <button
                      onClick={handleRoomScoring}
                      disabled={roomScoringLoading || batchLoading || roomSubmissions.length === 0 || !hasRubricInput}
                      className="scoring-submit-button"
                    >
                      {roomScoringLoading
                        ? '파일 준비 중...'
                        : roomSubmissions.length > 0
                          ? `${roomSubmissions.length}명 채점 시작`
                          : '제출을 기다리는 중...'}
                    </button>
                    {!roomScoringLoading && !batchLoading && (
                      <p className="scoring-submit-hint">
                        {roomSubmissions.length === 0
                          ? 'QR코드를 학생들에게 보여주세요'
                          : `학생들이 모두 제출하면 채점을 시작하세요`}
                      </p>
                    )}
                  </>
                ) : batchUploadMode === 'teacher' && scanPdfDetected ? (
                  <>
                    <button
                      onClick={handleScanPdfSubmit}
                      disabled={scanPdfLoading || scanRangeLoading || scanPageRanges.length === 0 || !hasRubricInput || rubricFileUploading}
                      className="scoring-submit-button"
                    >
                      {scanPdfLoading ? '채점 중...' : scanRangeLoading ? '학생 범위 확인 중...' : '확인한 범위로 채점 시작'}
                    </button>
                    {!scanPdfLoading && !rubricFileUploading && (
                      <p className="scoring-submit-hint">
                        {!hasRubricInput
                          ? '채점 기준서를 입력하면 시작할 수 있어요'
                          : scanRangeLoading
                            ? 'AI가 학생별 페이지 범위를 나누고 있어요'
                            : scanPageRanges.length > 0
                              ? `${scanPageRanges.length}명 결과로 나와요 · 페이지 범위를 확인해 주세요`
                              : '학생별 페이지 범위를 먼저 확인해 주세요'}
                      </p>
                    )}
                  </>
                ) : batchUploadMode === 'teacher' ? (
                  <>
                    <button
                      onClick={handleBatchSubmit}
                      disabled={batchLoading || !hasRubricInput || batchStudents.length === 0 || rubricFileUploading}
                      className="scoring-submit-button"
                    >
                      {batchStudents.length > 0 ? `${batchStudents.length}명 일괄 채점 시작` : '일괄 채점 시작'}
                    </button>
                    {!batchLoading && !rubricFileUploading && (
                      <p className="scoring-submit-hint">
                        {!hasRubricInput
                          ? '채점 기준서를 입력하면 시작할 수 있어요'
                          : batchStudents.length === 0
                            ? (batchAnswerStructure === 'singleFileStudents'
                              ? '한 파일을 올려 학생을 먼저 나눠주세요'
                              : '학생 답안 파일을 업로드하면 시작할 수 있어요')
                            : (() => {
                              const hasImages = batchStudents.some(s => hasImageFilename(s.filename || ''));
                              const estimate = estimateBatchScoringMinutes(batchStudents.length, hasImages, batchAnswerStructure === 'scanPdf');
                              return hasImages
                                ? `${batchStudents.length}명 결과로 나와요 · 약 ${estimate.min}~${estimate.max}분 소요 · 이미지는 안정적으로 2장씩 인식해요`
                                : `${batchStudents.length}명 결과로 나와요 · 약 ${estimate.min}~${estimate.max}분 소요`;
                            })()}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="scoring-submit-hint">
                    학생 과제를 모으는 방법을 선택해주세요
                  </p>
                )}
              </div>
            </>
          )}

          {/* ── 로딩 상태 ── */}
          {isLoading && (
            <div className="scoring-loading">
              <div className="scoring-loading-progress-container">
                <div
                  className="scoring-loading-progress-bar"
                  style={{ width: `${loadingProgress}%` }}
                />
              </div>
              <div className="scoring-loading-steps">
                {loadingSteps.map((step, i) => (
                  <div
                    key={i}
                    className={`scoring-loading-step ${i < completedSteps ? 'completed' : i === completedSteps ? 'active' : ''}`}
                  >
                    <span className="scoring-loading-step-icon">
                      {i < completedSteps ? '✓' : i === completedSteps ? '' : ''}
                    </span>
                    <span className="scoring-loading-step-text">{step}</span>
                  </div>
                ))}
              </div>
              {currentTip && (
                <p className="scoring-loading-tip">{currentTip}</p>
              )}
              <p className="scoring-loading-hint">
                보통 30초 안에 끝나요. 기준서가 길면 1분 정도 걸릴 수 있어요
              </p>
            </div>
          )}

          {/* ── 일괄 채점 로딩 ── */}
          {batchLoading && (
            <div className="scoring-loading">
              <div className="scoring-loading-progress-container">
                <div
                  className="scoring-loading-progress-bar"
                  style={{ width: `${batchProgress}%` }}
                />
              </div>
              <p className="scoring-batch-loading-text">
                {batchPhaseMessage
                  ? batchPhaseMessage
                  : scanPdfLoading
                    ? `왓퀴즈가 학생을 구분하고 채점하고 있어요 ${batchProgress}%`
                    : batchLiveCounts && batchLiveCounts.completed > 0
                      ? `${batchLiveCounts.total}명 중 ${batchLiveCounts.completed}명 채점 완료 · ${batchProgress}%`
                      : `${(batchUploadMode === 'student' ? roomSubmissions.length : batchStudents.length)}명 일괄 채점 중... ${batchProgress}%`
                }
              </p>
              {batchRetryCount > 0 && (
                <p className="scoring-batch-retry-notice">
                  네트워크 지연이 감지되어 자동 재시도 중... ({batchRetryCount}/{batchMaxRetries})
                </p>
              )}
              {currentTip && (
                <p className="scoring-loading-tip">{currentTip}</p>
              )}
              <p className="scoring-loading-hint">
                {(() => {
                  const count = batchUploadMode === 'student' ? roomSubmissions.length : batchStudents.length;
                  const hasImages = batchUploadMode === 'student'
                    || batchStudents.some(s => hasImageFilename(s.filename || ''));
                  const estimate = estimateBatchScoringMinutes(count, hasImages, batchAnswerStructure === 'scanPdf');
                  return hasImages
                    ? `약 ${estimate.min}~${estimate.max}분 소요 · 오류를 줄이기 위해 이미지를 2장씩 안정적으로 인식하고 있어요`
                    : `약 ${estimate.min}~${estimate.max}분 소요`;
                })()
                }
              </p>
              <p className="scoring-batch-stability-note">
                진행률이 잠시 천천히 올라가도 멈춘 것이 아니에요. 손글씨/이미지 답안은 인식 품질을 위해 한 장씩 차례로 확인해요.
              </p>
              {/* 로딩 중에도 지금까지 채점 완료된 학생을 바로 보여준다 (부분 결과 가시화) */}
              {batchResults.length > 0 && (
                <div className="scoring-batch-partial-results">
                  <p className="scoring-batch-partial-title">
                    채점이 끝난 학생 {batchResults.filter(r => !r.error).length}명
                  </p>
                  <div className="scoring-batch-partial-chips">
                    {batchResults.map((r, i) => (
                      <span
                        key={`${r.fileName || r.studentName || 'student'}-${i}`}
                        className={`scoring-batch-partial-chip${r.error ? ' scoring-batch-partial-chip--error' : ''}`}
                      >
                        {r.studentName || r.fileName || `학생${i + 1}`}
                        {!r.error && (() => {
                          const gradeOnly = isGradeOnlyResult(r, getActiveRubricCriteria());
                          return gradeOnly ? '' : ` · ${getResultTotalScore(r)}점`;
                        })()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── 일괄 채점 결과 ── */}
          {batchResults.length > 0 && !batchLoading && (
            <div
              ref={resultsTopRef}
              className={`scoring-results ${isResultInteractionLocked ? 'scoring-results--busy' : ''}`}
              aria-busy={isResultInteractionLocked}
            >
              {renderReScoreBusyOverlay()}
              <div className="scoring-results-header">
                <h2 className="scoring-results-title">일괄 채점 결과</h2>
                <span className="scoring-results-total-badge">
                  {batchResults.filter(r => !r.error).length}/{batchResults.length}명 완료
                </span>
              </div>

              {/* 실패한 청크가 있을 때 안내 + 재시도 버튼 */}
              {batchFailedChunks.length > 0 && (
                <div className="scoring-batch-failed-chunks-card" role="alert">
                  <div className="scoring-batch-failed-chunks-icon" aria-hidden="true">
                    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
                      <circle cx="12" cy="12" r="10" fill="#ffb020" />
                      <path d="M12 7v6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
                      <circle cx="12" cy="16.5" r="1.3" fill="#fff" />
                    </svg>
                  </div>
                  <div className="scoring-batch-failed-chunks-body">
                    <h3 className="scoring-batch-failed-chunks-title">
                      {batchFailedChunks.reduce((sum, e) => sum + (e.students?.length || 0), 0)}명 채점이 실패했어요
                    </h3>
                    <p className="scoring-batch-failed-chunks-message">
                      네트워크 또는 서버 일시적 오류로 일부 묶음이 실패했어요.
                      성공한 학생은 그대로 두고, 실패한 학생만 다시 채점할 수 있어요.
                    </p>
                    <div className="scoring-batch-failed-chunks-students">
                      {batchFailedChunks.flatMap(c => c.students || []).slice(0, 8).map((s, idx) => (
                        <span key={idx} className="scoring-batch-failed-chunk-chip">
                          {s.name || s.filename || '이름 없음'}
                        </span>
                      ))}
                      {batchFailedChunks.flatMap(c => c.students || []).length > 8 && (
                        <span className="scoring-batch-failed-chunk-chip">
                          +{batchFailedChunks.flatMap(c => c.students || []).length - 8}명
                        </span>
                      )}
                    </div>
                    <div className="scoring-batch-failed-chunks-actions">
                      <button
                        type="button"
                        className="scoring-batch-error-retry-btn"
                        onClick={handleRetryFailedChunks}
                      >
                        실패한 학생만 다시 채점
                      </button>
                      <button
                        type="button"
                        className="scoring-batch-error-dismiss-btn"
                        onClick={() => setBatchFailedChunks([])}
                      >
                        닫기
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* 적용된 채점 기준 — 배치도 동일하게 표시 */}
              {activeRubric && activeRubric.evaluation_items?.length > 0 && (
                <div className="scoring-applied-rubric">
                  <span className="scoring-applied-rubric-label">적용된 채점 기준</span>
                  <span className="scoring-applied-rubric-subject">
                    {activeRubric.subject || '채점 기준서'} · 총 {activeRubric.total_points || 0}점
                  </span>
                  <div className="scoring-applied-rubric-items">
                    {activeRubric.evaluation_items.map((item, idx) => (
                      <span key={idx} className="scoring-applied-rubric-chip">
                        {getRubricItemName(item)} <strong>{getRubricItemMaxScore(item)}점</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 기준서 원문 (접이식) */}
              {rubricText && (
                <div className="scoring-rubric-summary-card">
                  <button
                    className={`scoring-rubric-summary-toggle ${rubricSummaryOpen ? 'open' : ''}`}
                    onClick={() => setRubricSummaryOpen(prev => !prev)}
                  >
                    <div className="scoring-rubric-summary-left">
                      <span className="scoring-rubric-summary-icon" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M8 6h8" />
                          <path d="M8 10h8" />
                          <path d="M8 14h5" />
                          <path d="M6 2h9l5 5v15H6z" />
                          <path d="M15 2v5h5" />
                        </svg>
                      </span>
                      <span className="scoring-rubric-summary-label">기준서 원문 보기</span>
                    </div>
                    <svg
                      className="scoring-rubric-summary-arrow"
                      xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {rubricSummaryOpen && (
                    <div className="scoring-rubric-summary-content">
                      <div className="scoring-rubric-summary-text">{rubricText}</div>
                    </div>
                  )}
                </div>
              )}

              {/* 오류 요약 배너 */}
              {batchResults.some(r => r.error) && (
                <div className="scoring-error-summary">
                  <div className="scoring-error-summary-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span>{batchResults.length}명 중 {batchResults.filter(r => !r.error).length}명 채점 완료, {batchResults.filter(r => r.error).length}명 실패</span>
                  </div>
                  <div className="scoring-error-summary-list">
                    {batchResults.filter(r => r.error).map((r, idx) => (
                      <div key={idx} className="scoring-error-summary-item">
                        <span className="scoring-error-summary-name">{r.studentName || `학생 ${idx + 1}`}</span>
                        {r.fileName && <span className="scoring-error-summary-file">{r.fileName}</span>}
                        <span className="scoring-error-summary-reason">{r.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="scoring-review-launcher-head">
                <p className="scoring-review-launcher-title">학생별 결과</p>
                <p className="scoring-review-launcher-desc">학생 카드를 누르면 제출물과 채점 근거를 함께 볼 수 있어요.</p>
              </div>
              <ScoringReviewLauncher
                onSelect={(index) => setReviewModal({ source: 'batch', index })}
                rubric={activeRubric}
                results={batchResults}
                answerTexts={cachedAnswerTexts}
              />

              {!isResultInteractionLocked && (
                <RosterResultCard
                  names={batchResults.filter(result => !result.error).map(result => result.studentName || result.student_name)}
                  category="채점"
                  surface="scoring"
                />
              )}

              <div className="scoring-pdf-download-bar scoring-pdf-download-bar--bulk">
                <div>
                  <strong>전체 결과 PDF</strong>
                  <span>모든 학생을 한 파일에 학생별 페이지로 담아요.</span>
                </div>
                <div className="scoring-pdf-download-actions">
                  <button
                    type="button"
                    onClick={() => handleResultsPdfDownload(true)}
                    disabled={isGeneratingPdf || isResultInteractionLocked}
                  >
                    {isGeneratingPdf ? 'PDF 준비 중...' : '전체 학생용 PDF'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleResultsPdfDownload(false)}
                    disabled={isGeneratingPdf || isResultInteractionLocked}
                  >
                    {isGeneratingPdf ? 'PDF 준비 중...' : '전체 교사용 PDF'}
                  </button>
                </div>
              </div>

              {/* 결과 요약 테이블 (기존 상세/수정 흐름 유지) */}
              <details className="scoring-review-detail-toggle">
                <summary>결과 수정 및 다운로드</summary>
              <div className="scoring-batch-table-wrap">
                <table className="scoring-batch-table">
                  <thead>
                    <tr>
                      <th>번호</th>
                      <th>학생명</th>
                      <th>총점</th>
                      {((batchResults.find(r => r.question_results?.length > 0)?.question_results || []).map((question, idx) => (
                        <th key={`q-${idx}`}>{question.question_title || `${question.question_id}번`}</th>
                      )))}
                      {(batchResults.find(r => r.item_scores?.length > 0)?.item_scores || []).map((item, idx) => (
                        <th key={idx}>{item.item_name}</th>
                      ))}
                      <th>상세</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchResults.map((result, index) => {
                      const isExpanded = batchDetailIndex === index;
                      const resultGradeOnly = isGradeOnlyResult(result, activeRubric);
                      const resultScoreLabel = resultGradeOnly
                        ? getGradeOnlySummary(result)
                        : `${result.total_score}점`;
                      const questionColCount = (batchResults.find(r => r.question_results?.length > 0)?.question_results || []).length;
                      const itemColCount = (batchResults.find(r => r.item_scores?.length > 0)?.item_scores || []).length;
                      const totalColSpan = 4 + questionColCount + itemColCount;
                      return (
                      <React.Fragment key={index}>
                        <tr
                          className={`${result.error ? 'scoring-batch-row-error' : ''} ${isExpanded ? 'scoring-batch-row-expanded' : ''} ${!result.error ? 'scoring-batch-row-clickable' : ''}`}
                          onClick={() => { if (!result.error) setBatchDetailIndex(isExpanded ? null : index); }}
                        >
                          <td>{index + 1}</td>
                          <td>
                            <span className="scoring-batch-student-name">
                              {getScoringStudentName(result, `학생 ${index + 1}`)}
                            </span>
                          </td>
                          <td className="scoring-batch-score-cell">
                            {result.error ? (
                              <span className="scoring-batch-error-badge" title={result.error}>오류</span>
                            ) : (
                              <strong title={resultScoreLabel}>{resultScoreLabel}</strong>
                            )}
                          </td>
                          {!result.error && result.question_results?.length > 0
                            ? result.question_results.map((question, idx) => (
                              <td key={`q-${idx}`}>{resultGradeOnly ? (question.matched_level || getGradeOnlySummary(question)) : `${question.score || 0}/${question.max_score || 0}`}</td>
                            ))
                            : (batchResults.find(r => r.question_results?.length > 0)?.question_results || []).map((_, idx) => (
                              <td key={`q-empty-${idx}`}>{result.error ? '-' : '-'}</td>
                            ))
                          }
                          {!result.error && result.item_scores?.length > 0
                            ? result.item_scores.map((item, idx) => (
                              <td key={idx}>{resultGradeOnly ? (item.matched_level || '등급') : `${item.score || 0}/${item.max_score || 0}`}</td>
                            ))
                            : (batchResults.find(r => r.item_scores?.length > 0)?.item_scores || []).map((_, idx) => (
                              <td key={idx}>{result.error ? '-' : '-'}</td>
                            ))
                          }
                          <td>
                            {!result.error && (
                              <button
                                className="scoring-batch-detail-btn"
                                type="button"
                                aria-expanded={isExpanded}
                                aria-label={`${getScoringStudentName(result, `학생 ${index + 1}`)} 상세 ${isExpanded ? '접기' : '펼치기'}`}
                                onClick={(e) => { e.stopPropagation(); setBatchDetailIndex(isExpanded ? null : index); }}
                              >
                                <svg
                                  className={isExpanded ? 'is-open' : ''}
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <polyline points="6 9 12 15 18 9" />
                                </svg>
                              </button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && !result.error && (
                          <tr className="scoring-batch-detail-row">
                            <td colSpan={totalColSpan}>
                              <div className="scoring-result-card scoring-batch-inline-detail">
                                {result.item_scores?.length > 0 && !resultGradeOnly && (
                                  <div className="scoring-points-summary-bar">
                                    {result.item_scores.map((item, idx) => {
                                      const maxTotal = result.item_scores.reduce((sum, it) => sum + (it.max_score || 0), 0);
                                      const widthPercent = maxTotal > 0 ? ((item.max_score || 0) / maxTotal) * 100 : 0;
                                      const fillPercent = item.max_score > 0 ? (item.score / item.max_score) * 100 : 0;
                                      return (
                                        <div key={idx} className="scoring-points-bar-segment" style={{ width: `${widthPercent}%` }}>
                                          <div className="scoring-points-bar-fill" style={{ width: `${fillPercent}%` }} />
                                          <span className="scoring-points-bar-label">{item.item_name} {item.score}/{item.max_score}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                <div className="scoring-result-header">
                                  <div className="scoring-student-name-editor">
                                    {editingField === `batch-name-${index}` ? (
                                      <input
                                        className="scoring-student-name-input"
                                        value={result.studentName || result.student_name || ''}
                                        onChange={(e) => handleEditStudentName('batch', index, e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleFinishStudentNameEdit('batch', index);
                                          }
                                          if (e.key === 'Escape') {
                                            setEditingField(null);
                                          }
                                        }}
                                        autoFocus
                                      />
                                    ) : (
                                      <h3 className="scoring-result-student-name">{getScoringStudentName(result, `학생 ${index + 1}`)}</h3>
                                    )}
                                    <button
                                      type="button"
                                      className="scoring-student-name-edit-btn"
                                      aria-label={editingField === `batch-name-${index}` ? '학생 이름 저장' : '학생 이름 수정'}
                                      title={editingField === `batch-name-${index}` ? '저장' : '이름 수정'}
                                      onClick={() => {
                                        if (editingField === `batch-name-${index}`) {
                                          handleFinishStudentNameEdit('batch', index);
                                        } else {
                                          setEditingField(`batch-name-${index}`);
                                        }
                                      }}
                                    >
                                      {editingField === `batch-name-${index}` ? '저장' : '이름 변경'}
                                    </button>
                                  </div>
                                  <div className="scoring-result-score">{resultGradeOnly ? getGradeOnlySummary(result) : `${getResultTotalScore(result)}점`}</div>
                                </div>

                                {/* 제출물 보기 — 항목별 평가 위에 위치(제출물 먼저 확인 → 채점 결과 흐름).
                                    제출방 모드: roomSubmissions 매칭 시 room API,
                                    매칭 실패/일반 모드: storage_path → preview-by-path. */}
                                {(() => {
                                  let onClick = null;
                                  let label = '';
                                  if (batchUploadMode === 'student') {
                                    const studentName = getScoringStudentName(result);
                                    const matchedSub = roomSubmissions.find(
                                      s => normalizeStudentNameForMatch(s.student_name) === normalizeStudentNameForMatch(studentName)
                                    );
                                    if (matchedSub) {
                                      onClick = () => handlePreviewSubmission(matchedSub);
                                      label = matchedSub.filename || '';
                                    } else if (result.storage_path) {
                                      onClick = () => handlePreviewByPath(result);
                                      label = result.fileName || '';
                                    }
                                  } else if (result.storage_path) {
                                    onClick = () => handlePreviewByPath(result);
                                    label = result.fileName || '';
                                  }
                                  if (!onClick) return null;
                                  return (
                                    <div className="scoring-result-section scoring-result-preview-section">
                                      <button
                                        type="button"
                                        className="scoring-result-preview-btn"
                                        onClick={onClick}
                                        disabled={isResultInteractionLocked}
                                      >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                          <circle cx="12" cy="12" r="3" />
                                        </svg>
                                        제출물 보기
                                        {label ? ` (${label})` : ''}
                                      </button>
                                    </div>
                                  );
                                })()}

                                {result.question_results?.length > 0 && (
                                  <div className="scoring-result-section">
                                    <h4 className="scoring-result-section-title">문항별 평가</h4>
                                    <div className="scoring-question-results">
                                      {result.question_results.map((question, qIdx) => (
                                        <div key={question.question_id || qIdx} className="scoring-question-result">
                                          <div className="scoring-question-result-head">
                                            <div>
                                              <span className="scoring-question-result-kicker">{question.question_id}번 문항</span>
                                              <strong>{question.question_title || `${question.question_id}번 문항`}</strong>
                                            </div>
                                            <span className="scoring-question-result-score">{resultGradeOnly ? (question.matched_level || getGradeOnlySummary(question)) : `${question.score || 0}/${question.max_score || 0}점`}</span>
                                          </div>
                                          {question.answer_excerpt && (
                                            <p className="scoring-question-answer-excerpt">답안: {question.answer_excerpt}</p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {result.item_scores?.length > 0 && (
                                  <div className="scoring-result-section">
                                    <h4 className="scoring-result-section-title">항목별 평가</h4>
                                    <ScoringDistributionNotice result={result} />
                                    <div className="scoring-item-scores">
                                      {result.item_scores.map((item, idx) => {
                                        const rubricItem = activeRubric?.evaluation_items?.find(
                                          ri => getRubricItemName(ri) === item.item_name
                                        );
                                        const matchedCriteria = rubricItem?.scoring_levels?.find(
                                          sl => sl.level === item.matched_level
                                        )?.criteria;
                                        const isEditingThisItem = (
                                          editingItemField?.source === 'batch'
                                          && editingItemField?.resultIndex === index
                                          && editingItemField?.itemIndex === idx
                                        );
                                        return (
                                        <div key={idx} className="scoring-item-score">
                                          <div className="scoring-item-header">
                                            <div className="scoring-item-main">
                                              <span className="scoring-item-name">{item.item_name}</span>
                                              <span className="scoring-item-points">{resultGradeOnly ? (item.matched_level || '등급') : `${item.score}/${item.max_score}점`}</span>
                                            </div>
                                            <div className="scoring-item-actions">
                                              <button
                                                type="button"
                                                className="scoring-edit-toggle"
                                                onClick={() => handleStartItemEdit('batch', index, idx, item)}
                                              >
                                                수정
                                              </button>
                                              <button
                                                type="button"
                                                className="scoring-edit-toggle"
                                                disabled={isAiRefineTargetBlocked({ source: 'batch', resultIndex: index, targetType: 'item', itemIndex: idx })}
                                                onClick={() => handleOpenAiRefine({ source: 'batch', resultIndex: index, targetType: 'item', itemIndex: idx })}
                                              >
                                                AI에게 요청
                                              </button>
                                            </div>
                                          </div>
                                          {isEditingThisItem && (
                                            <div className="scoring-item-edit-area">
                                              {!resultGradeOnly && (
                                                <label className="scoring-item-edit-label">
                                                  점수
                                                  <input
                                                    className="scoring-item-score-input"
                                                    type="number"
                                                    min="0"
                                                    max={item.max_score || undefined}
                                                    step="0.5"
                                                    value={editingItemDraft.score}
                                                    onChange={(e) => setEditingItemDraft(prev => ({ ...prev, score: e.target.value }))}
                                                  />
                                                </label>
                                              )}
                                              {rubricItem?.scoring_levels?.length > 0 && (
                                                <label className="scoring-item-edit-label">
                                                  평가 기준
                                                  <select
                                                    className="scoring-item-level-select"
                                                    value={editingItemDraft.matched_level}
                                                    onChange={(e) => setEditingItemDraft(prev => ({ ...prev, matched_level: e.target.value }))}
                                                  >
                                                    <option value="">선택 안 함</option>
                                                    {rubricItem.scoring_levels.map((level, levelIdx) => (
                                                      <option key={levelIdx} value={level.level}>
                                                        {level.level}{!resultGradeOnly && level.score !== undefined ? ` (${level.score}점)` : ''}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </label>
                                              )}
                                              <label className="scoring-item-edit-label scoring-item-edit-label--full">
                                                평가 근거
                                                <textarea
                                                  className="scoring-editable-textarea"
                                                  value={editingItemDraft.reason}
                                                  onChange={(e) => setEditingItemDraft(prev => ({ ...prev, reason: e.target.value }))}
                                                  rows={3}
                                                />
                                              </label>
                                              <div className="scoring-edit-actions">
                                                <button type="button" className="scoring-item-edit-cancel" onClick={handleCancelItemEdit}>취소</button>
                                                <button type="button" className="scoring-item-edit-save" onClick={handleSaveItemEdit}>저장</button>
                                              </div>
                                            </div>
                                          )}
                                          {item.matched_level && !matchedCriteria && (
                                            <div className="scoring-criteria-match">
                                              <div className="scoring-criteria-match-row">
                                                <span className="scoring-criteria-match-tag">평가 기준</span>
                                                <span>{item.matched_level}</span>
                                              </div>
                                            </div>
                                          )}
                                          {(matchedCriteria || item.reason) && (
                                            <div className="scoring-criteria-match">
                                              {matchedCriteria && (
                                                <div className="scoring-criteria-match-row">
                                                  <span className="scoring-criteria-match-tag">평가 기준</span>
                                                  <span>{matchedCriteria}</span>
                                                </div>
                                              )}
                                              {item.reason && (
                                                <div className="scoring-criteria-match-row scoring-criteria-match-row--ai">
                                                  <span className="scoring-criteria-match-tag scoring-criteria-match-tag--ai">평가 근거</span>
                                                  <span>{item.reason}</span>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                          <ScoringCriterionChecks item={item} />
                                          {renderAiRefinePanel({ source: 'batch', resultIndex: index, targetType: 'item', itemIndex: idx })}
                                        </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                <div className="scoring-result-feedback-grid">
                                  {result.strengths?.length > 0 && (
                                    <div className="scoring-result-section strengths">
                                      <div className="scoring-editable-header">
                                        <h4 className="scoring-result-section-title">잘한 점</h4>
                                        <div className="scoring-editable-actions">
                                          <button
                                            className="scoring-edit-toggle"
                                            onClick={() => {
                                              if (editingField === `batch-strengths-${index}`) {
                                                handleFinishResultEdit('batch', index, 'strengths');
                                              } else {
                                                setEditingField(`batch-strengths-${index}`);
                                              }
                                            }}
                                          >
                                            {editingField === `batch-strengths-${index}` ? '완료' : '수정'}
                                          </button>
                                          <button
                                            className="scoring-edit-toggle"
                                            disabled={isAiRefineTargetBlocked({ source: 'batch', resultIndex: index, targetType: 'field', field: 'strengths' })}
                                            onClick={() => handleOpenAiRefine({ source: 'batch', resultIndex: index, targetType: 'field', field: 'strengths' })}
                                          >
                                            AI에게 요청
                                          </button>
                                        </div>
                                      </div>
                                      {editingField === `batch-strengths-${index}` ? (
                                        <textarea
                                          className="scoring-editable-textarea"
                                          value={(result.strengths || []).join('\n')}
                                          onChange={(e) => handleEditBatchResult(index, 'strengths', e.target.value.split('\n').map(line => line.trim()).filter(Boolean))}
                                          rows={4}
                                        />
                                      ) : (
                                        <ul className="scoring-result-list">
                                          {result.strengths.map((item, idx) => <li key={idx}>{item}</li>)}
                                        </ul>
                                      )}
                                      {renderAiRefinePanel({ source: 'batch', resultIndex: index, targetType: 'field', field: 'strengths' })}
                                    </div>
                                  )}
                                  {result.improvements?.length > 0 && (
                                    <div className="scoring-result-section improvements">
                                      <div className="scoring-editable-header">
                                        <h4 className="scoring-result-section-title">보완할 점</h4>
                                        <div className="scoring-editable-actions">
                                          <button
                                            className="scoring-edit-toggle"
                                            onClick={() => {
                                              if (editingField === `batch-improvements-${index}`) {
                                                handleFinishResultEdit('batch', index, 'improvements');
                                              } else {
                                                setEditingField(`batch-improvements-${index}`);
                                              }
                                            }}
                                          >
                                            {editingField === `batch-improvements-${index}` ? '완료' : '수정'}
                                          </button>
                                          <button
                                            className="scoring-edit-toggle"
                                            disabled={isAiRefineTargetBlocked({ source: 'batch', resultIndex: index, targetType: 'field', field: 'improvements' })}
                                            onClick={() => handleOpenAiRefine({ source: 'batch', resultIndex: index, targetType: 'field', field: 'improvements' })}
                                          >
                                            AI에게 요청
                                          </button>
                                        </div>
                                      </div>
                                      {editingField === `batch-improvements-${index}` ? (
                                        <textarea
                                          className="scoring-editable-textarea"
                                          value={(result.improvements || []).join('\n')}
                                          onChange={(e) => handleEditBatchResult(index, 'improvements', e.target.value.split('\n').map(line => line.trim()).filter(Boolean))}
                                          rows={4}
                                        />
                                      ) : (
                                        <ul className="scoring-result-list">
                                          {result.improvements.map((item, idx) => <li key={idx}>{item}</li>)}
                                        </ul>
                                      )}
                                      {renderAiRefinePanel({ source: 'batch', resultIndex: index, targetType: 'field', field: 'improvements' })}
                                    </div>
                                  )}
                                </div>

                                {result.detailed_feedback && (
                                  <div className="scoring-result-section">
                                    <div className="scoring-editable-header">
                                      <h4 className="scoring-result-section-title">종합 의견</h4>
                                      <div className="scoring-editable-actions">
                                        <button
                                          className="scoring-edit-toggle"
                                          onClick={() => {
                                            if (editingField === `batch-feedback-${index}`) {
                                              handleFinishResultEdit('batch', index, 'detailed_feedback');
                                            } else {
                                              setEditingField(`batch-feedback-${index}`);
                                            }
                                          }}
                                        >
                                          {editingField === `batch-feedback-${index}` ? '완료' : '수정'}
                                        </button>
                                        <button
                                          className="scoring-edit-toggle"
                                          disabled={isAiRefineTargetBlocked({ source: 'batch', resultIndex: index, targetType: 'field', field: 'detailed_feedback' })}
                                          onClick={() => handleOpenAiRefine({ source: 'batch', resultIndex: index, targetType: 'field', field: 'detailed_feedback' })}
                                        >
                                          AI에게 요청
                                        </button>
                                        <button
                                          className="scoring-copy-toggle"
                                          onClick={() => {
                                            navigator.clipboard.writeText(result.detailed_feedback);
                                            showToast('총평을 복사했어요', 'success');
                                          }}
                                        >
                                          복사
                                        </button>
                                      </div>
                                    </div>
                                    {editingField === `batch-feedback-${index}` ? (
                                      <textarea
                                        className="scoring-editable-textarea"
                                        value={result.detailed_feedback}
                                        onChange={(e) => handleEditBatchResult(index, 'detailed_feedback', e.target.value)}
                                        rows={4}
                                      />
                                    ) : (
                                      <div className="scoring-detailed-feedback">
                                        <p>{result.detailed_feedback}</p>
                                      </div>
                                    )}
                                    {renderAiRefinePanel({ source: 'batch', resultIndex: index, targetType: 'field', field: 'detailed_feedback' })}
                                  </div>
                                )}

                                {result.teacher_comment && (
                                  <div className="scoring-result-section">
                                    <div className="scoring-editable-header">
                                      <h4 className="scoring-result-section-title">교과/세특 생기부</h4>
                                      <div className="scoring-editable-actions">
                                        <button
                                          className="scoring-edit-toggle"
                                          onClick={() => {
                                            if (editingField === `batch-comment-${index}`) {
                                              handleFinishResultEdit('batch', index, 'teacher_comment');
                                            } else {
                                              setEditingField(`batch-comment-${index}`);
                                            }
                                          }}
                                        >
                                          {editingField === `batch-comment-${index}` ? '완료' : '수정'}
                                        </button>
                                        <button
                                          className="scoring-edit-toggle"
                                          disabled={isAiRefineTargetBlocked({ source: 'batch', resultIndex: index, targetType: 'field', field: 'teacher_comment' })}
                                          onClick={() => handleOpenAiRefine({ source: 'batch', resultIndex: index, targetType: 'field', field: 'teacher_comment' })}
                                        >
                                          AI에게 요청
                                        </button>
                                        <button
                                          className="scoring-copy-toggle"
                                          onClick={() => {
                                            navigator.clipboard.writeText(result.teacher_comment);
                                            showToast('교과세특을 복사했어요', 'success');
                                          }}
                                        >
                                          복사
                                        </button>
                                      </div>
                                    </div>
                                    {editingField === `batch-comment-${index}` ? (
                                      <textarea
                                        className="scoring-editable-textarea"
                                        value={result.teacher_comment}
                                        onChange={(e) => handleEditBatchResult(index, 'teacher_comment', e.target.value)}
                                        rows={4}
                                      />
                                    ) : (
                                      <div className="scoring-teacher-comment">
                                        <p>{result.teacher_comment}</p>
                                      </div>
                                    )}
                                    {renderAiRefinePanel({ source: 'batch', resultIndex: index, targetType: 'field', field: 'teacher_comment' })}
                                  </div>
                                )}

                                <div className="scoring-pdf-download-bar scoring-pdf-download-bar--student">
                                  <div>
                                    <strong>이 학생 PDF</strong>
                                    <span>학생용은 생기부 문구를 제외해요.</span>
                                  </div>
                                  <div className="scoring-pdf-download-actions">
                                    <button
                                      type="button"
                                      onClick={() => handleResultsPdfDownload(true, [result])}
                                      disabled={isGeneratingPdf || isResultInteractionLocked}
                                    >
                                      {isGeneratingPdf ? '준비 중...' : '학생용 PDF'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleResultsPdfDownload(false, [result])}
                                      disabled={isGeneratingPdf || isResultInteractionLocked}
                                    >
                                      {isGeneratingPdf ? '준비 중...' : '교사용 PDF'}
                                    </button>
                                  </div>
                                </div>

                                {/* 제출물 보기는 항목별 평가 위로 이동했음.
                                    학생 카드 펼쳤을 때 "총점 → 제출물 → 항목별 평가" 흐름. */}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </details>

              {isSaved && (
                <div className="scoring-batch-saved-notice">
                  채점 기록에 자동 저장됨
                  <button className="scoring-batch-saved-link" onClick={() => navigate('/scoring-history')}>
                    기록 보기
                  </button>
                </div>
              )}

              {/* 재채점 요청 섹션 (일괄) */}
              {shouldShowReScoreSection && (
                <div className="scoring-rescore-section">
                  <div className="scoring-rescore-header">
                    <span className="scoring-rescore-title">채점 기준 세부 조정</span>
                    {reScoreCount > 0 && (
                      <span className="scoring-rescore-badge">{reScoreCount}회 조정</span>
                    )}
                    {previousResults && (
                      <button className="scoring-rescore-undo" onClick={handleRestorePrevious} disabled={isResultInteractionLocked}>
                        이전 결과로 되돌리기
                      </button>
                    )}
                  </div>
                  <p className="scoring-rescore-desc">
                    {canReScoreCurrentResults
                      ? '파일을 다시 올리지 않아도 돼요. 조정할 내용을 입력하면 같은 답안으로 다시 채점해요.'
                      : '이전 기록에 원문 답안이 저장되어 있지 않아 재채점할 수 없어요. 앞으로 저장되는 기록은 여기서 바로 재채점할 수 있어요.'}
                  </p>
                  {renderReScorePresetButtons()}
                  <div className="scoring-rescore-input-wrap">
                    <textarea
                      className="scoring-rescore-textarea"
                      value={reScoreInstructions}
                      onChange={(e) => setReScoreInstructions(e.target.value)}
                      placeholder="상 30%, 중 50%, 하 20% 비율로 맞춰줘"
                      rows={2}
                      disabled={reScoreLoading || !canReScoreCurrentResults}
                    />
                    <button
                      className="scoring-rescore-submit"
                      onClick={() => handleReScore()}
                      disabled={reScoreLoading || !canReScoreCurrentResults || !reScoreInstructions.trim()}
                    >
                      {reScoreLoading ? (
                        <span className="scoring-rescore-loading">
                          <span className="scoring-rescore-spinner" />
                          재채점 중...
                        </span>
                      ) : '재채점'}
                    </button>
                  </div>
                  {reScoreLoading && currentTip && (
                    <p className="scoring-rescore-tip">{currentTip}</p>
                  )}
                </div>
              )}

              {/* 액션 버튼 */}
              <div className="scoring-actions">
                <button
                  onClick={handleSaveBatch}
                  className="scoring-action-primary scoring-action-full"
                  disabled={isSaving || isSaved || isResultInteractionLocked}
                  aria-busy={isSaving}
                  data-testid="scoring-batch-save-button"
                >
                  {renderSaveLabel()}
                </button>
                <div className="scoring-actions-row">
                  <button className="scoring-action-secondary" onClick={handleBatchDownloadExcel} disabled={isResultInteractionLocked}>
                    답안·결과 엑셀
                  </button>
                  <button
                    className="scoring-action-secondary"
                    onClick={() => handleHandoffToSaenggibu(batchResults, 'batch')}
                    disabled={isResultInteractionLocked}
                    data-testid="scoring-batch-saenggibu-handoff"
                  >
                    생기부로 이어가기
                  </button>
                  <button className="scoring-action-secondary" disabled={isResultInteractionLocked} onClick={() => {
                    setBatchResults([]);
                    setSavedScoringRecordId(null);
                    setBatchFiles([]);
                    setBatchStudents([]);
                    setBatchFilesStats([]);
                    setBatchDetailIndex(null);
                    setCachedAnswerTexts([]);
                    setReScoreInstructions('');
                    setReScoreCount(0);
                    setPreviousResults(null);
                  }}>
                    다른 기준서로 새로 채점
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── 개별 채점 결과 ── */}
          {evaluationResults.length > 0 && !isLoading && (
            <div
              ref={resultsTopRef}
              className={`scoring-results ${isResultInteractionLocked ? 'scoring-results--busy' : ''}`}
              aria-busy={isResultInteractionLocked}
            >
              {renderReScoreBusyOverlay()}
              <div className="scoring-results-header">
                <h2 className="scoring-results-title">채점 결과</h2>
                {evaluationResults.length === 1 && !evaluationResults[0].error && (
                  <span className="scoring-results-total-badge">
                    {getResultTotalScore(evaluationResults[0])}점
                  </span>
                )}
              </div>

              <div className="scoring-review-launcher-head">
                <p className="scoring-review-launcher-title">결과와 제출물</p>
                <p className="scoring-review-launcher-desc">카드를 누르면 제출물과 채점 근거를 한 화면에서 볼 수 있어요.</p>
              </div>
              <ScoringReviewLauncher
                onSelect={(index) => setReviewModal({ source: 'single', index })}
                rubric={activeRubric}
                results={evaluationResults}
              />

              {/* 적용된 채점 기준 — 항상 보이는 구조화 요약 */}
              {activeRubric && activeRubric.evaluation_items?.length > 0 && (
                <div className="scoring-applied-rubric">
                  <span className="scoring-applied-rubric-label">적용된 채점 기준</span>
                  <span className="scoring-applied-rubric-subject">
                    {activeRubric.subject || '채점 기준서'} · 총 {activeRubric.total_points || 0}점
                  </span>
                  <div className="scoring-applied-rubric-items">
                    {activeRubric.evaluation_items.map((item, idx) => (
                      <span key={idx} className="scoring-applied-rubric-chip">
                        {getRubricItemName(item)} <strong>{getRubricItemMaxScore(item)}점</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 기준서 원문 (접이식) */}
              {rubricText && (
                <div className="scoring-rubric-summary-card">
                  <button
                    className={`scoring-rubric-summary-toggle ${rubricSummaryOpen ? 'open' : ''}`}
                    onClick={() => setRubricSummaryOpen(prev => !prev)}
                  >
                    <div className="scoring-rubric-summary-left">
                      <span className="scoring-rubric-summary-icon" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M8 6h8" />
                          <path d="M8 10h8" />
                          <path d="M8 14h5" />
                          <path d="M6 2h9l5 5v15H6z" />
                          <path d="M15 2v5h5" />
                        </svg>
                      </span>
                      <span className="scoring-rubric-summary-label">기준서 원문 보기</span>
                    </div>
                    <svg
                      className="scoring-rubric-summary-arrow"
                      xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {rubricSummaryOpen && (
                    <div className="scoring-rubric-summary-content">
                      <div className="scoring-rubric-summary-text">{rubricText}</div>
                    </div>
                  )}
                </div>
              )}

              {/* 제출물 보기 (단일 채점) — 기준서 원문 바로 아래에 위치.
                  사용자가 채점 결과를 보기 전에 "어떤 제출물이 채점된 건지" 먼저 확인하도록 흐름 조정.
                  - 파일 첨부 → storage_path 기반 signed URL 모달
                  - 텍스트만 입력 → answer_text를 모달에 직접 표시
                  단일 채점은 결과가 1개이므로 evaluationResults[0]를 사용. */}
              {evaluationResults.length === 1 && !evaluationResults[0].error
                && (evaluationResults[0].storage_path || evaluationResults[0].answer_text) && (
                <div className="scoring-result-section scoring-result-preview-section">
                  <button
                    type="button"
                    className="scoring-result-preview-btn"
                    onClick={() => evaluationResults[0].storage_path
                      ? handlePreviewByPath(evaluationResults[0])
                      : handlePreviewText(evaluationResults[0])
                    }
                    disabled={isResultInteractionLocked}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    제출물 보기
                    {evaluationResults[0].storage_path
                      ? (evaluationResults[0].fileName ? ` (${evaluationResults[0].fileName})` : '')
                      : ' (직접 입력한 답안)'
                    }
                  </button>
                </div>
              )}

              {evaluationResults.map((result, index) => (
                <div key={index} className="scoring-result-card">
                  {result.error ? (
                    <div className="scoring-result-error">
                      <h4 className="scoring-result-student-name">{result.studentName}</h4>
                      <p className="scoring-result-error-message">{result.feedback || result.error}</p>
                    </div>
                  ) : (
                    <>
                      {/* 배점 요약 바 */}
                      {result.item_scores && result.item_scores.length > 0 && !isGradeOnlyResult(result, activeRubric) && (
                        <div className="scoring-points-summary-bar">
                          {result.item_scores.map((item, idx) => {
                            const maxTotal = result.item_scores.reduce((sum, it) => sum + (it.max_score || 0), 0);
                            const widthPercent = maxTotal > 0 ? ((item.max_score || 0) / maxTotal) * 100 : 0;
                            const fillPercent = item.max_score > 0 ? (item.score / item.max_score) * 100 : 0;
                            return (
                              <div key={idx} className="scoring-points-bar-segment" style={{ width: `${widthPercent}%` }}>
                                <div className="scoring-points-bar-fill" style={{ width: `${fillPercent}%` }} />
                                <span className="scoring-points-bar-label">{item.item_name} {item.score}/{item.max_score}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="scoring-result-header">
                        <div className="scoring-student-name-editor">
                          {editingField === `single-name-${index}` ? (
                            <input
                              className="scoring-student-name-input"
                              value={result.studentName || result.student_name || ''}
                              onChange={(e) => handleEditStudentName('single', index, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleFinishStudentNameEdit('single', index);
                                }
                                if (e.key === 'Escape') {
                                  setEditingField(null);
                                }
                              }}
                              autoFocus
                            />
                          ) : (
                            <h3 className="scoring-result-student-name">
                              {getScoringStudentName(result, '학생') || '학생'}
                            </h3>
                          )}
                          <button
                            type="button"
                            className="scoring-student-name-edit-btn"
                            aria-label={editingField === `single-name-${index}` ? '학생 이름 저장' : '학생 이름 수정'}
                            title={editingField === `single-name-${index}` ? '저장' : '이름 수정'}
                            onClick={() => {
                              if (editingField === `single-name-${index}`) {
                                handleFinishStudentNameEdit('single', index);
                              } else {
                                setEditingField(`single-name-${index}`);
                              }
                            }}
                          >
                            {editingField === `single-name-${index}` ? '저장' : '이름 변경'}
                          </button>
                        </div>
                        <div className="scoring-result-score">
                          {isGradeOnlyResult(result, activeRubric) ? getGradeOnlySummary(result) : `${getResultTotalScore(result)}점`}
                        </div>
                      </div>

                      {result.question_results && result.question_results.length > 0 && (
                        <div className="scoring-result-section">
                          <h4 className="scoring-result-section-title">문항별 평가</h4>
                          <div className="scoring-question-results">
                            {result.question_results.map((question, qIdx) => (
                              <div key={question.question_id || qIdx} className="scoring-question-result">
                                <div className="scoring-question-result-head">
                                  <div>
                                    <span className="scoring-question-result-kicker">{question.question_id}번 문항</span>
                                    <strong>{question.question_title || `${question.question_id}번 문항`}</strong>
                                  </div>
                                  <span className="scoring-question-result-score">{isGradeOnlyResult(result, activeRubric) ? (question.matched_level || getGradeOnlySummary(question)) : `${question.score || 0}/${question.max_score || 0}점`}</span>
                                </div>
                                {question.answer_excerpt && (
                                  <p className="scoring-question-answer-excerpt">답안: {question.answer_excerpt}</p>
                                )}
                                {question.item_scores?.length > 0 && (
                                  <div className="scoring-item-scores scoring-question-item-scores">
                                    {question.item_scores.map((item, idx) => (
                                      <div key={idx} className="scoring-item-score">
                                        <div className="scoring-item-header">
                                          <span className="scoring-item-name">{item.item_name}</span>
                                          <span className="scoring-item-points">{isGradeOnlyResult(result, activeRubric) ? (item.matched_level || '등급') : `${item.score}/${item.max_score}점`}</span>
                                        </div>
                                        {item.reason && (
                                          <div className="scoring-criteria-match">
                                            <div className="scoring-criteria-match-row scoring-criteria-match-row--ai">
                                              <span className="scoring-criteria-match-tag scoring-criteria-match-tag--ai">평가 근거</span>
                                              <span>{item.reason}</span>
                                            </div>
                                          </div>
                                        )}
                                        <ScoringCriterionChecks item={item} />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {result.item_scores && result.item_scores.length > 0 && (
                        <div className="scoring-result-section">
                          <h4 className="scoring-result-section-title">항목별 평가</h4>
                          <ScoringDistributionNotice result={result} />
                          <div className="scoring-item-scores">
                            {result.item_scores.map((item, idx) => {
                              const rubricItem = activeRubric?.evaluation_items?.find(
                                ri => getRubricItemName(ri) === item.item_name
                              );
                              const matchedCriteria = rubricItem?.scoring_levels?.find(
                                sl => sl.level === item.matched_level
                              )?.criteria;
                              const isEditingThisItem = (
                                editingItemField?.source === 'single'
                                && editingItemField?.resultIndex === index
                                && editingItemField?.itemIndex === idx
                              );
                              return (
                              <div key={idx} className="scoring-item-score">
                                <div className="scoring-item-header">
                                  <div className="scoring-item-main">
                                    <span className="scoring-item-name">{item.item_name}</span>
                                    <span className="scoring-item-points">{isGradeOnlyResult(result, activeRubric) ? (item.matched_level || '등급') : `${item.score}/${item.max_score}점`}</span>
                                  </div>
                                  <div className="scoring-item-actions">
                                    <button
                                      type="button"
                                      className="scoring-edit-toggle"
                                      onClick={() => handleStartItemEdit('single', index, idx, item)}
                                    >
                                      수정
                                    </button>
                                    <button
                                      type="button"
                                      className="scoring-edit-toggle"
                                      disabled={isAiRefineTargetBlocked({ source: 'single', resultIndex: index, targetType: 'item', itemIndex: idx })}
                                      onClick={() => handleOpenAiRefine({ source: 'single', resultIndex: index, targetType: 'item', itemIndex: idx })}
                                    >
                                      AI에게 요청
                                    </button>
                                  </div>
                                </div>
                                {isEditingThisItem && (
                                  <div className="scoring-item-edit-area">
                                    {!isGradeOnlyResult(result, activeRubric) && (
                                      <label className="scoring-item-edit-label">
                                        점수
                                        <input
                                          className="scoring-item-score-input"
                                          type="number"
                                          min="0"
                                          max={item.max_score || undefined}
                                          step="0.5"
                                          value={editingItemDraft.score}
                                          onChange={(e) => setEditingItemDraft(prev => ({ ...prev, score: e.target.value }))}
                                        />
                                      </label>
                                    )}
                                    {rubricItem?.scoring_levels?.length > 0 && (
                                      <label className="scoring-item-edit-label">
                                        평가 기준
                                        <select
                                          className="scoring-item-level-select"
                                          value={editingItemDraft.matched_level}
                                          onChange={(e) => setEditingItemDraft(prev => ({ ...prev, matched_level: e.target.value }))}
                                        >
                                          <option value="">선택 안 함</option>
                                          {rubricItem.scoring_levels.map((level, levelIdx) => (
                                            <option key={levelIdx} value={level.level}>
                                              {level.level}{!isGradeOnlyResult(result, activeRubric) && level.score !== undefined ? ` (${level.score}점)` : ''}
                                            </option>
                                          ))}
                                        </select>
                                      </label>
                                    )}
                                    <label className="scoring-item-edit-label scoring-item-edit-label--full">
                                      평가 근거
                                      <textarea
                                        className="scoring-editable-textarea"
                                        value={editingItemDraft.reason}
                                        onChange={(e) => setEditingItemDraft(prev => ({ ...prev, reason: e.target.value }))}
                                        rows={3}
                                      />
                                    </label>
                                    <div className="scoring-edit-actions">
                                      <button type="button" className="scoring-item-edit-cancel" onClick={handleCancelItemEdit}>취소</button>
                                      <button type="button" className="scoring-item-edit-save" onClick={handleSaveItemEdit}>저장</button>
                                    </div>
                                  </div>
                                )}
                                {item.matched_level && !matchedCriteria && (
                                  <div className="scoring-criteria-match">
                                    <div className="scoring-criteria-match-row">
                                      <span className="scoring-criteria-match-tag">평가 기준</span>
                                      <span>{item.matched_level}</span>
                                    </div>
                                  </div>
                                )}
                                {(matchedCriteria || item.reason) && (
                                  <div className="scoring-criteria-match">
                                    {matchedCriteria && (
                                      <div className="scoring-criteria-match-row">
                                        <span className="scoring-criteria-match-tag">평가 기준</span>
                                        <span>{matchedCriteria}</span>
                                      </div>
                                    )}
                                    {item.reason && (
                                      <div className="scoring-criteria-match-row scoring-criteria-match-row--ai">
                                        <span className="scoring-criteria-match-tag scoring-criteria-match-tag--ai">평가 근거</span>
                                        <span>{item.reason}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                                <ScoringCriterionChecks item={item} />
                                {renderAiRefinePanel({ source: 'single', resultIndex: index, targetType: 'item', itemIndex: idx })}
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="scoring-result-feedback-grid">
                        {result.strengths && result.strengths.length > 0 && (
                          <div className="scoring-result-section strengths">
                            <div className="scoring-editable-header">
                              <h4 className="scoring-result-section-title">잘한 점</h4>
                              <div className="scoring-editable-actions">
                                <button
                                  className="scoring-edit-toggle"
                                  onClick={() => {
                                    if (editingField === `strengths-${index}`) {
                                      handleFinishResultEdit('single', index, 'strengths');
                                    } else {
                                      setEditingField(`strengths-${index}`);
                                    }
                                  }}
                                >
                                  {editingField === `strengths-${index}` ? '완료' : '수정'}
                                </button>
                                <button
                                  className="scoring-edit-toggle"
                                  disabled={isAiRefineTargetBlocked({ source: 'single', resultIndex: index, targetType: 'field', field: 'strengths' })}
                                  onClick={() => handleOpenAiRefine({ source: 'single', resultIndex: index, targetType: 'field', field: 'strengths' })}
                                >
                                  AI에게 요청
                                </button>
                              </div>
                            </div>
                            {editingField === `strengths-${index}` ? (
                              <textarea
                                className="scoring-editable-textarea"
                                value={(result.strengths || []).join('\n')}
                                onChange={(e) => handleEditResult(index, 'strengths', e.target.value.split('\n').map(line => line.trim()).filter(Boolean))}
                                rows={4}
                              />
                            ) : (
                              <ul className="scoring-result-list">
                                {result.strengths.map((item, idx) => (
                                  <li key={idx}>{item}</li>
                                ))}
                              </ul>
                            )}
                            {renderAiRefinePanel({ source: 'single', resultIndex: index, targetType: 'field', field: 'strengths' })}
                          </div>
                        )}
                        {result.improvements && result.improvements.length > 0 && (
                          <div className="scoring-result-section improvements">
                            <div className="scoring-editable-header">
                              <h4 className="scoring-result-section-title">보완할 점</h4>
                              <div className="scoring-editable-actions">
                                <button
                                  className="scoring-edit-toggle"
                                  onClick={() => {
                                    if (editingField === `improvements-${index}`) {
                                      handleFinishResultEdit('single', index, 'improvements');
                                    } else {
                                      setEditingField(`improvements-${index}`);
                                    }
                                  }}
                                >
                                  {editingField === `improvements-${index}` ? '완료' : '수정'}
                                </button>
                                <button
                                  className="scoring-edit-toggle"
                                  disabled={isAiRefineTargetBlocked({ source: 'single', resultIndex: index, targetType: 'field', field: 'improvements' })}
                                  onClick={() => handleOpenAiRefine({ source: 'single', resultIndex: index, targetType: 'field', field: 'improvements' })}
                                >
                                  AI에게 요청
                                </button>
                              </div>
                            </div>
                            {editingField === `improvements-${index}` ? (
                              <textarea
                                className="scoring-editable-textarea"
                                value={(result.improvements || []).join('\n')}
                                onChange={(e) => handleEditResult(index, 'improvements', e.target.value.split('\n').map(line => line.trim()).filter(Boolean))}
                                rows={4}
                              />
                            ) : (
                              <ul className="scoring-result-list">
                                {result.improvements.map((item, idx) => (
                                  <li key={idx}>{item}</li>
                                ))}
                              </ul>
                            )}
                            {renderAiRefinePanel({ source: 'single', resultIndex: index, targetType: 'field', field: 'improvements' })}
                          </div>
                        )}
                      </div>

                      {/* 종합 의견 (편집 + 복사) */}
                      {result.detailed_feedback && (
                        <div className="scoring-result-section">
                          <div className="scoring-editable-header">
                            <h4 className="scoring-result-section-title">종합 의견</h4>
                            <div className="scoring-editable-actions">
                              <button
                                className="scoring-edit-toggle"
                                onClick={() => {
                                  if (editingField === `feedback-${index}`) {
                                    handleFinishResultEdit('single', index, 'detailed_feedback');
                                  } else {
                                    setEditingField(`feedback-${index}`);
                                  }
                                }}
                              >
                                {editingField === `feedback-${index}` ? '완료' : '수정'}
                              </button>
                              <button
                                className="scoring-edit-toggle"
                                disabled={isAiRefineTargetBlocked({ source: 'single', resultIndex: index, targetType: 'field', field: 'detailed_feedback' })}
                                onClick={() => handleOpenAiRefine({ source: 'single', resultIndex: index, targetType: 'field', field: 'detailed_feedback' })}
                              >
                                AI에게 요청
                              </button>
                              <button
                                className="scoring-copy-toggle"
                                onClick={() => {
                                  navigator.clipboard.writeText(result.detailed_feedback);
                                  showToast('총평을 복사했어요', 'success');
                                }}
                              >
                                복사
                              </button>
                            </div>
                          </div>
                          {editingField === `feedback-${index}` ? (
                            <textarea
                              className="scoring-editable-textarea"
                              value={result.detailed_feedback}
                              onChange={(e) => handleEditResult(index, 'detailed_feedback', e.target.value)}
                              rows={4}
                            />
                          ) : (
                            <div className="scoring-detailed-feedback">
                              <p>{result.detailed_feedback}</p>
                            </div>
                          )}
                          {renderAiRefinePanel({ source: 'single', resultIndex: index, targetType: 'field', field: 'detailed_feedback' })}
                        </div>
                      )}

                      {/* 교과/세특 생기부 (편집 + 복사) */}
                      {result.teacher_comment && (
                        <div className="scoring-result-section">
                          <div className="scoring-editable-header">
                            <h4 className="scoring-result-section-title">교과/세특 생기부</h4>
                            <div className="scoring-editable-actions">
                              <button
                                className="scoring-edit-toggle"
                                onClick={() => {
                                  if (editingField === `comment-${index}`) {
                                    handleFinishResultEdit('single', index, 'teacher_comment');
                                  } else {
                                    setEditingField(`comment-${index}`);
                                  }
                                }}
                              >
                                {editingField === `comment-${index}` ? '완료' : '수정'}
                              </button>
                              <button
                                className="scoring-edit-toggle"
                                disabled={isAiRefineTargetBlocked({ source: 'single', resultIndex: index, targetType: 'field', field: 'teacher_comment' })}
                                onClick={() => handleOpenAiRefine({ source: 'single', resultIndex: index, targetType: 'field', field: 'teacher_comment' })}
                              >
                                AI에게 요청
                              </button>
                              <button
                                className="scoring-copy-toggle scoring-copy-toggle--green"
                                onClick={() => {
                                  navigator.clipboard.writeText(result.teacher_comment);
                                  showToast('교과세특을 복사했어요', 'success');
                                }}
                              >
                                복사
                              </button>
                            </div>
                          </div>
                          {editingField === `comment-${index}` ? (
                            <textarea
                              className="scoring-editable-textarea"
                              value={result.teacher_comment}
                              onChange={(e) => handleEditResult(index, 'teacher_comment', e.target.value)}
                              rows={4}
                            />
                          ) : (
                            <div className="scoring-teacher-comment">
                              <p>{result.teacher_comment}</p>
                            </div>
                          )}
                          {renderAiRefinePanel({ source: 'single', resultIndex: index, targetType: 'field', field: 'teacher_comment' })}
                        </div>
                      )}

                      <div className="scoring-pdf-download-bar scoring-pdf-download-bar--student">
                        <div>
                          <strong>이 학생 PDF</strong>
                          <span>학생용은 생기부 문구를 제외해요.</span>
                        </div>
                        <div className="scoring-pdf-download-actions">
                          <button
                            type="button"
                            onClick={() => handleResultsPdfDownload(true, [result])}
                            disabled={isGeneratingPdf || isResultInteractionLocked}
                          >
                            {isGeneratingPdf ? '준비 중...' : '학생용 PDF'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleResultsPdfDownload(false, [result])}
                            disabled={isGeneratingPdf || isResultInteractionLocked}
                          >
                            {isGeneratingPdf ? '준비 중...' : '교사용 PDF'}
                          </button>
                        </div>
                      </div>

                      {/* 제출물 보기는 결과 카드 위(기준서 원문 바로 아래)로 이동했음.
                          여기에 두면 채점 결과를 다 보고 나서야 보기 버튼이 나오는 흐름이라
                          "제출물 먼저 확인 → 채점 결과 확인" 순서가 깨짐. */}
                    </>
                  )}
                </div>
              ))}

              {/* 재채점 요청 섹션 */}
              {shouldShowReScoreSection && (
                <div className="scoring-rescore-section">
                  <div className="scoring-rescore-header">
                    <span className="scoring-rescore-title">채점 기준 세부 조정</span>
                    {reScoreCount > 0 && (
                      <span className="scoring-rescore-badge">{reScoreCount}회 조정</span>
                    )}
                    {previousResults && (
                      <button className="scoring-rescore-undo" onClick={handleRestorePrevious} disabled={isResultInteractionLocked}>
                        이전 결과로 되돌리기
                      </button>
                    )}
                  </div>
                  <p className="scoring-rescore-desc">
                    {canReScoreCurrentResults
                      ? '파일을 다시 올리지 않아도 돼요. 조정할 내용을 입력하면 같은 답안으로 다시 채점해요.'
                      : '이전 기록에 원문 답안이 저장되어 있지 않아 재채점할 수 없어요. 앞으로 저장되는 기록은 여기서 바로 재채점할 수 있어요.'}
                  </p>
                  {renderReScorePresetButtons()}
                  <div className="scoring-rescore-input-wrap">
                    <textarea
                      className="scoring-rescore-textarea"
                      value={reScoreInstructions}
                      onChange={(e) => setReScoreInstructions(e.target.value)}
                      placeholder="상 30%, 중 50%, 하 20% 비율로 맞춰줘"
                      rows={2}
                      disabled={reScoreLoading || !canReScoreCurrentResults}
                    />
                    <button
                      className="scoring-rescore-submit"
                      onClick={() => handleReScore()}
                      disabled={reScoreLoading || !canReScoreCurrentResults || !reScoreInstructions.trim()}
                    >
                      {reScoreLoading ? (
                        <span className="scoring-rescore-loading">
                          <span className="scoring-rescore-spinner" />
                          재채점 중...
                        </span>
                      ) : '재채점'}
                    </button>
                  </div>
                  {reScoreLoading && currentTip && (
                    <p className="scoring-rescore-tip">{currentTip}</p>
                  )}
                </div>
              )}

              {/* 액션 버튼: Assessment Plan 패턴 */}
              <div className="scoring-actions">
                <button
                  onClick={handleSave}
                  disabled={isSaving || isSaved || isResultInteractionLocked}
                  aria-busy={isSaving}
                  className="scoring-action-primary scoring-action-full"
                  data-testid="scoring-save-button"
                >
                  {renderSaveLabel()}
                </button>
                <div className="scoring-actions-row">
                  <button className="scoring-action-secondary" onClick={handleDownloadExcel} disabled={isResultInteractionLocked}>
                    답안·결과 엑셀
                  </button>
                  <button className="scoring-action-secondary" onClick={handleCopyResult} disabled={isResultInteractionLocked}>
                    복사하기
                  </button>
                  <button
                    className="scoring-action-secondary"
                    onClick={() => handleHandoffToSaenggibu(evaluationResults, 'single')}
                    disabled={isResultInteractionLocked}
                    data-testid="scoring-saenggibu-handoff"
                  >
                    생기부로 이어가기
                  </button>
                  {isSaved && (
                    <button className="scoring-action-secondary" onClick={() => navigate('/scoring-history')} disabled={isResultInteractionLocked}>
                      채점 기록
                    </button>
                  )}
                </div>
              </div>

              {/* 다른 학생 채점 섹션 */}
              {!isAnonymousUser && (
                <div className="scoring-next-student-section">
                  <button className="scoring-next-student-button" onClick={handleScoreNextStudent} disabled={isResultInteractionLocked}>
                    다른 학생 채점하기
                  </button>
                </div>
              )}

              {/* 비로그인 유저: 가입 유도 CTA */}
              {isAnonymousUser && (
                <div className="scoring-anon-cta">
                  <p className="scoring-anon-cta-title">채점 결과가 저장되지 않아요</p>
                  <p className="scoring-anon-cta-desc">무료 가입하면 채점 기록을 저장하고 엑셀로 내려받을 수 있어요</p>
                  <button className="scoring-anon-cta-button" onClick={() => setShowLoginModal(true)}>
                    무료로 시작하기
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showLoginModal && (
        <LoginPrompt isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} feature="scoring" />
      )}

      {showProRequiredModal && (
        <ProUpgradeModal isOpen={showProRequiredModal} onClose={() => setShowProRequiredModal(false)} feature="채점" trigger="not_pro" />
      )}

      {/* 채점 기준 확인 — 확인 전에는 닫을 수 없는(dismissible=false) 모달.
          안내 토스트("기준을 먼저 확인해 주세요" 등)가 레이어 대기열에 묶이지 않도록 레이어 등록은 하지 않는다. */}
      {isRubricReviewOpen && (
        <Dialog
          description="평가 방식과 평가요소가 맞는지 확인한 뒤 채점을 시작해주세요."
          dismissible={false}
          eyebrow="채점 전 확인"
          footer={(
            <ModalActions
              primary={{
                label: rubricReviewTextEdited ? '수정한 기준으로 채점하기' : '이 기준으로 채점하기',
                onClick: handleRubricReviewConfirm,
              }}
              secondary={isRubricReviewEditing
                ? { label: '요약으로 돌아가기', onClick: () => setIsRubricReviewEditing(false) }
                : { label: '수정하기', onClick: () => setIsRubricReviewEditing(true) }}
              tertiary={isRubricReviewEditing
                ? null
                : { label: '다시 업로드하기', onClick: handleRubricReviewReset }}
              testId="scoring-rubric-review-actions"
            />
          )}
          open
          panelClassName="scoring-rubric-review-modal"
          registerLayer={false}
          size="lg"
          testId="scoring-rubric-review"
          title="AI가 채점 기준을 이렇게 이해했어요"
        >
          <div className="scoring-rubric-review-body">
            {rubricReviewQuestionSet?.questions?.length > 1 && !isRubricReviewEditing && (
              <div className="scoring-rubric-review-question-set">
                <div className="scoring-rubric-review-question-head">
                  <div>
                    <strong>{rubricReviewQuestionSet.questions.length}개 문항을 찾았어요</strong>
                    <p>기본은 전체 문항 채점이에요. 필요한 문항만 선택할 수도 있어요.</p>
                  </div>
                  <button
                    type="button"
                    className="scoring-rubric-review-mini"
                    onClick={() => setSelectedQuestionIds(rubricReviewQuestionSet.questions.map((q) => String(q.question_id)))}
                  >
                    전체 선택
                  </button>
                </div>
                <div className="scoring-rubric-review-question-list">
                  {rubricReviewQuestionSet.questions.map((q) => {
                    const qid = String(q.question_id);
                    const selected = selectedQuestionIds.includes(qid);
                    return (
                      <button
                        key={qid}
                        type="button"
                        className={`scoring-rubric-review-question ${selected ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedQuestionIds(prev => {
                            if (prev.includes(qid)) {
                              return prev.length > 1 ? prev.filter(id => id !== qid) : prev;
                            }
                            return [...prev, qid];
                          });
                        }}
                      >
                        <span className="scoring-rubric-review-check">{selected ? '✓' : ''}</span>
                        <span className="scoring-rubric-review-question-title">{q.title || `${qid}번 문항`}</span>
                        <strong>
                          {isGradeOnlyRubric(q.rubric_criteria) ? '등급제' : `${q.max_points || 0}점`}
                        </strong>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {rubricReviewRubric && !isRubricReviewEditing && (() => {
              const reviewGradeOnly = isGradeOnlyRubric(rubricReviewRubric);
              return (
                <div className="scoring-rubric-review-summary">
                  <div className="scoring-rubric-review-total">
                    <span>{reviewGradeOnly ? '평가 방식' : '총점'}</span>
                    <strong>{reviewGradeOnly ? '등급제' : `${rubricReviewRubric.total_points || 0}점`}</strong>
                  </div>
                  {reviewGradeOnly && (
                    <div className="scoring-rubric-review-note">
                      배점 없는 기준서로 인식했어요. 결과는 점수 대신 수행수준 중심으로 보여드려요.
                    </div>
                  )}
                  {!reviewGradeOnly && (rubricReviewRubric.base_points || rubricReviewRubric.minimum_score) && (
                    <div className="scoring-rubric-review-note">
                      기본/최저점: {rubricReviewRubric.base_points || rubricReviewRubric.minimum_score}점
                    </div>
                  )}
                  <div className="scoring-rubric-review-items">
                    {(rubricReviewRubric.evaluation_items || []).map((item, idx) => (
                      <div key={idx} className="scoring-rubric-review-item">
                        <div className="scoring-rubric-review-item-head">
                          <span>{getRubricItemName(item)}</span>
                          <strong>{reviewGradeOnly ? '수행수준' : `${getRubricItemMaxScore(item)}점`}</strong>
                        </div>
                        {Array.isArray(item.scoring_levels) && item.scoring_levels.length > 0 && (
                          <div className="scoring-rubric-review-levels">
                            {item.scoring_levels.slice(0, 4).map((level, levelIdx) => (
                              <span key={levelIdx}>
                                {level.level || `수준 ${levelIdx + 1}`}
                                {!reviewGradeOnly && (level.score || level.points) ? ` ${level.score || level.points}점` : ''}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {!isRubricReviewEditing && showRubricReviewBlankToggle && (
              <div className="scoring-rubric-review-blank" data-testid="scoring-rubric-review-blank">
                <div className="scoring-rubric-review-blank-row">
                  <span id="rubric-review-blank-label">답안을 안 쓴 학생</span>
                  <div className="scoring-rubric-review-blank-options" role="group" aria-labelledby="rubric-review-blank-label">
                    {[
                      { value: 'zero', label: '0점' },
                      {
                        value: 'floor',
                        label: rubricReviewBlankDefault.hasItems ? `최저점 ${rubricReviewBlankDefault.floorTotal}점` : '최저점',
                      },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={rubricReviewBlankPolicy === option.value}
                        className={rubricReviewBlankPolicy === option.value ? 'active' : ''}
                        onClick={() => setRubricReviewBlankChoice(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                {!rubricReviewBlankChoice && rubricReviewBlankDefault.reason && (
                  <p className="scoring-rubric-review-blank-hint">
                    {rubricReviewBlankDefault.reason === 'instruction'
                      ? "추가 요청에 '백지 0점'이 있어서 0점으로 정했어요"
                      : "기준서에 '백지 0점'이 있어서 0점으로 정했어요"}
                  </p>
                )}
              </div>
            )}

            {isRubricReviewEditing ? (
              <div className="scoring-rubric-review-edit">
                <label className="scoring-rubric-review-edit-label" htmlFor="rubric-review-text">
                  채점 기준 수정
                </label>
                <textarea
                  id="rubric-review-text"
                  className="scoring-textarea scoring-rubric-review-textarea"
                  value={rubricReviewText}
                  onChange={(e) => {
                    setRubricReviewText(e.target.value);
                    setRubricReviewTextEdited(true);
                  }}
                  rows={10}
                />
                <p className="scoring-rubric-review-edit-hint">
                  수정한 내용은 채점할 때 다시 분석해서 적용해요.
                </p>
              </div>
            ) : (
              <details className="scoring-rubric-review-raw">
                <summary>추출된 기준서 원문 보기</summary>
                <pre>{rubricReviewText}</pre>
              </details>
            )}
          </div>
        </Dialog>
      )}

      <FilePreCheckModal
        isOpen={showPreCheckModal}
        onClose={handlePreCheckCancel}
        onConfirm={handlePreCheckConfirm}
        checkResult={preCheckResult}
        isLoading={preCheckLoading}
        loadingProgress={precheckChunkProgress}
        alternateAction={preCheckResult?.alternate_action === 'switch_to_batch_student_files'
          ? { label: '여러 명 채점으로 가기', onClick: switchPendingAnswerFilesToBatch }
          : null}
      />

      {pdfCompressionProgress && (
        <Dialog
          align="center"
          aria-busy
          description="스캔 파일이 커서 압축한 뒤 올려요. 잠시만 기다려주세요."
          dismissible={false}
          open
          panelClassName="scoring-pdf-compress-modal"
          role="alertdialog"
          size="sm"
          testId="scoring-pdf-compress"
          title="PDF를 압축하고 있어요"
        >
          {/* 페이지 수를 아는 동안만 실제 진행률을 보여준다. 모르는 동안은 흐르는 바. */}
          <Progress
            aria-label="PDF 압축 진행 상황"
            label={pdfCompressionProgress.total > 0
              ? `${pdfCompressionProgress.total}쪽 중 ${pdfCompressionProgress.current}쪽`
              : '압축을 준비하고 있어요'}
            value={pdfCompressionProgress.total > 0
              ? Math.min(100, Math.round((pdfCompressionProgress.current / pdfCompressionProgress.total) * 100))
              : null}
          />
        </Dialog>
      )}

      {showResetConfirm && (
        <div className="scoring-reset-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="scoring-reset-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="scoring-reset-modal-title">저장하지 않은 결과가 있어요</h3>
            <p className="scoring-reset-modal-desc">현재 채점 결과가 저장되지 않았어요.</p>
            <div className="scoring-reset-modal-actions-col">
              <button
                className="scoring-reset-modal-save"
                onClick={async () => {
                  setShowResetConfirm(false);
                  await handleSave();
                  doNextStudent();
                }}
              >
                저장하고 이동
              </button>
              <div className="scoring-reset-modal-actions-row">
                <button className="scoring-reset-modal-cancel" onClick={() => setShowResetConfirm(false)}>
                  돌아가기
                </button>
                <button
                  className="scoring-reset-modal-discard"
                  onClick={() => {
                    setShowResetConfirm(false);
                    doNextStudent();
                  }}
                >
                  저장 안 함
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {navBlocker.state === 'blocked' && (
        <div className="scoring-reset-overlay" onClick={() => navBlocker.reset()}>
          <div className="scoring-reset-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="scoring-reset-modal-title">저장하지 않은 결과가 있어요</h3>
            <p className="scoring-reset-modal-desc">현재 채점 결과가 저장되지 않았어요.</p>
            <div className="scoring-reset-modal-actions-col">
              <button
                className="scoring-reset-modal-save"
                onClick={async () => {
                  await handleSave();
                  navBlocker.proceed();
                }}
              >
                저장하고 이동
              </button>
              <div className="scoring-reset-modal-actions-row">
                <button className="scoring-reset-modal-cancel" onClick={() => navBlocker.reset()}>
                  돌아가기
                </button>
                <button
                  className="scoring-reset-modal-discard"
                  onClick={() => navBlocker.proceed()}
                >
                  저장 안 함
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {reviewModal && (
        <ScoringReviewWorkspace
          getSubmissionLabel={getWorkspaceSubmissionLabel}
          initialSelectedIndex={reviewModal.index}
          loadSubmissionPreview={loadWorkspaceSubmissionPreview}
          onClose={() => setReviewModal(null)}
          questionSet={getActiveQuestionSet()}
          onSaveResult={handleWorkspaceSaveResult}
          recordId={savedScoringRecordId}
          results={buildWorkspaceResults(reviewModal.source === 'batch' ? batchResults : evaluationResults)}
          rubric={activeRubric}
          rubricText={rubricText}
          title={reviewModal.source === 'batch' ? '학생별 채점 결과를 확인해 주세요' : '채점 결과를 확인해 주세요'}
          variant="modal"
        />
      )}

      {/* 제출물 미리보기 모달 — 단일/일괄/제출방 모든 모드에서 사용 */}
      {previewSubmission && (
        <div className="scoring-preview-overlay" onClick={() => { setPreviewSubmission(null); setPreviewUrl(''); setPreviewZoomed(false); }}>
          <div className="scoring-preview-content" onClick={(e) => e.stopPropagation()}>
            <div className="scoring-preview-header">
              <div className="scoring-preview-header-info">
                <h3>{previewSubmission.student_name}님의 제출물</h3>
                <span className="scoring-preview-header-meta">
                  {previewSubmission.filename}
                  {previewSubmission.pages ? ` · ${previewSubmission.pages}페이지` : ''}
                  {previewSubmission.file_size ? ` · ${(previewSubmission.file_size / 1024).toFixed(0)}KB` : ''}
                </span>
              </div>
              <button className="scoring-preview-close" onClick={() => { setPreviewSubmission(null); setPreviewUrl(''); setPreviewZoomed(false); }}>
                &times;
              </button>
            </div>
            <div className="scoring-preview-body">
              {previewLoading ? (
                <div className="scoring-preview-loading">
                  <div className="submit-loading-spinner" style={{ width: 28, height: 28 }} />
                  <span>파일 불러오는 중...</span>
                </div>
              ) : previewSubmission.text_content ? (
                // 텍스트 입력 케이스 — 사용자가 직접 입력한 답안 또는 추출된 텍스트를 그대로 표시
                <div className="scoring-preview-text-body">
                  <pre className="scoring-preview-text-pre">{previewSubmission.text_content}</pre>
                </div>
              ) : previewUrl ? (
                previewSubmission.file_type?.startsWith('image/') ? (
                  <img
                    src={previewUrl}
                    alt={previewSubmission.filename}
                    className={`scoring-preview-image ${previewZoomed ? 'is-zoomed' : ''}`}
                    title={previewZoomed ? '클릭하면 화면에 맞게 보기' : '클릭하면 원본 크기로 확대'}
                    onClick={() => setPreviewZoomed((prev) => !prev)}
                  />
                ) : previewSubmission.file_type === 'application/pdf' ? (
                  // PDF Open Parameter로 사이드바/도구바 기본 닫힘 시도.
                  // Chrome PDF Viewer는 일부 fragment만 인식(pagemode=none, view=FitH 등)하지만,
                  // Edge/Adobe/PDF.js 환경에서는 navpanes=0/toolbar=0이 모두 적용됨.
                  // 사용자가 명시적으로 사이드바를 다시 열고 싶으면 viewer 안에서 토글 가능.
                  <iframe
                    src={buildPdfPreviewSrc(previewUrl, previewSubmission.pages)}
                    title={previewSubmission.filename}
                    className="scoring-preview-pdf"
                  />
                ) : previewSubmission.file_type?.startsWith('text/') ? (
                  // text/plain, text/html 등은 브라우저에서 인라인 렌더 가능
                  <iframe
                    src={previewUrl}
                    title={previewSubmission.filename}
                    className="scoring-preview-pdf"
                  />
                ) : (
                  // HWP/HWPX/DOCX/XLSX/PPTX 등 브라우저 미리보기 불가 — 친절한 다운로드 안내
                  <div className="scoring-preview-file-info">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#8b95a1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span className="scoring-preview-file-name">{previewSubmission.filename}</span>
                    <p className="scoring-preview-file-hint">
                      {getPreviewFallbackHint(previewSubmission.filename, previewSubmission.file_type)}
                    </p>
                    <div className="scoring-preview-file-actions">
                      <a
                        href={previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="scoring-preview-download-btn"
                      >
                        새 탭에서 열기
                      </a>
                      <a
                        href={previewUrl}
                        download={previewSubmission.filename || ''}
                        className="scoring-preview-download-btn scoring-preview-download-btn--primary"
                      >
                        다운로드
                      </a>
                    </div>
                  </div>
                )
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScoringPageInner;
